import * as THREE from "three";
import { EffectComposer, RenderPass, EffectPass, DepthOfFieldEffect } from "postprocessing";
import {
  Direction,
  regionOfZone,
  type Region,
  MOVE_SPEED,
  SPAWN,
  MessageType,
  KICK_DUPLICATE_SESSION,
  PICKUP_RANGE,
  TAVERN_OWNER_ID,
  isBuildingId,
  MAX_EMPLOYEES_PER_BUILDING,
  buildingName,
  Building,
  DEFAULT_APPEARANCE,
  applyCosmetics,
  type CharacterAppearance,
  DEFAULT_MINING,
  DEFAULT_SKINNING,
  DEFAULT_COOKING,
  DEFAULT_BASE_STATS,
  CRAFT_STATION_RANGE,
  STATION_ANCHORS,
  STATION_FURNITURE,
  FURNITURE_USE_RANGE,
  recipeById,
  cookRecipeById,
  sewRecipeById,
  sewingMachineAnchor,
  cookingPotAnchor,
  drinkDispenserAnchor,
  fridgeAnchor,
  isRestaurantBuilding,
  isBusinessBuilding,
  isHouseBuilding,
  DEFAULT_HOUSE_LIMIT,
  storeChestAnchor,
  type CraftStation,
  deriveMoveSpeedMult,
  deriveMaxStamina,
  STAMINA_RUN_DRAIN,
  STAMINA_ATTACK_COST,
  STAMINA_BLOCK_COST,
  STAMINA_JUMP_COST,
  STAMINA_REGEN_DELAY_MS,
  STAMINA_REGEN_RATE,
  STAMINA_RUN_RESUME,
  JUMP_DURATION_MS,
  JUMP_HEIGHT,
  AGILITY_STAMINA_COST,
  SKINNING_KNIFE_ID,
  HOG_SKIN_TIER,
  SKIN_RANGE,
  skinDurationMs,
  canSkinTier,
  canMineTier,
  SKINNER_SHOP,
  TACKLE_SHOP,
  SMITH_SHOP,
  GHOSTLAB_SHOP,
  GHOST_SCROLL_ID,
  BLUNT_ID,
  HOG_CHARM_ID,
  PICKAXE_ID,
  FISHING_ROD_ID,
  FISH_RANGE,
  DEFAULT_FISHING,
  KAPON_TERRAIN,
  CITY_POND,
  WATER_PROFILES,
  WATER_FISH,
  isFishItem,
  FISHING_HAT_STYLE,
  FISH_HAT_ZONE_BONUS,
  FISH_HAT_SPEED_MULT,
  BMX_ID,
  BMX_SPEED,
  BMX_SPRINT_MULT,
  BMX_ACCEL,
  BMX_BRAKE,
  BMX_DRAG,
  BMX_TURN_MAX,
  BMX_TURN_MIN,
  BMX_SIT_Y,
  BMX_MOUNT_RANGE,
  BLACKMARKET_SHOP,
  WATABAGA_SUPPLIES,
  ITEM_NAMES,
  FURNITURE_SHOP,
  FURNI_BUILDING_ID,
  isFurnitureItem,
  SAFEBOX_ID,
  FURNITURE_MAX_Z,
  FURNITURE_FUNCTIONS,
  furnitureRadius,
  GRASS_CUTTER_ID,
  MINE_RANGE,
  ATM_RANGE,
  CHEST_RANGE,
  WEED_INTERACT_RANGE,
  WEED_SEED_ID,
  WATERING_POT_ID,
  POT_WATER_MAX_ML,
  BLACKJACK_TABLE_ANCHOR,
  POKER_TABLE_ANCHOR,
  POKER_SIT_RANGE,
  decodeItemMods,
  isWeapon,
  isShield,
  isFood,
  ATTACK_COOLDOWN_MS,
  BLOCK_COOLDOWN_MS,
  BLOCK_DURATION_MS,
  type MoveMessage,
  type Player,
  type WeedPlant,
  type Hog,
  type Goblin,
  type Slime,
  type PetHog,
  type Guard,
  type CityNpc,
  type Chest,
  type GroundItem,
  type Hand,
  type InventoryMessage,
  type PickupRejectedMessage,
  type EquipRejectedMessage,
  type BalanceMessage,
  type DayTimeMessage,
  type PlayerStatsMessage,
  type QuestLogMessage,
  QUESTS,
  type CraftResultMessage,
  type TableSeatMessage,
  type TableRejectedMessage,
  type ActionRejectedMessage,
  type PokerSeatedMessage,
  type PokerHoleMessage,
  type PokerRejectedMessage,
  type PokerActionRejectedMessage,
  type NoticeMessage,
  type LeaderboardMessage,
  type StorageMessage,
  type MarketMessage,
  type FridgeMessage,
  type FoodMenuMessage,
  type SafeboxMessage,
  type ChatSayMessage,
  type ServerNoticeMessage,
  type CombatDiedMessage,
  type CombatHitMessage,
  type CombatRespawnMessage,
  type TradeInviteMessage,
  type TradeUpdateMessage,
  type TradeClosedMessage,
  type SkillLevelUpMessage,
  type FishLevelSetMessage,
  type RodBaitMessage,
  type FishStopMessage,
  type FishCaughtMessage,
  type BmxMountMessage,
  type ImprintFishMessage,
  type FishBoardMessage,
} from "@ghostland/shared";
import { session } from "../auth/session.js";
import { joinGameRoom, getStateCallbacks } from "../net/connect.js";
import { showStatus, hideStatus } from "../ui/statusOverlay.js";
import { beginTransition, endTransition, whenAssetsIdle } from "../ui/loadingScreen.js";
import { popCombatNumber } from "../ui/damageNumbers.js";
import { drawItemIcon } from "../items/itemArt.js";
import { inventoryStore } from "../items/inventoryStore.js";
import { appearanceStore } from "../items/appearanceStore.js";
import { cosmeticStore } from "../items/cosmeticStore.js";
import { balanceStore } from "../items/balanceStore.js";
import { workingStore } from "../items/workingStore.js";
import { statsStore } from "../items/statsStore.js";
import { staminaStore } from "../items/staminaStore.js";
import { blackjackStore, type SeatView, type HandView } from "../items/blackjackStore.js";
import { pokerStore, type PokerSeatView } from "../items/pokerStore.js";
import { toggleInventory, closeInventory, isInventoryOpen, setInventoryActions } from "../ui/inventory.js";
import { toggleFishingDebug, setFishingDebugActions } from "../ui/fishingDebug.js";
import { buildBmx, type BmxProp } from "./bmxProp.js";
import { openFishBoard, setFishBoard, isFishBoardOpen, closeFishBoard, openImprint, setFishBoardActions } from "../ui/fishBoard.js";
import { showFishCatch } from "../ui/fishCatchPopup.js";
import { openTable, closeTable, isTableOpen, setTableActions } from "../ui/blackjackTable.js";
import { openPokerTable, closePokerTable, isPokerTableOpen, setPokerActions } from "../ui/pokerTable.js";
import { Cards3D } from "./cards3d.js";
import { openAtm, closeAtm, isAtmOpen, setAtmActions } from "../ui/atm.js";
import { openCraftBook, openCraftStation, closeCraft, isCraftOpen, setCraftActions } from "../ui/craftMenu.js";
import { openRankboard, closeRankboard, isRankboardOpen, setRankboardActions, renderLeaderboard } from "../ui/rankboard.js";
import { openStorage, closeStorage, isStorageOpen, setStorageActions } from "../ui/storage.js";
import { openSell, closeSell, isSellOpen, setSellActions } from "../ui/sellShop.js";
import { openMarket, closeMarket, isMarketOpen, setMarketActions } from "../ui/marketOverlay.js";
import { marketStore } from "../items/marketStore.js";
import { storageStore } from "../items/storageStore.js";
import { openFridge, closeFridge, isFridgeOpen, setFridgeActions } from "../ui/fridge.js";
import { fridgeStore } from "../items/fridgeStore.js";
import { openCook, closeCook, isCookOpen, setCookActions } from "../ui/cookMenu.js";
import { openSew, closeSew, isSewOpen, setSewActions } from "../ui/sewMenu.js";
import { openFoodShop, closeFoodShop, isFoodShopOpen, setFoodShopActions } from "../ui/foodShop.js";
import { openDispenser, closeDispenser, isDispenserOpen, setDispenserActions } from "../ui/dispenser.js";
import { foodMenuStore } from "../items/foodMenuStore.js";
import { showShop, closeShop, isShopOpen, setShopActions } from "../ui/shopOverlay.js";
import { showSalon, closeSalon, isSalonOpen, setSalonActions } from "../ui/salonOverlay.js";
import { mountHudButton } from "../ui/hudButton.js";
import { mountCombatHud, setCombatActive, flashCooldown } from "../ui/combatHud.js";
import { flashHurt } from "../ui/hitFlash.js";
import { mountSkillBar, setSkillCooldown } from "../ui/skillBar.js";
import { toggleSkillPage, closeSkillPage, isSkillPageOpen } from "../ui/skillPage.js";
import { skillStore, availableSkillIds } from "../items/skillStore.js";
import { weaponStore } from "../items/weaponStore.js";
import { neckStore } from "../items/neckStore.js";
import { CatController } from "./animal/cat.js";
import { HogController } from "./animal/hog.js";
import { GoblinController } from "./animal/goblin.js";
import { SlimeController } from "./animal/slime.js";
import { PetHogController } from "./animal/petHog.js";
import { CityNpcController } from "./animal/cityNpc.js";
import { GuardController } from "./animal/guard.js";
import { SelectRing } from "./selectRing.js";
import { UI_LAYER } from "./overheadLayer.js";
import { distanceBlur } from "./distanceBlur.js";
import { dayNight } from "./dayNight.js";
import { cameraView } from "./cameraView.js";
import { ChestController } from "./chest.js";
import { openChest, updateChest, closeChest, isChestOpen, currentChestId } from "../ui/chestPanel.js";
import { WeedPlantController } from "./plants.js";
import { openPlantPanel, updatePlantPanel, closePlantPanel, isPlantPanelOpen, currentPlantId, type PlantPanelData, type PlantPanelActions } from "../ui/plantPanel.js";
import { setHighVisible } from "../ui/highOverlay.js";
import { ChatBubbles } from "./chatBubbles.js";
import { setChatActions, openChat, isChatOpen, enableServerMode } from "../ui/chatBar.js";
import { showServerNotice, destroyServerNotice } from "../ui/serverNotice.js";
import { showDeath, hideDeath } from "../ui/deathOverlay.js";
import { mountMoneyHud } from "../ui/moneyHud.js";
import { mountVitalsHud } from "../ui/vitalsHud.js";
import { mountStaminaHud } from "../ui/staminaHud.js";
import { mountBlockHud, setBlockProgress } from "../ui/blockHud.js";
import { registerBottomSlot, setBottomSlot } from "../ui/bottomStack.js";
import { openBuildingManage, refreshBuildingManage, closeBuildingManage, isBuildingManageOpen, buildingManageId, type ManageView } from "../ui/buildingManage.js";
import { openAuctionBid, refreshAuctionBid, closeAuctionBid, isAuctionBidOpen, auctionBidId, type BidView } from "../ui/auctionBid.js";
import { setQuestLog, toggleQuestLog, closeQuestLog, isQuestLogOpen, questEntries } from "../ui/questLog.js";
import { openElevator, closeElevator, isElevatorOpen } from "../ui/elevator.js";
import { showToast, showBanner } from "../ui/toast.js";
import { drawCard } from "../items/cardArt.js";
import { showConfirm } from "../ui/confirmDialog.js";
import { showSkillLevelUp } from "../ui/skillNotice.js";
import { appearanceFromSchema } from "../sprites/c3dImage.js";
import { CharBillboard, headingToDir } from "./charBillboard.js";
import { buildStreetScene, U, isAptUnitZone, type SceneBuild, type ZoneId, type Portal } from "./streetScene.js";
import { aptFloorY } from "./villageScene.js";
import { kaponHeightAt } from "./kaponTerrain.js";
import { audio } from "../audio/audio.js";
import { getInvertMouse } from "../settings.js";
import { NpcManager, tavernNpcs, villageNpcs, threadsNpcs, casinoNpcs, bankNpcs, cityTavernNpcs, foodNpcs, furniNpcs, tackleNpcs, smithNpcs, makeSelectLabel, type Npc, type DialogNode, type NpcAction } from "./npc.js";
import { FurnitureLayer, furnitureMesh, ghostify, setGhostBlocked } from "./furniture.js";
import { openSafebox, closeSafebox, isSafeboxOpen, setSafeboxActions, setSafeboxSnapshot } from "../ui/safebox.js";
import { showPlacementBar, setPlacementBarMode, hidePlacementBar, type PlacementMode } from "../ui/placementBar.js";
import { openFurnitureMenu as showFurniMenu, closeFurnitureMenu, isFurnitureMenuOpen, type FurnitureMenuOption } from "../ui/furnitureMenu.js";
import { PortalEntity } from "./portal.js";
import { MineController, MinerPickaxe } from "./mine.js";
import { FishingController } from "./fishing.js";
import { SkinController } from "./skin.js";
import { RollController } from "./roll.js";
import { CraftController, CraftGear } from "./craft.js";
import { GrassController } from "./grass.js";
import { showNpcDialog, hideNpcDialog, isNpcDialogOpen, destroyNpcDialog, advanceNpcDialog, setNpcDialogCinematic, QUEST_OPT } from "../ui/npcDialog.js";
import { openInspect, openSelfStats, setInspectOwnName, setInspectActions, closeInspect, isInspectOpen } from "../ui/inspectPanel.js";
import { setTradeActions, showTradeInvite, renderTrade, closeTradeWindow, closeTradeInvite, isTradeOpen, isTradeInviteOpen } from "../ui/tradeWindow.js";
import { toggleGameMenu, isGameMenuOpen, closeGameMenu, destroyGameMenu } from "../ui/gameMenu.js";
import { showNpcTarget, showTarget, hideNpcTarget, destroyNpcTarget } from "../ui/npcTarget.js";
import { composeBust } from "../sprites/portrait.js";

const SEND_INTERVAL_MS = 50; // ~20Hz
const RUN_MULT = 1.7; // sprint speed multiplier (shift + WASD)
const LERP = 0.25;
const REMOTE_TELEPORT2 = 8 * 8; // remote position jump (world units²) beyond this = teleport → snap+fade, not lerp
const PIXEL_TARGET_H = 264; // internal render height; upscaled (nearest) for a chunky pixel look
const DOOR_RANGE = 48; // server px: how close to a door before the "Press E" prompt shows
// Collision padding (server px): the player is a flat 2D billboard ~half a tile wide, but collision
// only tests its CENTRE — so it can press its centre to a wall and the sprite's near half visually
// clips INTO the 3D building. We inflate every blocker by this margin so the player stops a sprite-
// half-width short of any wall and never buries into it. ~half a tile; city doors are teleports and
// gates are many tiles wide, so this never seals a real passage. Tune here if a gap feels tight.
const COLLISION_PAD = 16;
const TABLE_SIT_RANGE = 150; // server px: how close to the casino table before the "Press E to sit" prompt
const SEAT_SIT_Y = 0.5; // world units: raise a seated (blackjack) billboard so it perches on the stool
const POKER_SEAT_SIT_Y = 0.18; // poker uses the real seated sprite, which sits LOWER on the stool
const BED_SIT_Y = 0.6; // world units: perch a RESTING billboard on the bed's mattress top
const POKER_DEAL_GAP_MS = 450; // gap between each card the dealer flies out (one-by-one dealing)
const TAB_RANGE = 6;   // world units: Tab targets the nearest NPC within this radius
const TARGET_CLEAR_RANGE = 7.5; // world units: a selected NPC beyond this is auto-deselected

type GameRoom = Awaited<ReturnType<typeof joinGameRoom>>;

const TABLE_REJECT_TEXT: Record<TableRejectedMessage["reason"], string> = {
  tableFull: "Table is full",
  tooFar: "Move closer to the table",
  noCharacter: "No character to play",
  alreadySeated: "You're already seated",
  notSeated: "You're not seated",
  inRound: "Can't leave mid-round",
};
const PICKUP_REJECT_TEXT: Record<PickupRejectedMessage["reason"], string> = {
  full: "Inventory full",
  noCharacter: "No character to carry items",
  tooHeavy: "Too heavy to carry (100kg max)",
};
const POKER_REJECT_TEXT: Record<PokerRejectedMessage["reason"], string> = {
  tableFull: "Poker table is full",
  tooFar: "Move closer to the poker table",
  noCharacter: "No character to play",
  alreadySeated: "You're already seated",
  notSeated: "You're not seated",
  inHand: "Can't do that mid-hand",
  buyInRange: "Buy-in out of range",
  insufficient: "Not enough cash",
  hasChips: "Cash out your chips first",
};
const POKER_ACTION_REJECT_TEXT: Record<PokerActionRejectedMessage["reason"], string> = {
  notYourTurn: "Not your turn",
  noSeat: "You're not seated",
  cannotCheck: "You can't check — call or fold",
  raiseTooSmall: "Raise is too small",
  insufficient: "Not enough chips",
  handOver: "The hand is over",
};
const EQUIP_REJECT_TEXT: Record<EquipRejectedMessage["reason"], string> = {
  noCharacter: "No character to carry items",
  notEquippable: "Can't equip that",
  empty: "Nothing there",
  bagFull: "Inventory full",
};
const ACTION_REJECT_TEXT: Record<ActionRejectedMessage["reason"], string> = {
  betsClosed: "Betting is closed",
  betOutOfRange: "Bet out of range",
  insufficient: "Not enough coins",
  notYourTurn: "Not your turn",
  handResolved: "Hand already done",
  noSeat: "You're not seated",
  doubleNotAllowed: "Can't double now",
  splitNotAllowed: "Can't split that",
};

function handView(h: Hand): HandView {
  return { cards: h.cards, value: h.value, soft: h.soft, status: h.status, bet: h.bet };
}

interface RemotePlayer {
  bb: CharBillboard;
  label: THREE.Sprite;
  sessionId: string;  // for trade requests
  player: Player;     // the synced schema (read live appearance + hand1/hand2 + name for inspect)
  serverX: number;
  serverY: number;
  dir: Direction;
  moving: boolean;
  running: boolean;
  wasAttacking: boolean; // edge-detect this remote's attacking flag to fire the swing once
  wasBlocking: boolean;  // edge-detect this remote's blocking flag to fire the block sound once
  wasStunned: boolean;   // edge-detect this remote's stunned flag to fire the stagger shake once
  wasSmoking: boolean;   // edge-detect this remote's smoking flag to play the light-up sound once
  jumpSeq: number;       // last seen jumpSeq — an increment triggers the hop + airborne pose
  jumpActive: boolean;   // mid-hop (riding the parabola)
  jumpT: number;         // ms elapsed into the current hop
  seatIndex: number; // casino seat (0..4) when sitting, else -1 → billboard snaps to the stool
  pokerSeatIndex: number; // poker seat (0..5) when sitting, else -1 → billboard snaps to the poker stool
  labelRed: boolean; // whether the name plate is currently drawn red (red-named) — rebuild on change
}

/** Name-plate colour for a "red-named" (negative affinity) player vs everyone else. */
const RED_NAME_COLOR = "#ff5a5a";

interface GroundEntity {
  sprite: THREE.Sprite;
  x: number;
  y: number;
}

/**
 * The GhostLand world rendered as the spike proved out: a 3D scene with the
 * character as the pre-rendered pixel billboard, a trailing third-person orbit
 * camera, and camera-basis WASD. Server stays the 2D authority on positions; we
 * just paint that plane in 3D. All netcode/items/table/HUD mirror the old Phaser
 * WorldScene so multiplayer, pickups, and blackjack behave identically.
 */
export class World3D {
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(45, 1, 0.1, 420); // far enough to enclose the big Kapon sky dome
  private readonly renderer: THREE.WebGLRenderer;
  // Post-processing (HD-2D/Octopath look): a per-pixel depth-of-field (far distance softens).
  private composer!: EffectComposer;       // pmndrs/postprocessing composer
  private dof!: DepthOfFieldEffect;        // per-pixel depth-of-field (blurs everything by distance)
  private readonly clock = new THREE.Clock();
  private readonly camRay = new THREE.Raycaster();
  private raf = 0;
  private disposed = false;

  private readonly player = new CharBillboard();
  /** Combat mode: weapon drawn, only allowed in combat zones (e.g. Kapon North Way). */
  private combat = false;
  /** Our own synced Player schema (health/blocking/attacking/dead) — set on join. */
  private me: Player | null = null;
  private wasAttacking = false;   // edge-detect the local attacking flag to fire the swing once
  private wasBlockingLocal = false; // edge-detect the local blocking flag to fire the block sound once
  private wasSmokingLocal = false; // edge-detect the smoking flag to play the light-up sound once
  private stepTimer = 999;        // ms since the last footstep (high = play immediately on first step)
  private blockStartMs = 0;        // performance.now() when the current block stance was raised (countdown bar)
  // Client-side ability cooldowns + the block lock (mirror the server so we never waste stamina on a
  // rejected cast, and lock the player for the block's duration the instant it's cast).
  private attackReadyAt = 0;
  private blockReadyAt = 0;
  private localBlockUntil = 0;
  /** Ambient pixel cats wandering the city streets. */
  private cats?: CatController;
  private hogs?: HogController;
  private goblins?: GoblinController;
  private slimes?: SlimeController;
  private pets?: PetHogController;
  private guards?: GuardController;
  /** Decaying camera-shake amplitude (world units), kicked when the local player is hit. */
  private camShake = 0;
  /** Edge-detect the local player's stunned flag, to fire the stagger shake once. */
  private wasStunned = false;
  private chests?: ChestController;
  private plants?: WeedPlantController;
  /** Floating chat bubbles above players. */
  private chat!: ChatBubbles;
  /** Local position in SERVER pixels (the authority's coordinate space). */
  private px: { x: number; y: number } = { x: SPAWN.x, y: SPAWN.y };
  private faceAngle = Math.PI; // facing away at spawn → camera sees the character's back
  private running = false; // sprinting this frame (shift + move, gated by stamina)
  // BMX: the ridden bike's momentum (server px/s — GTA-style: pedal to accelerate, coast on
  // release, steering carves the velocity around). Parked bikes render as world props per player.
  private readonly bmxVel = new THREE.Vector2(0, 0);
  private readonly bmxParked = new Map<string, BmxProp>();
  private readonly bmxRemotePrev = new Map<string, { x: number; z: number }>(); // wheel-spin speed estimate
  // Stamina (client runtime resource; see docs/design/character-stats.md). Drained by sprint/
  // attack/block, regenerates after a quiet delay. `exhausted` forces a walk until it recovers.
  private stamina = 100;
  private staminaMax = 100;
  private staminaIdleMs = STAMINA_REGEN_DELAY_MS; // ms since last consumption (start ready to regen)
  private exhausted = false;
  private staminaSpent = 0; // cumulative stamina spent toward the next agility-xp tick
  // Jump (Space): a transient hop. The billboard rides a parabola to JUMP_HEIGHT over JUMP_DURATION_MS
  // while showing the static airborne pose; costs STAMINA_JUMP_COST. Synced to others via jumpSeq.
  private jumpActive = false;
  private jumpT = 0;  // ms elapsed into the current hop
  private jumpY = 0;  // current vertical offset (world units) added to the local billboard
  private readonly cam = { dist: 4, yaw: 0, pitch: 0.04 }; // default: zoomed in, behind, angle all the way down

  private room?: GameRoom;
  private callbacks?: ReturnType<typeof getStateCallbacks<GameRoom["state"]>>;
  private reconnectFallback?: number;
  private readonly remotes = new Map<string, RemotePlayer>();
  private cityNpcs?: CityNpcController; // synced walk-in restaurant customers (+ future townsfolk)
  private readonly groundItems = new Map<string, GroundEntity>();
  private highlightKey: string | null = null;
  private mySeat = -1;
  private myPokerSeat = -1;
  private fpYaw = 0;   // first-person look yaw offset (drag to turn the head) while seated at poker
  private fpPitch = 0; // first-person look pitch offset (drag up/down) while seated at poker
  private myHole = ""; // this client's PRIVATE hole cards for the current poker hand (e.g. "AS KD")
  private cards3d?: Cards3D; // 3D card-fly animator (lazily built once the casino zone exists)
  private pokerDealQueue: Array<() => void> = []; // pending card-fly closures, dealt one at a time
  private pokerDealTimer?: number;
  private lastPokerResult = ""; // last shown end-of-hand result banner (guards re-showing)
  private pokerWasMyTurn = false; // edge-detect when the action lands on us (the "your turn" chime)
  private lastBoardLen = 0; // poker community-card count last seen (delta → fly a card)
  private lastCardCounts: number[] = []; // per-seat hole-card count last seen (0→2 delta → fly cards)
  private lastPokerPhase = ""; // last poker phase — entering "preflop" anew = fresh hand → wipe old cards
  private sendTimer = 0;
  private lastSent: { x: number; y: number; dir: Direction; moving: boolean; running: boolean; combat: boolean } = {
    x: SPAWN.x, y: SPAWN.y, dir: Direction.Down, moving: false, running: false, combat: false,
  };

  private readonly keys = new Set<string>();
  private readonly prompt: HTMLDivElement;
  /** Scene data (zone groups/bounds/blockers/colliders + portals). */
  private build!: SceneBuild;
  /** Which zone the local player is in (plaza / tavern / village). */
  private zone: ZoneId = "plaza";
  /** The portal the player is standing on (drives the "Press E" prompt). */
  private activePortal: Portal | null = null;
  private dbgT = 0; // DEBUG: throttle for the day/night lamp diagnostic
  /** Shared building state (owner/lock/staff) synced from the server, keyed by building id. */
  private readonly buildings = new Map<string, { ownerId: string; ownerName: string; locked: boolean; employees: { id: string; name: string }[]; applicants: { id: string; name: string }[]; keyholders: { id: string; name: string }[]; forSale: boolean; salePrice: number; curtains: boolean; auctionEndsAt: number; auctionStartPrice: number; auctionMinIncrement: number; auctionBid: number; auctionBidderId: string; auctionBidderName: string }>();
  /** Our own character id — for "do I own / work at this building" checks. */
  private readonly myCharacterId = session.character?.characterId ?? "";
  /** Which building's fridge the open fridge panel is bound to (set when staff open it). */
  private fridgeBuildingId = "";
  /** Renders every building's synced placed furniture into its zone group. */
  private readonly furnitureLayer = new FurnitureLayer();
  /** FURNITURE-PLACEMENT mode ("Place" on a bagged piece / "Move" on a placed one). While set, the
   *  pointer drag drives the translucent ghost — MOVE mode slides it across the room (wheel raises/
   *  lowers), ROTATE mode spins it; the placement toolbar switches modes, confirms, or cancels.
   *  `slotIndex` set = placing from the bag; `fid` set = moving an existing piece. */
  private placing: { bid: string; itemId: string; slotIndex?: number; fid?: string; x: number; y: number; z: number; rot: number; mode: PlacementMode; ghost: THREE.Object3D } | null = null;
  /** Building storey the local player stands on (0 = ground; 2/3 = tenement floors). Set by door
   *  portals, synced via MoveMessage → Player.floor, drives everyone's render height. */
  private floor = 0;
  /** The storey last SENT to the server (movement messages dedupe against it, like lastSent). */
  private lastSentFloor = 0;
  /** Legacy: whether we're the Ghost character (still gates the SERVER-broadcast chat bar). */
  private readonly isTavernOwner = session.character?.characterId === TAVERN_OWNER_ID;
  /** NPC managers per zone (only the current zone's are shown/interactive). */
  private npcByZone: Partial<Record<ZoneId, NpcManager>> = {};
  /** Cinematic camera override (Sal's city tour): while set, the camera sits at a fixed shot. */
  private cinema: { pos: THREE.Vector3; look: THREE.Vector3 } | null = null;
  /** Fishing cast: the E-hold charge (since = when the meter started) + where the line landed. */
  private castCharge: { since: number } | null = null;
  private castPoint: { x: number; z: number } | null = null;
  private lastSentFishState = 0; // dedupe for the synced rod stance
  /** The left-click-selected NPC (drives the name+[T] label, target frame, and talk). */
  private selectedNpc: Npc | null = null;
  /** The NPC we're mid-conversation with — held in place (facing us) until the dialogue closes. */
  private talkingNpc: Npc | null = null;
  private selectedHog: string | null = null;
  private selectedGoblin: string | null = null;
  private selectedSlime: string | null = null;
  private selectedPet: string | null = null;
  // Resolves when the server's first Inventory message arrives — the boot veil waits on this so you
  // never load in with an empty bag. (Declared before `firstInventory` so its executor can set it.)
  private resolveFirstInventory: (() => void) | null = null;
  private readonly firstInventory = new Promise<void>((r) => { this.resolveFirstInventory = r; });
  private selectedGuard: string | null = null;
  private selectedCityNpc: string | null = null;
  private selectedCat: string | null = null; // a selected ambient cat (name shown; no interaction)
  /** The left-click-selected remote PLAYER (drives their name+[P] label, target card, P → inspect). */
  private selectedRemote: RemotePlayer | null = null;
  private targetPortraitGen = 0; // guards the async player-portrait against a changed selection
  /** The active teleport portal (a timed magic swirl), if one is open, + whether it's selected. */
  /** Shared timed portals (server-synced); any in the current zone can be entered. */
  private readonly portals = new Map<string, PortalEntity>();
  private selectedPortal: PortalEntity | null = null;
  /** Mine nodes + the timed mining channel (the city's mine area). */
  private mine!: MineController;
  private skin!: SkinController;
  private roll!: RollController;
  private craft!: CraftController;
  /** First-person fishing minigame (cast → bite → reel the catch bar). */
  private readonly fishingCtl = new FishingController();
  /** Whether the local player was mining last frame — used to send MineStart/MineStop on transitions. */
  private wasMining = false;
  /** Whether the local player was crafting last frame — drives CraftStart/CraftStop transitions. */
  private wasCrafting = false;
  /** Swinging-pickaxe visuals for REMOTE miners, keyed by sessionId (created/removed as they mine). */
  private readonly miningFx = new Map<string, MinerPickaxe>();
  /** Spinning-gear visuals for REMOTE crafters, keyed by sessionId. */
  private readonly craftingFx = new Map<string, CraftGear>();
  private grass!: GrassController;
  private selectRing!: SelectRing;
  private readonly pickRay = new THREE.Raycaster();

  // bound listeners (so we can remove them on dispose)
  private readonly onKeyDown = (e: KeyboardEvent): void => this.handleKeyDown(e);
  private readonly onKeyUp = (e: KeyboardEvent): void => {
    const k = e.key.toLowerCase();
    if (k === "e" && this.castCharge) this.releaseCast(); // let go of the rod — throw!
    this.keys.delete(k);
  };
  private readonly onResize = (): void => this.resize();
  private dragging = false;
  private lastPointer = { x: 0, y: 0 };
  private downPos = { x: 0, y: 0 };
  private pointerMoved = false;

  constructor(private readonly mount: HTMLElement) {
    // Dev-only debug handle for automated verification (Playwright) and console poking.
    if (import.meta.env.DEV) (window as unknown as { __world?: World3D }).__world = this;
    this.renderer = new THREE.WebGLRenderer({ antialias: false });
    this.renderer.domElement.style.width = "100%";
    this.renderer.domElement.style.height = "100%";
    this.renderer.domElement.style.imageRendering = "pixelated";
    mount.appendChild(this.renderer.domElement);
    this.setupComposer();

    this.prompt = document.createElement("div");
    this.prompt.id = "gl-prompt";
    this.prompt.style.cssText =
      "position:fixed;left:50%;bottom:96px;transform:translateX(-50%);z-index:11;" +
      "font-family:ui-monospace,monospace;font-size:12px;letter-spacing:.12em;color:#f4f4f4;" +
      "background:rgba(0,0,0,.55);padding:6px 12px;border-radius:4px;display:none;pointer-events:none;";
    document.body.appendChild(this.prompt);
    registerBottomSlot("prompt", this.prompt); // lowest-priority slot: stacks above stamina + block

    // Hold the loading veil up from the very first asset load. The manager's auto-hide only
    // tracks TextureLoader (scene props); the character sheets load via `new Image()` and aren't
    // counted, so without an explicit hold the veil would drop while the player is still white.
    // revealWhenReady() drops it once everything visible is actually composited + painted.
    beginTransition("Entering Ghost Land");
    this.build = buildStreetScene(this.scene);
    dayNight.build(this.scene); // one player-following day/night sky rig + global lights (shown outdoors)
    // Only the spawn island's NPCs exist at boot; the city's are created lazily once the
    // city is built (the player travelling there for the first time) — see initCityNpcs.
    this.npcByZone = {
      tavern: tavernNpcs(this.scene, this.build.tavernNpc.area, this.build.tavernNpc.bar),
    };
    this.mine = new MineController(this.scene);
    this.skin = new SkinController(this.scene);
    this.roll = new RollController(this.scene);
    this.craft = new CraftController(this.scene);
    this.grass = new GrassController(this.scene);
    this.selectRing = new SelectRing(this.scene);
    this.chat = new ChatBubbles(this.scene);
    setChatActions({
      send: (text) => this.room?.send(MessageType.Chat, { text }),
      announce: (text) => this.room?.send(MessageType.ServerAnnounce, { text }),
    });
    if (this.isTavernOwner) enableServerMode(); // Ghost can flip the chat bar to SERVER broadcast
    this.scene.add(this.player.group); // in the main scene so closer NPCs/mobs correctly occlude the player
    this.spawnAtSavedLocation();
    const ch = session.character as { cosHat?: string; cosTop?: string; cosBottom?: string; cosShoes?: string; showCosmetics?: boolean } | undefined;
    appearanceStore.set(session.character?.appearance ?? { ...DEFAULT_APPEARANCE });
    cosmeticStore.set({ hat: ch?.cosHat ?? "", top: ch?.cosTop ?? "", bottom: ch?.cosBottom ?? "", shoes: ch?.cosShoes ?? "", showCosmetics: ch?.showCosmetics ?? true });
    this.applyLocalAppearance(); // gear + cosmetic overlay (respects the toggle + zone)
  }

  start(): void {
    this.bindInput();
    audio.init(); // global sound (volume bar lives in the ESC menu)
    mountHudButton();
    mountCombatHud({ toggle: () => this.toggleCombat(), attack: () => this.attack(), block: () => this.block() });
    mountSkillBar((index) => this.activateSlot(index + 1)); // bottom-left 1-4 skill bar
    balanceStore.set(session.character?.balance ?? 0);
    if (session.character?.stats) {
      statsStore.set({ stats: session.character.stats, baseStats: session.character.baseStats ?? structuredClone(DEFAULT_BASE_STATS), bankMoney: session.character.bankMoney ?? 0, mining: session.character.mining ?? { ...DEFAULT_MINING }, skinning: session.character.skinning ?? { ...DEFAULT_SKINNING }, cooking: session.character.cooking ?? { ...DEFAULT_COOKING }, fishing: session.character.fishing ?? { ...DEFAULT_FISHING }, professions: session.character.professions ?? [], gearArmor: 0, statBonus: { strength: 0, dex: 0, agility: 0 } });
    }
    mountMoneyHud();
    mountVitalsHud();
    mountStaminaHud();
    mountBlockHud();
    setTableActions({
      leave: () => this.room?.send(MessageType.LeaveTable),
      bet: (amount: number) => this.room?.send(MessageType.PlaceBet, { amount }),
      hit: () => this.room?.send(MessageType.Hit),
      stand: () => this.room?.send(MessageType.Stand),
      double: () => this.room?.send(MessageType.Double),
      split: () => this.room?.send(MessageType.Split),
    });
    setPokerActions({
      buyIn: (amount: number) => this.room?.send(MessageType.PokerBuyIn, { amount }),
      act: (action, amount) => this.room?.send(MessageType.PokerAct, { action, amount }),
      withdraw: () => this.room?.send(MessageType.PokerWithdraw),
      leave: () => this.room?.send(MessageType.LeavePokerTable),
    });
    setAtmActions({
      deposit: (amount: number) => this.room?.send(MessageType.Deposit, { amount }),
      withdraw: (amount: number) => this.room?.send(MessageType.Withdraw, { amount }),
    });
    setCraftActions({
      process: (recipeId: string, quantity: number) => this.startCraft(recipeId, quantity),
    });
    setRankboardActions({
      request: () => this.room?.send(MessageType.RequestLeaderboard),
      setAnonymous: (anonymous: boolean) => this.room?.send(MessageType.SetAnonymous, { anonymous }),
    });
    setStorageActions({
      request: () => this.room?.send(MessageType.RequestStorage),
      store: (slotIndex: number, to?: number) => this.room?.send(MessageType.StoreItem, { slotIndex, to }),
      retrieve: (slotIndex: number, to?: number) => this.room?.send(MessageType.RetrieveItem, { slotIndex, to }),
      move: (from: number, to: number) => this.room?.send(MessageType.StorageReorder, { from, to }),
      bagMove: (from: number, to: number) => this.room?.send(MessageType.MoveItem, { from, to }),
      upgrade: () => this.room?.send(MessageType.UpgradeStorage),
    });
    setSellActions({
      sell: (slots: number[]) => this.room?.send(MessageType.SellToNpc, { slots }),
    });
    setMarketActions({
      request: () => this.room?.send(MessageType.RequestMarket),
      list: (slotIndex: number, quantity: number, price: number) => this.room?.send(MessageType.MarketList, { slotIndex, quantity, price }),
      buy: (listingId: string) => this.room?.send(MessageType.MarketBuy, { listingId }),
      cancel: (listingId: string) => this.room?.send(MessageType.MarketCancel, { listingId }),
    });
    setFridgeActions({
      request: () => this.room?.send(MessageType.RequestFridge, { buildingId: this.fridgeBuildingId }),
      store: (slotIndex: number) => this.room?.send(MessageType.FridgeStore, { buildingId: this.fridgeBuildingId, slotIndex }),
      take: (slotIndex: number) => this.room?.send(MessageType.FridgeTake, { buildingId: this.fridgeBuildingId, slotIndex }),
      setMenu: (items: string[]) => this.room?.send(MessageType.SetMenu, { buildingId: this.fridgeBuildingId, items }),
      setPrice: (itemId: string, price: number) => this.room?.send(MessageType.SetPrice, { buildingId: this.fridgeBuildingId, itemId, price }),
      setCost: (itemId: string, cost: number) => this.room?.send(MessageType.SetCost, { buildingId: this.fridgeBuildingId, itemId, cost }),
      setMax: (itemId: string, max: number) => this.room?.send(MessageType.SetMax, { buildingId: this.fridgeBuildingId, itemId, max }),
      collect: (amount: number) => this.room?.send(MessageType.CollectProfit, { buildingId: this.fridgeBuildingId, amount }),
      deposit: (amount: number) => this.room?.send(MessageType.DepositFunds, { buildingId: this.fridgeBuildingId, amount }),
    });
    setSafeboxActions({
      request: (buildingId, fid) => this.room?.send(MessageType.RequestSafebox, { buildingId, fid }),
      store: (buildingId, fid, slotIndex, to) => this.room?.send(MessageType.SafeboxStore, { buildingId, fid, slotIndex, to }),
      take: (buildingId, fid, slotIndex, to) => this.room?.send(MessageType.SafeboxTake, { buildingId, fid, slotIndex, to }),
      move: (buildingId, fid, from, to) => this.room?.send(MessageType.SafeboxReorder, { buildingId, fid, from, to }),
      bagMove: (from, to) => this.room?.send(MessageType.MoveItem, { from, to }),
    });
    setCookActions({ cook: (recipeId: string, quantity: number) => this.startCook(recipeId, quantity) });
    setSewActions({ sew: (recipeId: string, quantity: number) => this.startSew(recipeId, quantity) });
    setFoodShopActions({ buy: (itemId: string) => this.room?.send(MessageType.BuyFood, { buildingId: this.zone, itemId }) });
    setDispenserActions({ dispense: (itemId: string) => this.room?.send(MessageType.Dispense, { buildingId: this.zone, itemId }) });
    setTradeActions({
      offer: (items, cash) => this.room?.send(MessageType.TradeOffer, { items, cash }),
      lock: (locked) => this.room?.send(MessageType.TradeLock, { locked }),
      confirm: () => this.room?.send(MessageType.TradeConfirm),
      cancel: () => this.room?.send(MessageType.TradeCancel),
      respond: (accept) => this.room?.send(MessageType.TradeRespond, { accept }),
    });
    setShopActions({
      buy: (itemId: string) => this.room?.send(MessageType.Buy, { itemId }),
    });
    setSalonActions({
      setHair: (hair: number, hairColor: number) => this.room?.send(MessageType.SetHair, { hair, hairColor }),
    });
    setInspectOwnName(session.character?.name ?? "My Character"); // title for the self stats sheet
    setFishBoardActions({
      setAnonymous: (anonymous) => this.room?.send(MessageType.SetAnonymous, { anonymous }),
    });
    setFishingDebugActions({
      setLevel: (level) => this.room?.send(MessageType.FishLevelSet, { level } satisfies FishLevelSetMessage),
    });
    setInventoryActions({
      hasBmxOut: () => (this.me?.bmxState ?? 0) !== 0,
      rodBait: (rod, action, worm) => this.room?.send(MessageType.RodBait, { rod, worm, action } satisfies RodBaitMessage),
      drop: (slotIndex: number) => this.room?.send(MessageType.Drop, { slotIndex }),
      equip: (slotIndex: number) => this.room?.send(MessageType.Equip, { slotIndex }),
      unequip: (slot) => this.room?.send(MessageType.Unequip, { slot }),
      move: (from: number, to: number) => this.room?.send(MessageType.MoveItem, { from, to }),
      use: (slot) => {
        // The Ghost Land scroll — single-use: the server consumes one unit; the client opens a
        // shared timed portal to the city (anyone nearby can use it too).
        if (slot.itemId === GHOST_SCROLL_ID && this.zone !== "village") {
          this.room?.send(MessageType.UseItem, { slotIndex: slot.slotIndex });
          closeInventory();
          this.requestPortal("ghostland");
        } else if (slot.itemId === BLUNT_ID) {
          // Smoke a blunt — the server consumes one, flips the synced `smoking` flag (drives the
          // standing-smoke idle), and heals over time. Close the bag so you can watch.
          this.room?.send(MessageType.UseItem, { slotIndex: slot.slotIndex });
          closeInventory();
        } else if (slot.itemId === HOG_CHARM_ID) {
          // Summon (or re-summon) the pet hog next to you — the server gates the 1-min cooldown and
          // is NOT consumed. Close the bag so you can see your new companion appear.
          this.room?.send(MessageType.UseItem, { slotIndex: slot.slotIndex });
          closeInventory();
        } else if (slot.itemId === BMX_ID) {
          // Deploy/stow the BMX — the server toggles bmxState (parked at your feet / away).
          this.room?.send(MessageType.UseItem, { slotIndex: slot.slotIndex });
          closeInventory();
        } else if (isFood(slot.itemId)) {
          // Eat/drink — the server restores hunger/thirst and consumes one.
          this.room?.send(MessageType.UseItem, { slotIndex: slot.slotIndex });
        } else if (slot.itemId === WEED_SEED_ID) {
          // Plant a weed seed at your feet — the server validates the spot (open Kapon ground)
          // and consumes the seed. Close the bag so you can see the sprout.
          this.room?.send(MessageType.UseItem, { slotIndex: slot.slotIndex });
          closeInventory();
        } else if (isFurnitureItem(slot.itemId)) {
          // Furniture — enter placement mode (inside a property you own); the server consumes the
          // item only when the placement is confirmed.
          closeInventory();
          this.tryStartPlacing(slot);
        }
      },
      useNeck: () => { this.room?.send(MessageType.UseNeck, {}); closeInventory(); }, // summon/dismiss from the worn charm
      // Watering pot: right-click → Fill, enabled only at the water's edge (server re-validates).
      fillPot: (slotIndex: number) => this.room?.send(MessageType.FillPot, { slotIndex }),
      canFillPot: () => this.nearWater(),
      // Rolling paper: right-click → Roll starts the 5s channel (bar over the player; moving cancels).
      roll: (slotIndex: number) => {
        if (this.roll.isRolling() || this.mine.isMining() || this.skin.isSkinning() || this.fishingCtl.isFishing()) return;
        closeInventory();
        this.roll.start(slotIndex, performance.now());
      },
      hasPet: () => !!this.room && this.room.state.petHogs.has(`pet_${this.room.sessionId}`),
      setShowCosmetics: (show: boolean) => this.room?.send(MessageType.ShowCosmetics, { show }),
    });
    setInspectActions({ setShowCosmetics: (show: boolean) => this.room?.send(MessageType.ShowCosmetics, { show }) });

    this.resize();
    this.lastSent = { x: this.px.x, y: this.px.y, dir: Direction.Down, moving: false, running: false, combat: false };
    void this.connectMultiplayer();
    hideStatus();
    this.loop();
    void this.revealWhenReady();
  }

  /** Drop the boot veil only when the world is actually ready to look at: the local player's
   *  appearance has composited + applied, every scene texture has finished downloading, and at
   *  least one frame has painted with them. Prevents loading in to white/untextured sprites. */
  private async revealWhenReady(): Promise<void> {
    // Wait for the player to actually be textured — NOT just for one setAppearance() to resolve.
    // The network's initial appearance sync fires a second setAppearance that supersedes the boot
    // one (gen guard), so the boot composite can resolve white; `textured` resolves only once a real
    // texture has been applied, whichever call wins. A timeout guards against a failed/missing asset
    // trapping the player on the loading screen forever.
    await Promise.race([
      this.player.textured,
      new Promise<void>((r) => window.setTimeout(r, 8000)),
    ]);
    // Also wait for the server's first inventory sync so the bag is populated before we reveal —
    // otherwise you load in with an empty inventory that fills a beat later. Timeout-guarded.
    await Promise.race([
      this.firstInventory,
      new Promise<void>((r) => window.setTimeout(r, 8000)),
    ]);
    await whenAssetsIdle();   // scene props (TextureLoader) finished downloading
    await this.prewarm();     // compile shaders + upload every texture so the first frame is instant
    endTransition();
  }

  /** Pre-compile every shader program and upload every texture for the current view, then let two
   *  frames paint. THREE otherwise does this lazily on first render — which is exactly the white
   *  sprites + frame drops seen the instant the veil drops. compileAsync resolves once the GPU is
   *  warm, so the first visible frame is fully textured and smooth. */
  private async prewarm(): Promise<void> {
    try { await this.renderer.compileAsync(this.scene, this.camera); }
    catch { /* fall back to lazy compile if unsupported */ }
    // One frame to present the warmed scene, a second to absorb any residual upload before reveal.
    await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));
  }

  // --- input ------------------------------------------------------------------

  private bindInput(): void {
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("resize", this.onResize);
    const el = this.renderer.domElement;
    el.addEventListener("pointerdown", (e) => {
      this.dragging = true; this.lastPointer = { x: e.clientX, y: e.clientY };
      this.downPos = { x: e.clientX, y: e.clientY }; this.pointerMoved = false;
    });
    window.addEventListener("pointerup", (e) => {
      const wasCanvas = this.dragging; // true only if the gesture began on the 3D canvas (not UI)
      this.dragging = false;
      // While placing furniture, canvas gestures belong to the ghost (drag/rotate) — no click-to-select.
      if (wasCanvas && !this.pointerMoved && !this.placing) this.handleNpcClick(e.clientX, e.clientY); // a click, not a drag
    });
    window.addEventListener("pointermove", (e) => {
      if (!this.dragging) return;
      if (Math.abs(e.clientX - this.downPos.x) + Math.abs(e.clientY - this.downPos.y) > 6) this.pointerMoved = true;
      const dx = e.clientX - this.lastPointer.x, dy = e.clientY - this.lastPointer.y;
      if (this.placing) {
        // FURNITURE-PLACEMENT: the held pointer drags/spins the ghost instead of orbiting the camera.
        this.dragPlacement(e, dx);
        this.lastPointer = { x: e.clientX, y: e.clientY };
        return;
      }
      if (this.fishingCtl?.isFishing()) {
        // While fishing the camera is fixed on the water — the held pointer is the REEL, not a look-drag.
        this.lastPointer = { x: e.clientX, y: e.clientY };
        return;
      }
      const vdy = getInvertMouse() ? -dy : dy; // the "Invert Mouse" setting flips vertical look
      if (this.myPokerSeat >= 0) {
        // Seated at poker: drag turns the FIRST-PERSON look (head), not the 3rd-person orbit.
        this.fpYaw -= dx * 0.005;
        this.fpYaw = Math.max(-1.2, Math.min(1.2, this.fpYaw));     // ~±70° left/right
        this.fpPitch = Math.max(-0.7, Math.min(0.5, this.fpPitch - vdy * 0.005)); // look down at the cards / up at the table
      } else {
        this.cam.yaw -= dx * 0.005;
        this.cam.pitch = Math.min(1.15, Math.max(0.04, this.cam.pitch + vdy * 0.005));
      }
      this.lastPointer = { x: e.clientX, y: e.clientY };
    });
    // Non-passive + preventDefault so the wheel (and trackpad pinch, which fires a
    // wheel with ctrlKey) zooms the CAMERA instead of the browser zooming the page.
    el.addEventListener("wheel", (e) => {
      e.preventDefault();
      const p = this.placing;
      if (p) {
        // Placing furniture: the wheel raises/lowers the ghost (Z) instead of zooming the camera.
        p.z = Math.max(0, Math.min(FURNITURE_MAX_Z, p.z - e.deltaY * 0.004));
        this.updatePlacementGhost();
        return;
      }
      this.cam.dist = Math.min(16, Math.max(2.5, this.cam.dist + e.deltaY * 0.01));
    }, { passive: false });
    // Safari trackpad pinch uses gesture* events (not wheel) — block its page zoom too.
    for (const ev of ["gesturestart", "gesturechange", "gestureend"]) {
      el.addEventListener(ev, (e) => e.preventDefault());
    }
  }

  private handleKeyDown(e: KeyboardEvent): void {
    const k = e.key.toLowerCase();
    if (isChatOpen()) return; // typing in chat — let the input handle everything
    if (isBuildingManageOpen() && k !== "escape") return; // staff panel is modal (its own click handlers)
    if (isAuctionBidOpen() && k !== "escape") return;     // bid panel is modal too (typing an amount)
    if (this.cinema) {
      // Cinematic tour: EVERY key only advances the catalogue until it finishes (nothing else works).
      advanceNpcDialog();
      return;
    }
    // FURNITURE-PLACEMENT mode: E/Enter confirms, Esc cancels (the drag itself is pointer-driven);
    // anything it doesn't consume (walking, bag, menu…) falls through as normal.
    if (this.placing && this.handlePlacementKey(k, e)) return;
    if (k === "enter") { this.keys.clear(); openChat(); return; } // Enter opens the chat bar
    this.keys.add(k);
    // Downed or stunned: actions are LOCKED (no pickup/interact/skills/combat). Inventory + menu stay.
    if ((this.me?.dead || this.me?.stunned) && k !== "i" && k !== "escape") return;
    if (k === "t") {
      // T steps through a selected portal; otherwise it talks to the selected NPC.
      if (this.selectedPortal) this.enterPortal();
      else if (isNpcDialogOpen()) hideNpcDialog();
      else if (this.selectedNpc) this.openDialog(this.selectedNpc);
      else if (this.selectedCityNpc) this.talkCityNpc();
    } else if (k === "~" || k === "`") {
      // Fishing DEBUG panel (dev builds only): instant bites + forced fish kind + fish stats.
      // Bound to ~ / ` (same physical key) — macOS routes bare F-keys to media controls.
      if (import.meta.env.DEV) toggleFishingDebug();
    } else if (k === "p") {
      // P toggles the inspect window: a selected player → their gear; nothing selected → my stats.
      if (isInspectOpen()) closeInspect();
      else if (this.selectedRemote) this.inspectRemote();
      else openSelfStats();
    } else if (k === "b") {
      // B buys the listed building you're standing on (owned by someone else + on the market);
      // otherwise it sends a trade request to the selected player (server throttles trades to 1 per 30s).
      const ap = this.activePortal;
      const bid = ap && isBuildingId(ap.to) ? ap.to : null;
      if (bid && this.iCanBid(bid)) this.promptBid(bid);
      else if (bid && this.iCanBuy(bid)) this.confirmBuyBuilding(bid);
      else if (this.selectedRemote && !isTradeOpen()) this.room?.send(MessageType.TradeRequest, { to: this.selectedRemote.sessionId });
    } else if (k === "tab") {
      e.preventDefault(); // don't let Tab move browser focus
      this.cycleTarget();
    } else if (k === "1" || k === "2" || k === "3" || k === "4") {
      this.activateSlot(Number(k)); // skill bar: trigger the skill bound to this slot
    } else if (k === "e") {
      // E is the world-interact key (combat actions are now on the 1-4 skill bar).
      if (this.castCharge) return; // key-repeat while charging a cast — the meter is running
      // An open NPC dialog eats E first: skip the typewriter / advance a continue-phase.
      if (isNpcDialogOpen()) { advanceNpcDialog(); return; }
      if (this.me?.resting) {
        // In bed — E gets up (the server toggles rest off regardless of the payload).
        this.room?.send(MessageType.UseFurniture, { buildingId: this.zone, fid: "" });
        return;
      }
      if (this.fishingCtl.isFishing()) { this.stopFishing(); return; } // E again reels up / stops fishing
      if (this.ridingBmx()) {
        // hop off — the bike parks right here (server records the spot)
        this.bmxVel.set(0, 0);
        this.room?.send(MessageType.BmxMount, { on: false } satisfies BmxMountMessage);
        return;
      }
      const p = this.activePortal;
      if (p) {
        const bid = isBuildingId(p.to) ? p.to : null;
        // A locked building admits only its owner + employees; everyone else is turned away.
        if (!(bid && this.buildingLocked(bid) && !this.iMayEnterLocked(bid))) this.travel(p);
      } else if (this.nearOwnBmx()) {
        this.room?.send(MessageType.BmxMount, { on: true } satisfies BmxMountMessage); // hop on
      } else if (this.nearAptElevator()) {
        this.openAptElevator(); // lobby elevator — pick a floor to ride to
      } else if (this.aptWindowHere()) {
        this.room?.send(MessageType.ToggleCurtains, { buildingId: this.zone }); // draw/open the curtains
      } else if (this.highlightKey) {
        this.room?.send(MessageType.Pickup); // a ground item is in range — pick it up (any zone)
      } else if (this.tryOpenChest()) {
        // opened a nearby loot chest (the loot panel is now showing)
      } else if (this.tryOpenPlantPanel()) {
        // opened the weed-plant status panel (Kapon only)
      } else if (this.nearTable() && this.mySeat === -1) {
        this.room?.send(MessageType.JoinTable); // sit at the blackjack table; TableSeat opens the UI
      } else if (this.nearPokerTable() && this.myPokerSeat === -1) {
        this.room?.send(MessageType.JoinPokerTable); // sit at the poker table; PokerSeated opens the HUD
      } else if (this.nearAtm()) {
        openAtm();
      } else if (this.nearOwnBmx() && !this.ridingBmx()) {
      this.prompt.textContent = "Press E to ride your BMX";
      this.prompt.style.display = "block";
    } else if (this.nearFishBoard()) {
        openFishBoard(); // the records scroll — fills in when the FishBoard snapshot arrives
        this.room?.send(MessageType.FishBoardRequest);
      } else if (this.nearRankboard()) {
        openRankboard();
      } else if (this.nearStorage()) {
        openStorage();
      } else if (this.nearLeatherMachine()) {
        openCraftStation("leather_machine"); // warehouse machine — pick a recipe + quantity
      } else if (this.nearCraftingTable()) {
        openCraftStation("crafting_table"); // warehouse crafting table (leather armor etc.)
      } else if (this.nearCookingPot()) {
        this.openCookingPot(); // Watabaga pot — clocked-in staff cook hog burgers
      } else if (this.nearFoodFridge()) {
        this.openWatabagaFridge(); // Watabaga fridge — clocked-in staff store inventory
      } else if (this.nearDrinkDispenser()) {
        this.openDrinkDispenser(); // Watabaga dispenser — clocked-in staff pour drinks
      } else if (this.nearStoreChest()) {
        this.openStoreChest(); // Threads back-room chest — clocked-in staff store inventory
      } else if (this.nearSewingMachine()) {
        this.openSewingMachine(); // Threads sewing machine — clocked-in staff craft clothing
      } else if (this.tryFurnitureInteract()) {
        // opened a safebox / the furniture menu / collected the OKKA register
      } else if (this.zone === "kapon" && this.trySkin()) {
        // started skinning a nearby hog corpse (or showed a "need a knife / level" notice)
      } else if ((this.zone === "village" || this.zone === "kapon") && this.tryStartMining()) {
        // started mining a nearby node (or showed a "need a pickaxe / level" notice)
      } else if (this.zone === "village" && this.tryCutGrass()) {
        // cut a nearby grass tuft (or showed a "need a cutter" notice)
      } else if (this.tryBeginCast()) {
        // cast a line at the lake's edge (or showed a "need a rod" notice)
      }
    } else if (k === "l") {
      // Toggle a building's lock at its entrance — or from anywhere INSIDE a home you own / hold a
      // key to (lock the door behind you).
      const bid = this.buildingHere();
      if (bid && this.iMayLock(bid)) this.room?.send(MessageType.ToggleLock, { buildingId: bid });
    } else if (k === "m") {
      // M is context-aware: at the door of (or inside) a building you own/admin it opens the MANAGE
      // panel; anywhere else it toggles the QUEST LOG (WoW-style). A second M always closes the log.
      if (isQuestLogOpen()) { closeQuestLog(); return; }
      const bid = this.buildingHere();
      if (bid && (this.iOwnBuilding(bid) || this.me?.admin)) this.openManageFor(bid);
      else toggleQuestLog();
    } else if (k === "i") {
      toggleInventory();
    } else if (k === "u") {
      // U opens the crafting recipe book (across learned professions); toggle closed if open.
      if (isCraftOpen()) closeCraft(); else openCraftBook();
    } else if (k === "k") {
      toggleSkillPage();
    } else if (k === "j") {
      this.toggleCombat();
    } else if (k === " " || k === "spacebar") {
      e.preventDefault(); // don't let Space scroll the page
      this.startJump();
    } else if (k === "escape") {
      if (isFishBoardOpen()) { closeFishBoard(); return; }
      // ESC closes whatever panel is open first; with nothing open it toggles the game menu.
      if (isElevatorOpen()) closeElevator();
      else if (isAuctionBidOpen()) closeAuctionBid();
      else if (isBuildingManageOpen()) closeBuildingManage();
      else if (isQuestLogOpen()) closeQuestLog();
      else if (isGameMenuOpen()) closeGameMenu();
      else if (isTradeInviteOpen()) { this.room?.send(MessageType.TradeRespond, { accept: false }); closeTradeInvite(); } // decline
      else if (isTradeOpen()) this.room?.send(MessageType.TradeCancel); // server closes both sides
      // NOTE: the inspect window is intentionally NOT closed by ESC — it toggles with P only.
      else if (isSkillPageOpen()) closeSkillPage();
      else if (isCraftOpen()) closeCraft();
      else if (isCookOpen()) closeCook();
      else if (isSewOpen()) closeSew();
      else if (isDispenserOpen()) closeDispenser();
      else if (isFoodShopOpen()) closeFoodShop();
      else if (isFridgeOpen()) closeFridge();
      else if (isSafeboxOpen()) closeSafebox();
      else if (isPlantPanelOpen()) closePlantPanel();
      else if (isFurnitureMenuOpen()) closeFurnitureMenu();
      else if (isSellOpen()) closeSell();
      else if (isMarketOpen()) closeMarket();
      else if (isInventoryOpen() || isAtmOpen() || isShopOpen() || isSalonOpen() || isRankboardOpen() || isStorageOpen()) { closeInventory(); closeAtm(); closeShop(); closeSalon(); closeRankboard(); closeStorage(); }
      else toggleGameMenu();
    }
  }

  /** HD-2D post-processing: a per-pixel depth-of-field that softens the world by distance.
   *  (Bloom removed — it washed out white clothing/highlights.) */
  private setupComposer(): void {
    // pmndrs/postprocessing: robust depth handling (its composer manages the depth texture the DOF
    // needs). HalfFloat buffers for clean DOF. RenderPass → EffectPass(DOF).
    const composer = new EffectComposer(this.renderer, { frameBufferType: THREE.HalfFloatType });
    composer.addPass(new RenderPass(this.scene, this.camera));
    const dof = new DepthOfFieldEffect(this.camera, { bokehScale: 0.7 });
    dof.cocMaterial.worldFocusRange = 80; // world units — large sharp band (proper setter; the ctor option is normalized)
    composer.addPass(new EffectPass(this.camera, dof));
    this.composer = composer; this.dof = dof;
  }

  private resize(): void {
    const w = window.innerWidth, h = window.innerHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    // Render at a reduced internal resolution, then CSS-upscale with nearest for the
    // chunky "everything pixelated" world look — the sprites already carry the palette.
    const scale = Math.min(1, PIXEL_TARGET_H / h);
    const rw = Math.round(w * scale), rh = Math.round(h * scale);
    this.renderer.setPixelRatio(1);
    this.renderer.setSize(rw, rh, false);
    this.composer.setSize(rw, rh);
  }

  /** World position (head height) of a combat target id (player / remote / hog / pet / guard), or null. */
  private targetWorldPos(id: string): THREE.Vector3 | null {
    if (id === this.room?.sessionId) { const p = this.player.group.position; return new THREE.Vector3(p.x, p.y + 1.4, p.z); }
    const r = this.remotes.get(id);
    if (r) { const p = r.bb.group.position; return new THREE.Vector3(p.x, p.y + 1.4, p.z); }
    let p: { x: number; z: number } | null = null; let head = 1.4;
    if (id.startsWith("goblin")) p = this.goblins?.posOf(id) ?? null;
    else if (id.startsWith("slime")) p = this.slimes?.posOf(id) ?? null;
    else if (id.startsWith("hog")) p = this.hogs?.posOf(id) ?? null;
    else if (id.startsWith("pet_")) { p = this.pets?.posOf(id) ?? null; head = 0.9; }
    else if (id.startsWith("guard")) p = this.guards?.posOf(id) ?? null;
    return p ? new THREE.Vector3(p.x, kaponHeightAt(p.x, p.z) + head, p.z) : null;
  }

  /** Project a world point to screen pixels, or null if it's behind the camera. */
  private projectToScreen(v: THREE.Vector3): { x: number; y: number } | null {
    const p = v.clone().project(this.camera);
    if (p.z > 1) return null;
    return { x: (p.x * 0.5 + 0.5) * window.innerWidth, y: (-p.y * 0.5 + 0.5) * window.innerHeight };
  }

  // --- main loop --------------------------------------------------------------

  private loop = (): void => {
    if (this.disposed) return;
    this.raf = requestAnimationFrame(this.loop);
    const dt = this.clock.getDelta();
    this.updateLocal(dt);
    this.maybeSendMovement(dt * 1000);
    this.updateRemotes(dt);
    const player = { x: this.px.x * U, z: this.px.y * U };
    const rz = this.renderZone(); // the world we're rendered IN (apartment homes render the village)
    // Whoever we're mid-conversation with stands still + faces us until the dialogue fully ends
    // (held every frame; a short grace after the panel closes, then they go back to their day).
    if (this.talkingNpc) {
      if (isNpcDialogOpen()) this.talkingNpc.lookTimer = 10;
      else { this.talkingNpc.lookTimer = 1.5; this.talkingNpc = null; }
    }
    for (const z of Object.keys(this.npcByZone) as ZoneId[]) {
      const active = z === rz; // village NPCs stay visible through an apartment's open window
      // In first-person poker, the casino NPCs (the dealer) face the actual camera, not the orbit yaw.
      const npcYaw = z === "casino" && this.build.pokerNpc ? this.viewYaw(this.build.pokerNpc.x, this.build.pokerNpc.z) : this.cam.yaw;
      this.npcByZone[z]!.update(dt, npcYaw, player, active, active ? this.selectedNpc : null);
    }
    this.updateGroundItems();
    this.updateAptLights(); // apartment ceiling lights follow occupancy (light on = someone's home)
    if (this.zone === "tackle") this.build.tackleBucket?.tick(this.cam.yaw); // 8-dir bucket sprite frame
    // Mine nodes (village only). Run the mining channel; when the bar fills, ask the server
    // to grant the ore for that node.
    this.mine.setVisible(rz === "village" || rz === "kapon");
    const minedId = this.mine.tick(player.x, player.z, performance.now());
    if (minedId) this.room?.send(MessageType.Mine, { nodeId: minedId });
    // Skinning channel (Kapon corpses): when the bar fills, ask the server to grant the hide.
    const skinnedId = this.skin.tick(player.x, player.z, performance.now());
    if (skinnedId) this.room?.send(MessageType.Skin, { hogId: skinnedId });
    // Rolling channel (paper + buds): when the bar fills, ask the server to swap them for a Blunt.
    const rolledSlot = this.roll.tick(player.x, player.z, performance.now());
    if (rolledSlot !== null) this.room?.send(MessageType.RollBlunt, { slotIndex: rolledSlot });
    // Fishing (Kapon lake or the city pond). CHARGING: drive the power meter while E is held (and
    // drop the wind-up if the player wanders from the shore). CAST: the held pointer reels the
    // catch bar; on a catch ask the server to grant the fish; on an escape just exit. Leaving
    // fishable water (zone change / teleport) cancels.
    if (this.castCharge) {
      if (!this.nearWater()) { this.castCharge = null; this.fishingCtl.chargeHide(); this.setOwnFishState(0); }
      else this.fishingCtl.chargeUpdate(this.castPower());
    }
    if (this.fishingCtl.isFishing()) {
      const steer = ((this.keys.has("d") || this.keys.has("arrowright")) ? 1 : 0)
        - ((this.keys.has("a") || this.keys.has("arrowleft")) ? 1 : 0);
      const ev = this.fishingCtl.tick(performance.now(), steer);
      if (ev?.type === "bite") { audio.play("attack"); this.setOwnFishState(4); } // hooked — the rod bends
      else if (ev?.type === "caught") { this.room?.send(MessageType.Fish, { kind: ev.kind }); this.room?.send(MessageType.FishStop); this.lastSentFishState = 0; this.castPoint = null; }
      else if (ev?.type === "escaped") { showToast("It slipped off the line… and took the bait."); this.room?.send(MessageType.FishStop, { escaped: true } satisfies FishStopMessage); this.lastSentFishState = 0; this.castPoint = null; }
      if (!this.waterBody() && this.fishingCtl.isFishing()) this.stopFishing();
    } else if (!this.castCharge) {
      this.setOwnFishState(0); // the rod only appears while actually casting/fishing
    }
    // Crafting channel (over the player): each completed 10s unit asks the server to process ONE
    // unit; the CraftResult drives the SUCCESS/FAIL pop before the next unit begins.
    const unit = this.craft.tick(player.x, player.z, performance.now());
    if (unit) this.room?.send(cookRecipeById(unit.recipeId) ? MessageType.Cook : sewRecipeById(unit.recipeId) ? MessageType.Sew : MessageType.Craft, unit);
    // Tell the server when our mining channel starts/ends (covers start, cancel-on-move, and
    // completion in one place) so everyone else sees our swinging-pickaxe animation.
    const nowMining = this.mine.isMining();
    if (nowMining !== this.wasMining) {
      this.wasMining = nowMining;
      this.room?.send(nowMining ? MessageType.MineStart : MessageType.MineStop);
    }
    // Likewise for crafting → the synced spinning-gear over our head, seen by everyone.
    const nowCrafting = this.craft.isCrafting();
    if (nowCrafting !== this.wasCrafting) {
      this.wasCrafting = nowCrafting;
      this.room?.send(nowCrafting ? MessageType.CraftStart : MessageType.CraftStop);
    }
    // Grass tufts (village only): just render + sway; cutting is an instant E action.
    this.grass.setVisible(rz === "village");
    this.grass.tick(dt);
    this.cats?.setVisible(rz === "village");
    this.cats?.tick(dt, this.cam.yaw);
    this.hogs?.tick(dt, this.cam.yaw, this.zone === "kapon");
    this.goblins?.tick(dt, this.cam.yaw, this.zone === "kapon");
    this.slimes?.tick(dt, this.cam.yaw, this.zone === "kapon");
    // Pets follow their owner across EVERY scene: render my own pet always; others' pets only when
    // they're in the same zone as me (so you see another player's hoglet trotting beside them).
    this.pets?.tick(dt, this.cam.yaw, (ownerSid, sx, sy) =>
      ownerSid === this.room?.sessionId || this.build?.zoneOf(sx, sy) === this.zone);
    this.guards?.tick(dt, this.cam.yaw, this.zone === "kapon");
    this.chests?.tick(dt, this.zone === "kapon");
    this.plants?.setVisible(this.zone === "kapon");
    setHighVisible(!!this.me?.smoking); // blunt "high" screen filter while the smoke loop runs
    this.cityNpcs?.tick(dt, this.cam.yaw, rz, this.build.zoneOf); // each customer renders only in its own zone's world
    this.updateSelectRing(); // pixelated white ring under the selected player/NPC/mob
    distanceBlur.setEnabled(false); // per-object blur off — the depth-of-field pass blurs by distance
    // Distance culling: hide far scenery (incl. background trees) past distanceBlur.cullRadius.
    if (this.zone === "kapon") distanceBlur.cull(player.x, player.z); else distanceBlur.uncull();
    // Day/night cycle (outdoor zones: plaza spawn island, Ghostland city, Kapon) on a global clock.
    // The sky rig follows the player; in outdoor zones match the fog + background to the sky + tint the
    // unlit sprites (trees/items/players/NPCs/mobs) so they read as day/night too.
    // The apartment homes count as OUTDOOR: their open windows put them in the village world, so
    // the sun/moon/sky and day/night tint stay live (sunlight fills the room at noon).
    const outdoor = rz === "plaza" || rz === "village" || rz === "kapon";
    dayNight.setEnabled(outdoor);
    dayNight.setCenter(player.x, player.z);
    dayNight.tick(dt);
    this.dbgT += dt; // DEBUG: report day/night lamp state ~1/s
    if (this.dbgT >= 1 && outdoor) {
      this.dbgT = 0;
      console.log(`[dayNight] night=${dayNight.debugNight.toFixed(2)} lamps=${dayNight.debugLightCount} nearestLampEdge=${dayNight.nearestLampDist(player.x, player.z).toFixed(1)} zone=${this.zone}`);
    }
    if (outdoor) {
      if (this.scene.fog) this.scene.fog.color.copy(dayNight.horizon);
      if (this.scene.background instanceof THREE.Color) this.scene.background.copy(dayNight.horizon);
      else this.scene.background = dayNight.horizon.clone();
    }
    // Audio: keep the listener on the player; loop city ambient in the outdoor city/plaza.
    audio.setListener(this.px.x * U, this.px.y * U);
    if (rz === "village" || rz === "plaza") audio.startAmbient("ambient_city");
    else audio.stopAmbient();
    // Auto-close the loot panel if the player wandered out of range of the open chest.
    if (isChestOpen() && !this.chests?.nearest(this.px.x, this.px.y, CHEST_RANGE)) closeChest();
    // Same for the weed-plant panel: walking away from the inspected plant closes it.
    if (isPlantPanelOpen() && this.plants?.nearest(this.px.x, this.px.y, WEED_INTERACT_RANGE)?.key !== currentPlantId()) closePlantPanel();
    this.chat.tick(performance.now(), (sid) => {
      if (sid === this.room?.sessionId) { const p = this.player.group.position; return { x: p.x, y: p.y, z: p.z, visible: true }; }
      const e = this.remotes.get(sid);
      if (!e) return null;
      const p = e.bb.group.position;
      return { x: p.x, y: p.y, z: p.z, visible: e.bb.group.visible };
    });
    // Animate every shared portal (spin/pulse/countdown); show only those in this zone. The
    // SERVER owns their lifetime — they persist for the full timer so anyone can pass through.
    for (const [key, e] of this.portals) {
      const here = e.zone === this.zone;
      e.group.visible = here;
      e.tick(dt);
      // The wobble loop hums while a portal is open in this zone (proximity-faded); stop it otherwise.
      if (here) { audio.startLoop(`portal:${key}`, "portal_loop"); audio.setLoopAt(`portal:${key}`, e.group.position.x, e.group.position.z); }
      else audio.stopLoop(`portal:${key}`);
    }
    if (this.selectedPortal && this.selectedPortal.zone !== this.zone) this.deselectPortal();
    if (this.zone !== "kapon") { this.deselectGuard(); } // guards only exist in Kapon
    if (this.zone !== "village") { this.deselectCat(); }  // cats only roam the city
    cameraView.yaw = this.cam.yaw; // publish for world props that pick a facing sprite (Kapon directional tents/totems)
    // Tavern door leaves stand OPEN while the tavern is unlocked (closed only when locked).
    this.build.animate(dt, !this.buildingLocked("tavern")); // spawn-island Winston's doors
    this.updateCamera();
    // Focus the DOF on the player's real distance from the camera; everything else softens by distance.
    this.dof.cocMaterial.worldFocusDistance = this.camera.position.distanceTo(this.player.group.position);
    this.composer.render();
    this.renderOverheadUI();
  };

  /** Draw the overhead UI sprites (names, health bars, chat/dialogue bubbles, working-FX icons) in a
   *  CRISP overlay pass on top of the post-processed frame. They live on UI_LAYER, which the composer's
   *  RenderPass camera excludes, so the depth-of-field never softens them — fixing the blur seen when
   *  the camera pitches up toward the sky (the sky depth behind a depthTest:false sprite would otherwise
   *  drive a huge circle-of-confusion). depthTest is off on these sprites, so no depth clear is needed.
   *  NOTE: the EffectComposer permanently sets `renderer.autoClear = false` (it manages clears itself),
   *  which is exactly what this overlay wants — so we must NOT flip autoClear back on or the composer's
   *  intermediate buffers get wrongly cleared next frame (whole screen goes white).
   *  NOTE 2: `scene.background` (the day/night horizon Color) force-clears the framebuffer even with
   *  autoClear off, so we null it for the overlay render — otherwise it repaints the sky over the whole
   *  composited frame (screen goes solid horizon-colour) before the sparse UI draws on top. */
  private renderOverheadUI(): void {
    const cam = this.camera;
    const bg = this.scene.background;
    this.scene.background = null;                 // don't force-clear the composited frame to the sky colour
    this.renderer.setRenderTarget(null);          // draw onto the screen, over the composited DoF frame
    cam.layers.set(UI_LAYER);                      // see only the overhead-UI layer
    this.renderer.render(this.scene, cam);
    cam.layers.set(0);                             // restore: world layer only (composer RenderPass)
    this.scene.background = bg;
  }


  private updateLocal(dt: number): void {
    let moving = false, running = false;
    if (!isTableOpen() && this.myPokerSeat < 0 && !this.fishingCtl.isFishing() && !this.me?.dead && !this.me?.stunned && !this.me?.resting && !this.isBusyActing()) { // rooted while seated/fishing/downed/stunned/resting/attacking/blocking
      const fwd = new THREE.Vector3();
      this.camera.getWorldDirection(fwd);
      fwd.y = 0;
      if (fwd.lengthSq() < 1e-6) fwd.set(0, 0, -1);
      fwd.normalize();
      const right = new THREE.Vector3().crossVectors(fwd, new THREE.Vector3(0, 1, 0)).normalize();
      const move = new THREE.Vector3();
      if (this.keys.has("w") || this.keys.has("arrowup")) move.add(fwd);
      if (this.keys.has("s") || this.keys.has("arrowdown")) move.sub(fwd);
      if (this.keys.has("d") || this.keys.has("arrowright")) move.add(right);
      if (this.keys.has("a") || this.keys.has("arrowleft")) move.sub(right);
      moving = move.lengthSq() > 1e-6;
      running = moving && this.keys.has("shift") && !this.exhausted; // sprint, unless out of stamina
      if (this.ridingBmx()) {
        // ── BMX physics (GTA-style momentum): WASD pedals TOWARD the camera-relative input, but
        // the bike carries VELOCITY — releasing coasts against drag, opposing input brakes, and
        // steering ROTATES the velocity at a rate that tightens with speed (carved arcs, no
        // instant strafing). Shift sprints the pedals (stamina), walls kill that axis' momentum.
        const v = this.bmxVel;
        const hasInput = moving;
        const agi = statsStore.get().baseStats.agility.level + statsStore.get().statBonus.agility;
        const top = BMX_SPEED * deriveMoveSpeedMult(agi) * (running ? BMX_SPRINT_MULT : 1);
        if (hasInput) {
          move.normalize();
          const sp = v.length();
          if (sp < 8) {
            // standing start: kick off straight toward the input
            v.set(move.x, move.z).multiplyScalar(Math.min(top, sp + BMX_ACCEL * dt));
          } else {
            const cur = Math.atan2(v.x, v.y);
            const want = Math.atan2(move.x, move.z);
            let dA = want - cur;
            while (dA > Math.PI) dA -= Math.PI * 2;
            while (dA < -Math.PI) dA += Math.PI * 2;
            if (Math.abs(dA) > Math.PI * 0.72) {
              // input opposes the motion — BRAKE hard (then kick off the other way once stopped)
              const ns = Math.max(0, sp - BMX_BRAKE * dt);
              v.setLength(Math.max(ns, 1e-4));
              if (ns <= 0) v.set(0, 0);
            } else {
              // carve: turn rate shrinks as speed grows (wider arcs at full tilt)
              const turn = BMX_TURN_MAX + (BMX_TURN_MIN - BMX_TURN_MAX) * Math.min(1, sp / BMX_SPEED);
              const a = cur + Math.max(-turn * dt, Math.min(turn * dt, dA));
              const ns = Math.min(top, sp + BMX_ACCEL * dt);
              v.set(Math.sin(a) * ns, Math.cos(a) * ns);
            }
          }
        } else {
          v.multiplyScalar(Math.exp(-BMX_DRAG * dt)); // coast
          if (v.length() < 6) v.set(0, 0);
        }
        if (v.length() > top) v.setLength(Math.max(top, v.length() - BMX_BRAKE * dt)); // sprint released
        const dx = v.x * dt, dy = v.y * dt;
        const stuck = this.blocked(this.px.x, this.px.y);
        if (stuck || !this.blocked(this.px.x + dx, this.px.y)) this.px.x += dx; else v.x = 0;
        if (stuck || !this.blocked(this.px.x, this.px.y + dy)) this.px.y += dy; else v.y = 0;
        if (v.lengthSq() > 25) this.faceAngle = Math.atan2(v.x, v.y);
        moving = v.lengthSq() > 100;       // pose/anim flags
        running = running && moving;       // stamina only drains while actually sprint-pedaling
      } else {
        this.bmxVel.set(0, 0); // momentum dies with the dismount
        if (moving) {
        move.normalize();
        // Advance in server pixels so positions stay in the authority's space. Base speed scales
        // with AGILITY (1 + agility×0.01); sprint = RUN_MULT faster on top. Axis-separated so the
        // player slides along walls.
        const agi = statsStore.get().baseStats.agility.level + statsStore.get().statBonus.agility; // incl. worn-gear agility
        const speed = MOVE_SPEED * deriveMoveSpeedMult(agi) * (running ? RUN_MULT : 1);
        const dx = move.x * speed * dt;
        const dy = move.z * speed * dt;
        // ESCAPE HATCH: if the current spot is ALREADY inside a blocker (a scene update moved
        // collision boxes under a saved position — e.g. the pond arriving), blocking every axis
        // would trap the player forever. While overlapped, all movement is allowed, so they can
        // simply walk out; normal per-axis wall-sliding resumes once they're clear.
        const stuck = this.blocked(this.px.x, this.px.y);
        if (stuck || !this.blocked(this.px.x + dx, this.px.y)) this.px.x += dx;
        if (stuck || !this.blocked(this.px.x, this.px.y + dy)) this.px.y += dy;
        this.faceAngle = Math.atan2(move.x, move.z);
        }
      }
      // Fence to the current zone's walkable bounds.
      const b = this.build.zones[this.zone]!.bounds;
      this.px.x = Math.min(b.x1, Math.max(b.x0, this.px.x));
      this.px.y = Math.min(b.y1, Math.max(b.y0, this.px.y));
    }
    this.running = running;
    this.tickStamina(dt, running);
    if (moving && this.mine.isMining()) this.mine.cancel(); // moving interrupts the channel
    if (moving && this.skin.isSkinning()) this.skin.cancel(); // moving interrupts skinning too
    if (moving && this.roll.isRolling()) this.roll.cancel();  // moving interrupts rolling a blunt
    if (moving && this.craft.isChanneling()) { this.craft.cancel(); showToast("Crafting cancelled — you moved."); } // must stand still
    const pokerSeatPos = this.myPokerSeat >= 0 ? this.build.pokerSeats?.[this.myPokerSeat] : undefined;
    const seat = pokerSeatPos ?? (this.mySeat >= 0 ? this.build.casinoSeats?.[this.mySeat] : undefined);
    if (seat) {
      // Seated at a table: perch on our stool, facing the dealer (north). Poker uses the real seated
      // sprite (sits lower); blackjack keeps the higher idle perch.
      this.player.setPosition(seat.x, pokerSeatPos ? POKER_SEAT_SIT_Y : SEAT_SIT_Y, seat.z);
      if (pokerSeatPos && this.build.pokerNpc) {
        this.player.setHeading(Math.atan2(this.build.pokerNpc.x - seat.x, this.build.pokerNpc.z - seat.z), false, false);
      } else {
        this.player.setMotion(Direction.Up, false, false); // blackjack: face the (north) dealer
      }
      this.player.setSitting(true);
      this.player.group.visible = this.myPokerSeat < 0; // hide own billboard in first-person poker
    } else if (this.me?.resting) {
      // RESTING in a bed: perch on it (the server snapped our synced position to the bed's centre;
      // px was mirrored when the flag flipped) at the mattress height + the bed's own lift.
      this.player.setPosition(this.px.x * U, this.floorY() + this.bedZAt(this.px.x, this.px.y) + BED_SIT_Y, this.px.y * U);
      this.player.setHeading(this.faceAngle, false, false);
      this.player.setSitting(true);
      this.player.group.visible = true;
    } else {
      const riding = this.ridingBmx();
      this.player.setBmx(riding);
      this.player.setSitting(riding); // saddle = the seated pose
      if (riding) this.player.setBmxSpeed(this.bmxVel.length() * U);
      this.player.group.visible = !this.fishingCtl.isFishing(); // hide own billboard in first-person fishing
      this.tickJump(dt); // advance the hop arc (jumpY); ended → setJumping(false)
      this.player.setPosition(this.px.x * U, kaponHeightAt(this.px.x * U, this.px.y * U) + this.floorY(), this.px.y * U);
      this.player.setHop(this.jumpY + (riding ? BMX_SIT_Y : 0)); // saddle lift + bunny-hop arc (shadow stays grounded)
      this.player.setHeading(this.faceAngle, moving && !riding, running && !riding);
    }
    this.syncParkedBmx("me", this.me);
    // Footsteps: play a (randomised) step on a gait cadence while moving — faster while sprinting.
    if (moving && !seat && !this.me?.dead) {
      this.stepTimer += dt * 1000;
      const interval = running ? 360 : 520;
      if (this.stepTimer >= interval) {
        this.stepTimer = 0;
        const steps = ["step1", "step2", "step3"] as const;
        audio.playAt(steps[Math.floor(Math.random() * steps.length)], this.px.x * U, this.px.y * U, 16, 0.5);
      }
    } else {
      this.stepTimer = 999; // reset so the first step fires the instant you start moving
    }
    const smoking = !seat && !!this.me?.smoking;
    if (smoking && !this.wasSmokingLocal) audio.play("smoke"); // lit a blunt → chill puff
    this.wasSmokingLocal = smoking;
    this.player.setSmoking(smoking); // Blunt buff: stand-and-smoke idle
    this.player.tick(dt * 1000, this.cam.yaw);
    this.cards3d?.tick(dt * 1000);
    if (this.me) {
      this.player.setHealth(this.me.health, this.me.maxHealth); this.player.setDead(this.me.dead);
      this.player.setSkeleton(this.me.affinity < 0); // see your own red-named skeleton too
      workingStore.set(!!this.me.working); // mirror the shift flag → inventory locks the uniform slots
      const stn = !!this.me.stunned;
      if (stn && !this.wasStunned) { this.player.stagger(); this.camShake = Math.max(this.camShake, 0.22); } // blocked → blue stagger
      this.wasStunned = stn;
    }
    this.updateWeaponSprite(); // per-frame: swing while attacking, raise while blocking
  }

  /** Per-frame stamina: drain while sprinting, regen after a quiet delay, push to the HUD store.
   *  `running` is the already-stamina-gated sprint flag from updateLocal. */
  private tickStamina(dt: number, running: boolean): void {
    const bs = statsStore.get().baseStats, sb = statsStore.get().statBonus; // worn-gear stat bonuses
    this.staminaMax = deriveMaxStamina(bs.agility.level + sb.agility, bs.strength.level + sb.strength);
    if (this.stamina > this.staminaMax) this.stamina = this.staminaMax;
    if (running) this.consumeStamina(STAMINA_RUN_DRAIN * dt); // continuous sprint drain
    this.staminaIdleMs += dt * 1000;
    if (this.staminaIdleMs >= STAMINA_REGEN_DELAY_MS && this.stamina < this.staminaMax) {
      this.stamina = Math.min(this.staminaMax, this.stamina + STAMINA_REGEN_RATE * dt);
    }
    if (this.stamina <= 0) this.exhausted = true;                       // out of stamina → forced walk
    else if (this.exhausted && this.stamina >= STAMINA_RUN_RESUME) this.exhausted = false; // hysteresis
    staminaStore.set(this.stamina, this.staminaMax, running);
  }

  /** Spend stamina (sprint/attack/block) and reset the regen delay. Drains to 0 even if short.
   *  Cumulative spend trains AGILITY: every AGILITY_STAMINA_COST spent reports a tick to the server. */
  private consumeStamina(amount: number): void {
    const spent = Math.min(amount, this.stamina); // only what was actually drained counts
    this.stamina = Math.max(0, this.stamina - amount);
    this.staminaIdleMs = 0;
    this.staminaSpent += spent;
    if (this.staminaSpent >= AGILITY_STAMINA_COST) {
      const ticks = Math.floor(this.staminaSpent / AGILITY_STAMINA_COST);
      this.staminaSpent -= ticks * AGILITY_STAMINA_COST;
      this.room?.send(MessageType.StaminaSpent, { ticks });
    }
  }

  /** Space: start a jump hop if grounded. Costs stamina; blocked while seated/downed/stunned/mid-action
   *  or too tired. Bumps jumpSeq server-side so everyone else sees the hop too. */
  private startJump(): void {
    if (this.jumpActive) return;                                   // already airborne
    if (this.me?.dead || this.me?.stunned || this.me?.resting) return; // downed/stunned/in-bed can't jump
    if (isTableOpen() || this.myPokerSeat >= 0 || this.mySeat >= 0) return; // not while seated at a table
    if (this.isBusyActing()) return;                               // not mid-swing / blocking
    if (this.stamina < STAMINA_JUMP_COST) return;                  // need stamina (also covers exhausted)
    this.consumeStamina(STAMINA_JUMP_COST);
    this.jumpActive = true;
    this.jumpT = 0;
    this.player.setJumping(true);
    this.room?.send(MessageType.Jump);
  }

  /** Advance the local jump arc (a parabola up to JUMP_HEIGHT, then back to ground). */
  private tickJump(dt: number): void {
    if (!this.jumpActive) return;
    this.jumpT += dt * 1000;
    const p = Math.min(1, this.jumpT / JUMP_DURATION_MS);
    this.jumpY = 4 * JUMP_HEIGHT * p * (1 - p); // 0 → peak → 0
    if (p >= 1) { this.jumpActive = false; this.jumpY = 0; this.player.setJumping(false); }
  }

  /** Left-click: select the clicked NPC (show its target frame + name/[E] label), or
   *  deselect when clicking anything that isn't an NPC. */
  private handleNpcClick(clientX: number, clientY: number): void {
    const ndc = new THREE.Vector2((clientX / window.innerWidth) * 2 - 1, -(clientY / window.innerHeight) * 2 + 1);
    this.pickRay.setFromCamera(ndc, this.camera);
    // Wild hogs first (the combat targets) — only in their zone.
    const hogKey = this.zone === "kapon" ? (this.hogs?.pick(this.pickRay) ?? null) : null;
    if (hogKey) { this.clearTargets(); this.selectHog(hogKey); return; }
    const goblinKey = this.zone === "kapon" ? (this.goblins?.pick(this.pickRay) ?? null) : null;
    if (goblinKey) { this.clearTargets(); this.selectGoblin(goblinKey); return; }
    const slimeKey = this.zone === "kapon" ? (this.slimes?.pick(this.pickRay) ?? null) : null;
    if (slimeKey) { this.clearTargets(); this.selectSlime(slimeKey); return; }
    // A pet hog — selectable (yours, to inspect its health; or someone else's, as a combat target).
    // Attacking your own pet is blocked in attack() / on the server.
    const petKey = this.pets?.pick(this.pickRay) ?? null;
    if (petKey) { this.clearTargets(); this.selectPet(petKey); return; }
    // Gate guards (friendly NPCs) — selectable like any NPC.
    const guardKey = this.zone === "kapon" ? (this.guards?.pick(this.pickRay) ?? null) : null;
    if (guardKey) { this.clearTargets(); this.selectGuard(guardKey); return; }
    const npc = this.activeNpcs()?.pick(this.pickRay) ?? null;
    if (npc) { this.clearTargets(); this.selectNpc(npc); return; }
    const cityNpcKey = this.cityNpcs?.pick(this.pickRay) ?? null; // synced townsfolk / customers
    if (cityNpcKey) { this.clearTargets(); this.selectCityNpc(cityNpcKey); return; }
    // The warehouse crafting stations: click one (when near) to open its station UI.
    const lm = this.build.leatherMachine;
    if (lm && this.zone === "village" && this.pickRay.intersectObject(lm.obj, true).length > 0) {
      this.clearTargets();
      if (this.nearLeatherMachine()) openCraftStation("leather_machine");
      else showToast("Get closer to the Leather Machine.");
      return;
    }
    const ct = this.build.craftingTable;
    if (ct && this.zone === "village" && this.pickRay.intersectObject(ct.obj, true).length > 0) {
      this.clearTargets();
      if (this.nearCraftingTable()) openCraftStation("crafting_table");
      else showToast("Get closer to the Crafting Table.");
      return;
    }
    // This restaurant's kitchen props: the cooking pot + fridge + dispenser (clocked-in staff only). Each
    // restaurant (Watabaga, Ghost's, …) has its OWN props, keyed by zone — not just Watabaga's.
    const props = isRestaurantBuilding(this.zone) ? this.build.restaurantProps?.[this.zone] : undefined;
    const pot = props?.pot;
    if (pot && this.pickRay.intersectObject(pot.obj, true).length > 0) {
      this.clearTargets();
      if (this.nearCookingPot()) this.openCookingPot();
      else showToast("Get closer to the cooking pot.");
      return;
    }
    const fri = props?.fridge;
    if (fri && this.pickRay.intersectObject(fri.obj, true).length > 0) {
      this.clearTargets();
      if (this.nearFoodFridge()) this.openWatabagaFridge();
      else showToast("Get closer to the fridge.");
      return;
    }
    const disp = props?.dispenser;
    if (disp && this.pickRay.intersectObject(disp.obj, true).length > 0) {
      this.clearTargets();
      if (this.nearDrinkDispenser()) this.openDrinkDispenser();
      else showToast("Get closer to the dispenser.");
      return;
    }
    // Placed furniture (bed / safebox) — click one nearby for its Open / Move / Pick up menu.
    const fhit = this.furnitureLayer.pick(this.pickRay);
    if (fhit) {
      const obj = this.furnitureLayer.objectOf(fhit.bid, fhit.fid);
      const d = obj ? Math.hypot(obj.position.x - this.px.x * U, obj.position.z - this.px.y * U) : Infinity;
      this.clearTargets();
      if (d <= 3.2) this.openFurnitureMenu(fhit);
      else showToast("Get closer to it.");
      return;
    }
    const remote = this.pickRemote();
    if (remote) { this.clearTargets(); this.selectRemote(remote); return; }
    for (const e of this.portals.values()) {
      if (e.zone === this.zone && e.pick(this.pickRay)) { this.clearTargets(); this.selectPortal(e); return; }
    }
    // Ambient cats (village) — selectable to see their name, but nothing to interact with.
    const catKey = this.zone === "village" ? (this.cats?.pick(this.pickRay) ?? null) : null;
    if (catKey) { this.clearTargets(); this.selectCat(catKey); return; }
    this.clearTargets();
  }

  /** Drop every kind of current selection (NPC / remote / hog / guard / cat / portal). */
  private clearTargets(): void {
    this.deselectNpc(); this.deselectRemote(); this.deselectHog(); this.deselectGoblin(); this.deselectSlime(); this.deselectPet(); this.deselectGuard(); this.deselectCat(); this.deselectCityNpc(); this.deselectPortal();
  }

  /** Select an ambient cat — shows its name on the target card + the ground ring. No interaction. */
  private selectCat(key: string): void {
    this.selectedCat = key;
    const info = this.cats?.infoOf(key);
    showTarget(null, info?.name ?? "Cat", { actions: [] }); // name only — no health bar, no action hints
  }

  private deselectCat(): void {
    if (!this.selectedCat) return;
    this.selectedCat = null;
    hideNpcTarget();
  }

  /** Keep the pixelated ground ring under whatever is currently selected (mob / player / NPC). */
  private updateSelectRing(): void {
    if (this.selectedHog && this.hogs) {
      const p = this.hogs.posOf(this.selectedHog);
      if (p) { this.selectRing.setAt(p.x, kaponHeightAt(p.x, p.z), p.z); return; }
    } else if (this.selectedGoblin && this.goblins) {
      const p = this.goblins.posOf(this.selectedGoblin);
      if (p) { this.selectRing.setAt(p.x, kaponHeightAt(p.x, p.z), p.z); return; }
    } else if (this.selectedSlime && this.slimes) {
      const p = this.slimes.posOf(this.selectedSlime);
      if (p) { this.selectRing.setAt(p.x, kaponHeightAt(p.x, p.z), p.z); return; }
    } else if (this.selectedPet && this.pets) {
      const p = this.pets.posOf(this.selectedPet);
      if (p) { this.selectRing.setAt(p.x, kaponHeightAt(p.x, p.z), p.z); return; }
    } else if (this.selectedGuard && this.guards) {
      const p = this.guards.posOf(this.selectedGuard);
      if (p) { this.selectRing.setAt(p.x, kaponHeightAt(p.x, p.z), p.z); return; }
    } else if (this.selectedRemote && this.selectedRemote.bb.group.visible) {
      const g = this.selectedRemote.bb.group.position;
      this.selectRing.setAt(g.x, g.y, g.z); return;
    } else if (this.selectedNpc && this.selectedNpc.bb.group.visible) {
      const g = this.selectedNpc.bb.group.position;
      this.selectRing.setAt(g.x, g.y, g.z); return;
    } else if (this.selectedCat && this.cats) {
      const p = this.cats.posOf(this.selectedCat);
      if (p) { this.selectRing.setAt(p.x, p.y, p.z); return; }
    } else if (this.selectedCityNpc && this.cityNpcs) {
      const p = this.cityNpcs.posOf(this.selectedCityNpc);
      if (p) { this.selectRing.setAt(p.x, 0, p.z); return; }
    }
    this.selectRing.hide();
  }

  private selectHog(key: string): void {
    this.selectedHog = key;
    const info = this.hogs?.infoOf(key);
    showNpcTarget(null, "Wild Hog", info?.health ?? 0, info?.maxHealth ?? 0);
  }

  private deselectHog(): void {
    if (!this.selectedHog) return;
    this.selectedHog = null;
    hideNpcTarget();
  }

  private selectGoblin(key: string): void {
    this.selectedGoblin = key;
    const info = this.goblins?.infoOf(key);
    showNpcTarget(null, "Goblin", info?.health ?? 0, info?.maxHealth ?? 0);
  }
  private deselectGoblin(): void {
    if (!this.selectedGoblin) return;
    this.selectedGoblin = null;
    hideNpcTarget();
  }

  private selectSlime(key: string): void {
    this.selectedSlime = key;
    const info = this.slimes?.infoOf(key);
    showNpcTarget(null, "Slime", info?.health ?? 0, info?.maxHealth ?? 0);
  }
  private deselectSlime(): void {
    if (!this.selectedSlime) return;
    this.selectedSlime = null;
    hideNpcTarget();
  }

  /** Select another player's pet hog — shows its name + health (it's killable, like a hog). */
  private selectPet(key: string): void {
    this.selectedPet = key;
    const info = this.pets?.infoOf(key);
    showNpcTarget(null, "Hoglet", info?.health ?? 0, info?.maxHealth ?? 0);
  }

  private deselectPet(): void {
    if (!this.selectedPet) return;
    this.selectedPet = null;
    hideNpcTarget();
  }

  /** Select a gate guard (a friendly NPC) — shows its target frame + the ground ring. */
  private selectGuard(key: string): void {
    this.selectedGuard = key;
    const info = this.guards?.infoOf(key);
    showNpcTarget(null, info?.name ?? "Gate Guard", info?.health ?? 0, info?.maxHealth ?? 0);
  }

  private deselectGuard(): void {
    if (!this.selectedGuard) return;
    this.selectedGuard = null;
    hideNpcTarget();
  }

  /** Select a synced city NPC (townsperson / customer) — a name-only target frame (no health, no combat). */
  private selectCityNpc(key: string): void {
    this.selectedCityNpc = key;
    showTarget(null, this.cityNpcs?.nameOf(key) ?? "Townsperson", { actions: [{ key: "T", action: "Talk" }] });
  }
  private deselectCityNpc(): void {
    if (!this.selectedCityNpc) return;
    this.selectedCityNpc = null;
    hideNpcTarget();
  }
  /** A throwaway one-liner when you talk to a synced townsperson (they're ambiance, no scripted dialog). */
  private talkCityNpc(): void {
    const lines = [
      "Morning. Fine day for it.", "Mind how you go.", "Heard the casino's paying out.",
      "Watabaga does a mean hog burger.",
      "Stay out of the wilds after dark.", "Just passing through, friend.",
    ];
    audio.play("npc_talk");
    const name = this.selectedCityNpc ? (this.cityNpcs?.nameOf(this.selectedCityNpc) ?? "Townsperson") : "Townsperson";
    showNpcDialog(null, name, lines[Math.floor(Math.random() * lines.length)]);
  }

  /** Pixel-accurate pick of a remote player's billboard under the current pick ray (same zone only). */
  private pickRemote(): RemotePlayer | null {
    const inZone = [...this.remotes.values()].filter((e) => e.bb.group.visible);
    const hits = this.pickRay.intersectObjects(inZone.map((e) => e.bb.group), true);
    for (const h of hits) {
      if (!h.uv) continue;
      const e = inZone.find((r) => r.bb.group === h.object.parent);
      if (e && e.bb.hitTestUV(h.uv)) return e;
    }
    return null;
  }

  private selectNpc(npc: Npc): void {
    this.selectedNpc = npc;
    showNpcTarget(npc.head, npc.name, npc.health, npc.maxHealth);
  }

  /** Select a remote player — their name+[P] label appears above them, the target card shows their
   *  portrait, and P opens the inspect window. */
  private selectRemote(e: RemotePlayer): void {
    this.selectedRemote = e;
    e.label.visible = true;
    const actions = [{ key: "P", action: "Inspect" }, { key: "B", action: "Trade" }];
    showTarget(null, e.player.name, { actions }); // immediate (portrait fills in)
    const gen = ++this.targetPortraitGen;
    // Bust shows their DISPLAY appearance (gear + cosmetic overlay), exactly as their avatar renders.
    const display = this.displayAppearance(appearanceFromSchema(e.player.appearance), e.player, e.player.showCosmetics, e.player.working);
    void composeBust(display).then((bust) => {
      if (gen === this.targetPortraitGen && this.selectedRemote === e) showTarget(bust, e.player.name, { actions });
    });
  }

  private deselectRemote(): void {
    if (!this.selectedRemote) return;
    this.selectedRemote.label.visible = false;
    this.selectedRemote = null;
    this.targetPortraitGen++; // invalidate any in-flight portrait
    hideNpcTarget();
  }

  /** Open the read-only inspect window for the selected remote player. */
  private inspectRemote(): void {
    const e = this.selectedRemote;
    if (!e) return;
    const mods = { top: decodeItemMods(e.player.topMods), bottom: decodeItemMods(e.player.bottomMods) };
    const cos = { hat: e.player.cosHat, top: e.player.cosTop, bottom: e.player.cosBottom, shoes: e.player.cosShoes };
    openInspect(e.player.name, appearanceFromSchema(e.player.appearance), e.player.hand1, e.player.hand2, mods, cos, e.player.showCosmetics);
  }

  /** Open a conversation with an NPC: a scripted branching dialog, or a random one-liner. */
  private openDialog(npc: Npc): void {
    audio.play("npc_talk");
    this.npcByZone[this.zone]?.lookAtPlayer(npc); // turn to face the player while talking
    this.talkingNpc = npc; // held still (re-faced every frame) until the dialogue fully closes
    let node = npc.dialog;
    // Sal's conversation depends on the player's quest state — built fresh on every talk.
    if (npc.name === "Sal") node = this.salDialog() ?? node;
    node = this.withQuestOptions(npc.name, node);
    // Ghost Lab's counter also retails Ghost's tech (the BMX) alongside the food-shop flow.
    if (node && this.zone === "ghosts") {
      node = { ...node, options: [{ label: "Buy items", action: "openGhostLabShop" as NpcAction }, ...(node.options ?? [])] };
    }
    // Watabaga staff get a Clock In/Out option on the vendor (the NPC that runs the food shop). The
    // fridge + pot are physical props in the kitchen (use them while clocked in), not dialog options.
    if (node && isBusinessBuilding(this.zone) && this.iWorkAt(this.zone) && node.options?.some((o) => o.action === "openFoodShop")) {
      const label = this.me?.working ? "Clock out" : "Clock in (work)";
      const extra = isRestaurantBuilding(this.zone)
        ? [{ label: "Buy kitchen supplies", action: "openSupplies" as NpcAction }] // raw potatoes for the pot
        : [];
      node = { ...node, options: [
        { label, action: "clockToggle" as NpcAction },
        ...extra,
        ...(node.options ?? []),
      ] };
    }
    // The OKKA clerk offers the OWNER the register takings (sales accrue there like any store).
    if (node && this.zone === "furni" && this.iOwnBuilding(FURNI_BUILDING_ID)) {
      node = { ...node, options: [{ label: "Collect the register", action: "collectRegister" as NpcAction }, ...(node.options ?? [])] };
    }
    // A for-sale building you don't own → its vendor offers to sell you the property on the spot.
    if (node && isBuildingId(this.zone) && this.iCanBuy(this.zone)) {
      const bid = this.zone;
      const price = this.buildings.get(bid)?.salePrice ?? 0;
      const label = `Buy ${buildingName(bid)} — $${price.toLocaleString()}`;
      node = { ...node, options: [{ label, action: "buyBuilding" as NpcAction }, ...(node.options ?? [])] };
    }
    // A building with an open position → a non-staff visitor can submit a job application. Slot it SECOND,
    // after the vendor's first option (e.g. "Show me the menu" comes first, then "Apply for a job").
    if (node && isBuildingId(this.zone) && this.canApplyAt(this.zone)) {
      const apply = { label: "Apply for a job", action: "applyJob" as NpcAction };
      const opts = node.options ?? [];
      node = { ...node, options: opts.length ? [opts[0], apply, ...opts.slice(1)] : [apply] };
    }
    if (node) this.showDialogNode(npc, node);
    else showNpcDialog(npc.head, npc.name, NpcManager.line(npc));
  }

  /** Do I work at (own or am employed by) this building? */
  private iWorkAt(id: string): boolean {
    const b = this.buildings.get(id);
    return !!b && this.myCharacterId !== "" && (b.ownerId === this.myCharacterId || b.employees.some((e) => e.id === this.myCharacterId));
  }
  /** Can I apply for a job here? (owned by someone else, has an open seat, I'm not staff/owner/already applied) */
  private canApplyAt(id: string): boolean {
    if (!isBusinessBuilding(id)) return false; // only businesses hire (the two restaurants + Threads) — no jobs at Winston's/Casino/Bank
    const b = this.buildings.get(id);
    if (!b || this.myCharacterId === "" || b.ownerId === "" || this.iWorkAt(id)) return false;
    if (b.applicants.some((a) => a.id === this.myCharacterId)) return false; // already applied
    return b.employees.length < MAX_EMPLOYEES_PER_BUILDING;
  }

  /** Sync every quest-giver's floating "!" — shown while their quest hasn't been accepted yet.
   *  Called when the quest log syncs and after a zone's NPC roster spawns. */
  private refreshQuestMarkers(): void {
    // A giver shows "!" while ANY of their quests is offerable (not yet accepted + prereq done).
    const offering = new Map<string, boolean>();
    for (const def of QUESTS) {
      if (!def.giver) continue;
      const accepted = questEntries().some((e) => e.questId === def.id);
      const unlocked = !def.requires || questEntries().some((e) => e.questId === def.requires && e.completed);
      offering.set(def.giver, (offering.get(def.giver) ?? false) || (!accepted && unlocked));
    }
    for (const [giver, offered] of offering) {
      for (const mgr of Object.values(this.npcByZone)) mgr?.setQuestMarker(giver, offered);
    }
  }

  /** Prepend giver-quest options to an NPC's dialog: an OFFER option (pixel "!") while their
   *  submit-item quest is unaccepted, a TURN-IN option while it's in progress. (Sal's intro quest
   *  has its own bespoke conversation, so this only handles quests with submit_item objectives.) */
  private withQuestOptions(npcName: string, node: DialogNode | undefined): DialogNode | undefined {
    const extra: NonNullable<DialogNode["options"]> = [];
    for (const def of QUESTS) {
      if (def.giver !== npcName) continue;
      if (def.objectives.some((o) => o.kind === "meet_giver")) continue; // bespoke intro flows (Sal)
      const submits = def.objectives.filter((o) => o.kind === "submit_item" && o.item);
      const entry = questEntries().find((e) => e.questId === def.id);
      if (entry?.completed) continue; // once only — done is done
      if (!entry) {
        // chained quests only appear once their prerequisite is completed
        if (def.requires && !questEntries().some((e) => e.questId === def.requires && e.completed)) continue;
        extra.push({ label: `${def.title} — ${def.objectives[0].text}`, action: "acceptQuest", quest: true, questId: def.id });
      } else if (submits.length) {
        const label = submits
          .map((o) => `${o.target}× ${ITEM_NAMES[o.item as keyof typeof ITEM_NAMES] ?? o.item}`)
          .join(" + ");
        extra.push({ label: `Turn in ${label}`, action: "submitQuest", questId: def.id });
      }
    }
    if (!extra.length) return node;
    // an NPC with only one-liners still gets a proper dialog while they have quest business
    const base: DialogNode = node ?? { text: "Somethin' I can do for you?" };
    return { ...base, options: [...extra, ...(base.options ?? [])] };
  }

  /** Sal's conversation, built from the local quest state. Before "First Introduction" is taken
   *  he sizes the new traveller up over a few CONTINUE phases (▼ / click / E to advance) and then
   *  offers the quest with a pixel "!" option; afterwards he's just a friendly face on the road. */
  private salDialog(): DialogNode | null {
    const def = QUESTS.find((q) => q.id === "first_introduction");
    if (!def) return null;
    const taken = questEntries().some((e) => e.questId === def.id);
    if (taken) return null; // fall back to his idle one-liners
    return {
      text: "Hmm... a new traveller. ",
      next: {
        text: "Seems the portal has opened again. Welcome to the first stop in this world.",
        next: {
          text: "I can walk you through what you'll need around here. Won't take long, and you'll be glad of it by nightfall.",
          options: [
            { label: "First Introduction — walk me through", action: "acceptIntro", quest: true },
            { label: "Maybe later" },
          ],
        },
      },
    };
  }

  /** Sal's cinematic city tour: LIVE camera shots (top of the city, then each landmark's door)
   *  captioned through the dialogue panel — click / E steps the catalogue; the final advance ends
   *  the tour, accepts + completes "First Introduction" on the server, and pays the reward. */
  private startIntroTour(npc: Npc): void {
    const b = this.build.zones.village?.bounds;
    // Each landmark's door is its entrance PORTAL trigger in the village.
    const door = (bid: ZoneId): { x: number; y: number } | null =>
      this.build.portals.find((p) => p.zone === "village" && p.to === bid)?.trigger ?? null;
    const bank = door("bank"), food = door("food"), threads = door("threads"), casino = door("casino");
    if (this.zone !== "village" || !b || !bank || !food || !threads || !casino) {
      // No city around us (shouldn't happen — Sal lives there): still grant the quest.
      this.room?.send(MessageType.AcceptQuest, { questId: "first_introduction" });
      return;
    }
    const cx = ((b.x0 + b.x1) / 2) * U, cz = ((b.y0 + b.y1) / 2) * U;
    // A landmark shot: the camera backs off the door toward the street (the city centre side),
    // eye slightly raised, looking back at the facade.
    const at = (d: { x: number; y: number }, caption: string): { pos: THREE.Vector3; look: THREE.Vector3; caption: string } => {
      const dx = d.x * U, dz = d.y * U;
      const v = new THREE.Vector3(cx - dx, 0, cz - dz);
      if (v.lengthSq() < 0.01) v.set(0, 0, 1); else v.normalize();
      return { pos: new THREE.Vector3(dx + v.x * 7, 2.6, dz + v.z * 7), look: new THREE.Vector3(dx, 1.9, dz), caption };
    };
    const shots = [
      { pos: new THREE.Vector3(cx, 46, cz + 16), look: new THREE.Vector3(cx, 0, cz), caption: "This is the BlackWater city." },
      at(bank, "We have a bank where you can store items and deposit cash."),
      at(food, "A restaurant for you to get some crafted food."),
      at(threads, "A clothing store for you to get a new fit."),
      at(casino, "A casino where you can maybe triple your money, or lose it all."),
    ];
    const play = (i: number): void => {
      if (i >= shots.length) {
        // Tour over: back to Sal in the street for one last word — advancing past it lands the
        // quest (server completes "First Introduction" + pays the reward).
        this.cinema = null;
        setNpcDialogCinematic(false);
        showNpcDialog(npc.head, npc.name,
          "There are also traveller marketplace, you can explore around the city to find out more about everything.",
          undefined, undefined, () => {
            hideNpcDialog();
            this.room?.send(MessageType.AcceptQuest, { questId: "first_introduction" });
            this.refreshQuestMarkers(); // the "!" drops the moment it's taken (server re-syncs the log too)
          });
        return;
      }
      this.cinema = { pos: shots[i].pos, look: shots[i].look };
      setNpcDialogCinematic(true); // whole screen = the camera; big caption bar; click anywhere advances
      showNpcDialog(npc.head, npc.name, shots[i].caption, undefined, undefined, () => play(i + 1));
    };
    play(0);
  }

  /** Render one node of a scripted conversation; reply buttons advance or end it. A node with no
   *  options but a `next` is a CONTINUE phase — the ▼ caret + click/E steps to the next line. */
  private showDialogNode(npc: Npc, node: DialogNode): void {
    showNpcDialog(
      npc.head, npc.name, node.text,
      node.options?.map((o) => (o.quest ? QUEST_OPT : "") + o.label),
      (i) => {
        const opt = node.options?.[i];
        if (opt?.action) { hideNpcDialog(); this.runNpcAction(opt.action, npc, opt.questId); return; }
        if (opt?.next) this.showDialogNode(npc, opt.next);
        else hideNpcDialog();
      },
      node.next ? () => this.showDialogNode(npc, node.next!) : undefined,
    );
  }

  /** Carry out a dialogue side-effect (Winston portal, miner pickaxe/sell, casino table). */
  private runNpcAction(action: NpcAction, npc?: Npc, questId?: string): void {
    if (action === "teleportSpawn") {
      this.deselectNpc();
      this.room?.send(MessageType.WinstonTeleport); // server hands back a return city scroll
      void this.goToZone("plaza", this.build.spawnIsland); // cross-region → veil + unload the city
    } else if (action === "getPickaxe") {
      this.room?.send(MessageType.MinerAction, { kind: "pickaxe" });
    } else if (action === "getCutter") {
      this.room?.send(MessageType.GardenerAction);
    } else if (action === "openStorage") {
      openStorage(); // Mr. Vault opens the storage panel (it requests its own contents)
    } else if (action === "openMarket") {
      openMarket(); // marketplace NPC — browse/buy + list (it requests its own snapshot)
    } else if (action === "openSellShop") {
      openSell(); // vendor NPC — drag items in + Trade to sell for cash
    } else if (action === "openFishBuyer") {
      // Marla only buys FISH — everything else grays out and can't be staged
      openSell({ title: "MARLA — FISH BUYER", accept: isFishItem, refuse: "Marla only buys fish" });
    } else if (action === "sellOres") {
      this.room?.send(MessageType.MinerAction, { kind: "sell" });
    } else if (action === "openCasino") {
      this.room?.send(MessageType.JoinTable); // the host seats you; TableSeat opens the UI
    } else if (action === "openPoker") {
      this.room?.send(MessageType.JoinPokerTable); // the poker dealer seats you; PokerSeated opens the HUD
    } else if (action === "openShop") {
      showShop(); // browse + buy THREADS clothing
    } else if (action === "openBlackmarket") {
      showShop({ title: "BLACK MARKET", entries: BLACKMARKET_SHOP }); // hooded trader's weapons
    } else if (action === "openSkinnerShop") {
      showShop({ title: "SKINNER", entries: SKINNER_SHOP }); // buy the skinning knife
    } else if (action === "openGhostLabShop") {
      showShop({ title: "GHOST LAB", entries: GHOSTLAB_SHOP }); // Ghost's tech — BMX, balaclava, fishing hat
    } else if (action === "openTackleShop") {
      showShop({ title: "OLD TIMER'S BAITS", entries: TACKLE_SHOP }); // fishing rod + bait worms
    } else if (action === "openSmithShop") {
      showShop({ title: "BLACKSMITH", entries: SMITH_SHOP }); // honest steel — sword + shield
    } else if (action === "learnLeatherSmith") {
      this.room?.send(MessageType.LearnProfession, { profession: "leather_smith" }); // needs 20 hog hides
    } else if (action === "learnBlackSmith") {
      this.room?.send(MessageType.LearnProfession, { profession: "black_smith" }); // needs 20 copper ore
    } else if (action === "openFoodShop") {
      this.room?.send(MessageType.RequestFoodMenu, { buildingId: this.zone }); // live menu + stock
      openFoodShop(); // fills in when the FoodMenu snapshot arrives
    } else if (action === "openSupplies") {
      showShop({ title: "WATABAGA SUPPLIES", entries: WATABAGA_SUPPLIES }); // raw potatoes for cooking
    } else if (action === "openFurniShop") {
      showShop({ title: "OKKA FURNITURE", entries: FURNITURE_SHOP }); // beds + safeboxes (sales land in the register)
    } else if (action === "collectRegister") {
      this.room?.send(MessageType.CollectProfit, { buildingId: FURNI_BUILDING_ID, amount: 0 }); // 0 ⇒ take all
    } else if (action === "clockToggle") {
      this.room?.send(MessageType.ClockToggle, { buildingId: this.zone }); // restaurant staff start/end their shift
    } else if (action === "imprintFish") {
      openImprint(inventoryStore.get(), (slotIndex) =>
        this.room?.send(MessageType.ImprintFish, { slotIndex } satisfies ImprintFishMessage), this.myCharacterId);
    } else if (action === "acceptQuest") {
      if (questId) { this.room?.send(MessageType.AcceptQuest, { questId }); this.refreshQuestMarkers(); }
    } else if (action === "submitQuest") {
      if (questId) this.room?.send(MessageType.SubmitQuestItems, { questId });
    } else if (action === "acceptIntro") {
      // Sal walks the newcomer through the city first — the quest completes when the tour ends.
      if (npc) this.startIntroTour(npc);
    } else if (action === "buyBuilding") {
      if (isBuildingId(this.zone) && this.iCanBuy(this.zone)) this.confirmBuyBuilding(this.zone);
    } else if (action === "applyJob") {
      if (isBuildingId(this.zone) && this.canApplyAt(this.zone)) this.room?.send(MessageType.ApplyJob, { buildingId: this.zone });
    } else if (action === "openSalon") {
      showSalon(); // Coco the stylist — change hair style + colour for a fee
    } else if (action === "exitKapon") {
      this.deselectNpc();
      if (!weaponStore.hasWeapon()) { showToast("The gate guard won't let you out unarmed — equip a weapon."); return; }
      const spawn = this.build.kaponSpawn;
      if (!spawn) return;
      showConfirm({
        title: "Venture into Kapon?",
        body: "Kapon is dangerous wilds. <b>If you die there, you lose EVERY item in your inventory and ALL your carried cash.</b> Bank anything you want to keep before you go.",
        confirmLabel: "Go to Kapon",
        cancelLabel: "Stay",
        danger: true,
        onConfirm: () => { void this.goToZone("kapon", spawn); },
      });
    }
  }

  /** Ask the server to open a SHARED timed portal in the CURRENT zone (anyone nearby can use
   *  it until it expires). By default it appears a few units ahead of the player; pass `at`
   *  (server px) to anchor it elsewhere (e.g. in front of an NPC). */
  private requestPortal(kind: "ghostland" | "spawn", at?: { x: number; y: number }): void {
    // Red-named (negative affinity) players are confined to the wilds — both kinds (Ghost Land → village,
    // spawn → plaza) lead to safe zones, so refuse to open either one for a flagged player.
    if ((this.me?.affinity ?? 0) < 0) {
      showToast("Your affinity is too low — you can't open a portal to safety yet.");
      return;
    }
    const b = this.build.zones[this.zone]!.bounds;
    const PXU = 1 / U; // server px per unit
    const px = at ? at.x : this.px.x;
    const py = at ? at.y : this.px.y - 1.2 * PXU; // default: just ahead of the player (~1.2u)
    const x = Math.max(b.x0 + 16, Math.min(b.x1 - 16, px));
    const y = Math.max(b.y0 + 16, Math.min(b.y1 - 16, py));
    this.room?.send(MessageType.OpenPortal, { kind, zone: this.zone, x, y });
  }

  /** Select a portal (mutually exclusive with NPC selection). */
  private selectPortal(e: PortalEntity): void {
    if (this.selectedPortal === e) return;
    this.selectedPortal?.setSelected(false);
    this.selectedPortal = e;
    e.setSelected(true);
  }

  private deselectPortal(): void {
    this.selectedPortal?.setSelected(false);
    this.selectedPortal = null;
  }

  /** Step through the selected portal — it STAYS OPEN afterwards so others can follow. */
  private enterPortal(): void {
    const p = this.selectedPortal;
    if (!p || p.zone !== this.zone) return;
    const { destZone, destSpawn } = p;
    audio.play("portal_teleport"); // warping through a magic portal
    this.deselectPortal();
    void this.goToZone(destZone as ZoneId, destSpawn);
  }

  /** Warp to a zone, lazily building + loading the CITY (village + threads) on first visit
   *  behind a loading veil so the spawn island never downloads city assets up front. */
  /** A red-named local player is confined to the wilds: refuse any warp into a SAFE zone (anything but
   *  Kapon). Guards both goToZone (shared portals + respawn) and teleportTo (static doors + the scroll),
   *  so there is no portal a flagged player can ride back to safety. Toasts + returns true when blocked. */
  private redConfinedFromZone(zone: ZoneId): boolean {
    if (zone === "kapon" || (this.me?.affinity ?? 0) >= 0) return false;
    showToast("Your affinity is too low — you can't return to safety yet.");
    return true;
  }

  private async goToZone(zone: ZoneId, spawn: { x: number; y: number }): Promise<void> {
    if (this.redConfinedFromZone(zone)) return; // red-named: barred from every safe zone
    const fromRegion = regionOfZone(this.zone);
    const toRegion = regionOfZone(zone);
    const crossRegion = fromRegion !== toRegion;
    // Same region + already resident → instant swap (e.g. village ↔ casino, plaza ↔ tavern).
    if (this.build.loaded(zone) && !crossRegion) { this.teleportTo(zone, spawn); return; }
    // Crossing a region boundary always shows the veil — even into the always-resident island — because
    // we UNLOAD the region we leave (heavy dispose), which must be hidden behind the loading screen.
    beginTransition(zone === "kapon" ? "Entering Kapon" : toRegion === "island" ? "Returning" : "Entering Ghost Land");
    this.build.ensure(zone);   // build ONLY this region's geometry + kick off its texture loads
    await whenAssetsIdle();     // hold the veil until those scene textures finish
    this.initCityNpcs();        // create the city's NPC managers now that spots exist
    this.teleportTo(zone, spawn);
    if (crossRegion) this.disposeRegion(fromRegion); // unload the region we just left (veil hides the dispose)
    // The destination's NPCs composite their character sheets via untracked `new Image()` loads,
    // so whenAssetsIdle() doesn't cover them — wait for them explicitly, then let two frames paint
    // (one to upload the textures, one to present them) so we never reveal white/untextured sprites.
    await this.npcByZone[zone]?.ready();
    await this.prewarm();       // warm the city's shaders + textures so it reveals without stutter
    endTransition();
  }

  /** Unload a region we just left: dispose its NPC managers (+ village cats) and its scene geometry
   *  (build.disposeRegion frees the GPU + drops its portals). The spawn island is always resident. */
  private disposeRegion(region: Region): void {
    if (region === "island") return;
    const zoneIds: ZoneId[] = region === "city"
      ? ["village", "threads", "casino", "bank", "citytavern", "food", "ghosts", "furni"]
      : ["kapon"];
    for (const z of zoneIds) { this.npcByZone[z]?.dispose(); delete this.npcByZone[z]; }
    if (region === "city") { this.cats?.dispose(); this.cats = undefined; }
    this.build.disposeRegion(region);
    this.refreshFurniture(); // drop furniture props that lived in the unloaded zone groups
  }

  /** Create the city zones' NPC managers (once), after the city has been built. */
  private initCityNpcs(): void {
    if (this.build.villageNpc && !this.npcByZone.village) {
      this.npcByZone.village = villageNpcs(this.scene, this.build.villageNpc, this.build.villageNpcArea ?? undefined);
      this.refreshQuestMarkers(); // Sal's "!" (the quest log may have synced before he spawned)
      if (this.build.villageNpcArea && !this.cats) {
        const a = this.build.villageNpcArea, mz = (a.z0 + a.z1) / 2;
        this.cats = new CatController(this.scene, { x0: a.x0, z0: mz - 6, x1: a.x1, z1: mz + 6 }, 3);
      }
    }
    if (this.build.threadsNpc && !this.npcByZone.threads) {
      this.npcByZone.threads = threadsNpcs(this.scene, this.build.threadsNpc);
    }
    if (this.build.casinoNpc && !this.npcByZone.casino) {
      this.npcByZone.casino = casinoNpcs(this.scene, this.build.casinoNpc, this.build.pokerNpc ?? undefined);
    }
    if (this.build.bankNpc && !this.npcByZone.bank) {
      this.npcByZone.bank = bankNpcs(this.scene, this.build.bankNpc);
    }
    if (this.build.cityTavernNpc && !this.npcByZone.citytavern) {
      this.npcByZone.citytavern = cityTavernNpcs(this.scene, this.build.cityTavernNpc.area, this.build.cityTavernNpc.bar);
    }
    if (this.build.foodNpc && !this.npcByZone.food) {
      this.npcByZone.food = foodNpcs(this.scene, this.build.foodNpc);
    }
    if (this.build.ghostsNpc && !this.npcByZone.ghosts) {
      this.npcByZone.ghosts = foodNpcs(this.scene, this.build.ghostsNpc); // Ghost's reuses the restaurant vendor
    }
    if (this.build.furniNpc && !this.npcByZone.furni) {
      this.npcByZone.furni = furniNpcs(this.scene, this.build.furniNpc);
    }
    if (this.build.tackleNpc && !this.npcByZone.tackle) {
      this.npcByZone.tackle = tackleNpcs(this.scene, this.build.tackleNpc); // the Old Timer, behind his counter
    }
    if (this.build.smithNpc && !this.npcByZone.smith) {
      this.npcByZone.smith = smithNpcs(this.scene, this.build.smithNpc); // Ember, behind the smithy counter
    }
  }

  /** Tab targeting: select the CLOSEST in-range NPC; if it's already selected, select the
   *  SECOND closest instead (so repeat presses toggle between the two nearest). */
  /** Tab through the nearest selectable things in range — NPCs, other players, and hogs. */
  private cycleTarget(): void {
    const pwx = this.px.x * U, pwz = this.px.y * U;
    type Tgt = { kind: "npc" | "remote" | "hog" | "goblin" | "slime" | "guard" | "cityNpc"; ref: Npc | RemotePlayer | string; d: number };
    const list: Tgt[] = [];
    const npcs = this.activeNpcs();
    if (npcs) for (const n of npcs.inRange(pwx, pwz, TAB_RANGE)) list.push({ kind: "npc", ref: n, d: (n.x - pwx) ** 2 + (n.z - pwz) ** 2 });
    if (this.cityNpcs) for (const c of this.cityNpcs.inRange(pwx, pwz, TAB_RANGE)) list.push({ kind: "cityNpc", ref: c.key, d: (c.x - pwx) ** 2 + (c.z - pwz) ** 2 });
    for (const e of this.remotes.values()) {
      if (!e.bb.group.visible) continue;
      const p = e.bb.group.position, d = (p.x - pwx) ** 2 + (p.z - pwz) ** 2;
      if (d <= TAB_RANGE * TAB_RANGE) list.push({ kind: "remote", ref: e, d });
    }
    if (this.zone === "kapon" && this.hogs) for (const h of this.hogs.inRange(pwx, pwz, TAB_RANGE)) list.push({ kind: "hog", ref: h.key, d: (h.x - pwx) ** 2 + (h.z - pwz) ** 2 });
    if (this.zone === "kapon" && this.goblins) for (const h of this.goblins.inRange(pwx, pwz, TAB_RANGE)) list.push({ kind: "goblin", ref: h.key, d: (h.x - pwx) ** 2 + (h.z - pwz) ** 2 });
    if (this.zone === "kapon" && this.slimes) for (const h of this.slimes.inRange(pwx, pwz, TAB_RANGE)) list.push({ kind: "slime", ref: h.key, d: (h.x - pwx) ** 2 + (h.z - pwz) ** 2 });
    if (this.zone === "kapon" && this.guards) for (const g of this.guards.inRange(pwx, pwz, TAB_RANGE)) list.push({ kind: "guard", ref: g.key, d: (g.x - pwx) ** 2 + (g.z - pwz) ** 2 });
    if (!list.length) return; // nothing in range → keep current selection
    list.sort((a, b) => a.d - b.d);
    // Find the currently-selected target, then advance (wrap) to the next.
    const cur = list.findIndex((t) =>
      (t.kind === "npc" && t.ref === this.selectedNpc) ||
      (t.kind === "remote" && t.ref === this.selectedRemote) ||
      (t.kind === "hog" && t.ref === this.selectedHog) ||
      (t.kind === "goblin" && t.ref === this.selectedGoblin) ||
      (t.kind === "slime" && t.ref === this.selectedSlime) ||
      (t.kind === "guard" && t.ref === this.selectedGuard) ||
      (t.kind === "cityNpc" && t.ref === this.selectedCityNpc));
    const next = list[(cur + 1) % list.length];
    this.clearTargets();
    if (next.kind === "npc") this.selectNpc(next.ref as Npc);
    else if (next.kind === "remote") this.selectRemote(next.ref as RemotePlayer);
    else if (next.kind === "hog") this.selectHog(next.ref as string);
    else if (next.kind === "goblin") this.selectGoblin(next.ref as string);
    else if (next.kind === "slime") this.selectSlime(next.ref as string);
    else if (next.kind === "cityNpc") this.selectCityNpc(next.ref as string);
    else this.selectGuard(next.ref as string);
  }

  private deselectNpc(): void {
    if (!this.selectedNpc) return;
    this.selectedNpc = null;
    hideNpcTarget();
    hideNpcDialog();
  }

  /** Is the player standing next to a city ATM (bank in the village, or the casino's)? */
  private nearAtm(): boolean {
    const atms = this.build.atms;
    if (!atms || (this.zone !== "village" && this.zone !== "casino")) return false;
    return atms.some((a) => {
      const dx = a.x - this.px.x, dy = a.y - this.px.y;
      return dx * dx + dy * dy <= ATM_RANGE * ATM_RANGE;
    });
  }

  /** Is the player standing at the bank's wealth rankboard? */
  private nearRankboard(): boolean {
    const rb = this.build.rankboard;
    if (!rb || this.zone !== "bank") return false;
    const dx = rb.x - this.px.x, dy = rb.y - this.px.y;
    return dx * dx + dy * dy <= ATM_RANGE * ATM_RANGE;
  }

  /** Is the player standing at the bank's vault storage terminal? */
  private nearStorage(): boolean {
    const st = this.build.storage;
    if (!st || this.zone !== "bank") return false;
    const dx = st.x - this.px.x, dy = st.y - this.px.y;
    return dx * dx + dy * dy <= ATM_RANGE * ATM_RANGE;
  }

  /** Is the player standing at the given crafting station (server px anchor, village only)? */
  private nearStation(station: CraftStation): boolean {
    if (this.zone !== "village") return false;
    const a = STATION_ANCHORS[station];
    if (!a) return false;
    const dx = a.x - this.px.x, dy = a.y - this.px.y;
    return dx * dx + dy * dy <= CRAFT_STATION_RANGE * CRAFT_STATION_RANGE;
  }
  private nearLeatherMachine(): boolean { return this.nearStation("leather_machine"); }
  private nearCraftingTable(): boolean { return this.nearStation("crafting_table"); }

  /** Near a placed furniture piece that EMBODIES a craft station (the forge = a furnace), inside
   *  a building I'm standing in and belong to (the safebox rule) — mirrors the server's
   *  nearPlacedFurniture check so the channel only starts where the craft will be accepted. */
  private nearStationFurniture(itemId: string): boolean {
    const bid = isBuildingId(this.zone) ? this.zone : null;
    if (!bid || !this.iMayAccessSafebox(bid)) return false;
    const b = this.room?.state.buildings.get(bid);
    if (!b) return false;
    const R2 = FURNITURE_USE_RANGE * FURNITURE_USE_RANGE;
    for (const f of b.furniture) {
      if (f.itemId !== itemId) continue;
      const dx = f.x - this.px.x, dy = f.y - this.px.y;
      if (dx * dx + dy * dy <= R2) return true;
    }
    return false;
  }

  /** Within range of a server-px anchor while in the CURRENT restaurant zone (pot / fridge / dispenser)? */
  private nearRestaurantAnchor(a: { x: number; y: number } | null | undefined): boolean {
    if (!isRestaurantBuilding(this.zone) || !a) return false;
    const dx = a.x - this.px.x, dy = a.y - this.px.y;
    return dx * dx + dy * dy <= CRAFT_STATION_RANGE * CRAFT_STATION_RANGE;
  }
  private nearCookingPot(): boolean { return this.nearRestaurantAnchor(cookingPotAnchor(this.zone)); }
  private nearFoodFridge(): boolean { return this.nearRestaurantAnchor(fridgeAnchor(this.zone)); }
  private nearDrinkDispenser(): boolean { return this.nearRestaurantAnchor(drinkDispenserAnchor(this.zone)); }

  /** Begin a crafting channel for a recipe (from the station UI's Process button). Requires standing
   *  at that recipe's station — a FIXED anchor (warehouse machines) or a placed furniture station
   *  (the forge = a furnace you have access to); the 10s bar runs over the player; movement
   *  cancels it. */
  private startCraft(recipeId: string, quantity: number): void {
    const recipe = recipeById(recipeId);
    if (!recipe) return;
    const stationItem = STATION_FURNITURE[recipe.station];
    const ok = stationItem ? this.nearStationFurniture(stationItem) : this.nearStation(recipe.station);
    if (!ok) {
      showToast(stationItem
        ? `Stand beside a ${ITEM_NAMES[stationItem]} you have access to.`
        : "Stand at the crafting station to craft.");
      return;
    }
    this.craft.start(recipeId, quantity, performance.now());
  }

  /** E at the Watabaga pot — must be clocked in (on shift). */
  private openCookingPot(): void {
    if (!this.me?.working) { showToast("Clock in at Watabaga to use the cooking pot."); return; }
    this.room?.send(MessageType.RequestFoodMenu, { buildingId: this.zone }); // so recipe cards show the live wage
    openCook();
  }
  /** E at the Watabaga fridge — must be clocked in (on shift). */
  private openWatabagaFridge(): void {
    if (!this.me?.working) { showToast("Clock in at Watabaga to open the fridge."); return; }
    this.fridgeBuildingId = this.zone;
    this.room?.send(MessageType.RequestFoodMenu, { buildingId: this.zone }); // owner sees the live menu editor
    openFridge();
  }
  /** E at the Watabaga drink dispenser — must be clocked in (on shift). */
  private openDrinkDispenser(): void {
    if (!this.me?.working) { showToast("Clock in at Watabaga to use the dispenser."); return; }
    this.room?.send(MessageType.RequestFoodMenu, { buildingId: this.zone }); // so the panel knows the menu drinks
    openDispenser();
  }

  /** Within range of the THREADS back-room store chest? */
  private nearStoreChest(): boolean {
    if (this.zone !== "threads") return false;
    const a = storeChestAnchor(this.zone);
    if (!a) return false;
    const dx = a.x - this.px.x, dy = a.y - this.px.y;
    return dx * dx + dy * dy <= CRAFT_STATION_RANGE * CRAFT_STATION_RANGE;
  }
  /** E at the Threads store chest — must be clocked in (on shift). Opens the store-inventory panel
   *  (stock clothes → get paid; owner sees the price/wage/max editor). */
  private openStoreChest(): void {
    if (!this.me?.working) { showToast("Clock in at Threads to open the store chest."); return; }
    this.fridgeBuildingId = this.zone;
    this.room?.send(MessageType.RequestFoodMenu, { buildingId: this.zone }); // owner sees the live editor
    openFridge();
  }

  /** Within range of the THREADS sewing machine? */
  private nearSewingMachine(): boolean {
    if (this.zone !== "threads") return false;
    const a = sewingMachineAnchor(this.zone);
    if (!a) return false;
    const dx = a.x - this.px.x, dy = a.y - this.px.y;
    return dx * dx + dy * dy <= CRAFT_STATION_RANGE * CRAFT_STATION_RANGE;
  }
  /** E at the Threads sewing machine — must be clocked in (on shift). Opens the sew-recipe menu. */
  private openSewingMachine(): void {
    if (!this.me?.working) { showToast("Clock in at Threads to use the sewing machine."); return; }
    openSew();
  }
  /** Begin a sewing channel at the Threads machine (clocked-in staff only; reuses the craft channel). */
  private startSew(recipeId: string, quantity: number): void {
    const recipe = sewRecipeById(recipeId);
    if (!recipe) return;
    if (!this.me?.working) { showToast("Clock in at Threads to sew."); return; }
    if (!this.nearSewingMachine()) { showToast("Stand at the sewing machine to sew."); return; }
    this.craft.start(recipeId, quantity, performance.now(), recipe.durationMs);
  }

  /** City zones — clocked-in shifts persist across these; leaving them (e.g. into the Kapon wild) clocks out. */
  private isCityZone(z: ZoneId): boolean {
    return z === "village" || z === "threads" || z === "casino" || z === "bank" || z === "citytavern" || z === "food" || z === "ghosts";
  }

  /** Begin a cooking channel at the Watabaga pot (clocked-in staff only; reuses the craft channel). */
  private startCook(recipeId: string, quantity: number): void {
    const recipe = cookRecipeById(recipeId);
    if (!recipe) return;
    if (!this.me?.working) { showToast("Clock in at Watabaga to cook."); return; }
    if (!this.nearCookingPot()) { showToast("Stand at the cooking pot to cook."); return; }
    this.craft.start(recipeId, quantity, performance.now(), recipe.durationMs);
  }

  // --- combat mode ----------------------------------------------------------

  /** Zones where weapons may be drawn. The city + interiors + spawn island are GREEN (safe). */
  private isCombatZone(): boolean {
    return this.zone === "kapon";
  }

  /** Toggle combat mode (J / button). Needs a weapon equipped AND a non-green zone. */
  private toggleCombat(): void {
    if (this.combat) { this.setCombat(false); return; }
    if (!weaponStore.hasWeapon()) { showToast("Equip a weapon (First/Second Hand) to enter combat."); return; }
    if (!this.isCombatZone()) { showToast("This is a safe zone — you can't draw your weapon here."); return; }
    this.setCombat(true);
  }

  private setCombat(on: boolean): void {
    this.combat = on;
    setCombatActive(on);
    this.updateWeaponSprite();
  }

  /** Activate the skill bound to skill-bar slot `n` (1-4) — only if a held weapon grants it. */
  private activateSlot(n: number): void {
    if (this.me?.stunned || this.isBusyActing()) return; // locked while stunned / mid-swing / blocking
    const id = skillStore.get()[n - 1];
    if (!id) return;
    const { hand1, hand2 } = weaponStore.get();
    if (this.ridingBmx()) { showToast("Not while riding — hop off first (E)."); return; }
    if (!availableSkillIds(hand1, hand2).includes(id)) { showToast("Equip a weapon with that skill."); return; }
    if (id === "attack") this.attack();
    else if (id === "block") this.block();
  }

  /** True while locked into an action — an attack swing OR a raised block. No movement / other ability
   *  then. The block lock uses both the synced flag AND a local timer so it engages instantly on cast. */
  private isBusyActing(): boolean {
    return this.player.isAttacking() || !!this.me?.blocking || performance.now() < this.localBlockUntil;
  }

  /** Swing the primary weapon at the SELECTED target (hog or player). Combat-only. Stamina is spent
   *  ONLY on a real swing (after the cooldown + target + busy gates). Sound/swing fire on server confirm. */
  private attack(): void {
    if (!this.combat || this.me?.dead || this.me?.stunned) return;
    if (this.isBusyActing()) return;                  // mid-swing / blocking
    if (performance.now() < this.attackReadyAt) return; // on cooldown — no swing, no stamina
    // Your own pet can be selected (to read its health) but never attacked — exclude it as a target.
    const petTarget = this.selectedPet && this.pets?.ownerOf(this.selectedPet) !== this.room?.sessionId ? this.selectedPet : null;
    const targetId = this.selectedHog ?? this.selectedGoblin ?? this.selectedSlime ?? petTarget ?? this.selectedRemote?.sessionId ?? "";
    if (!targetId) { showToast("Select a target first (click it)."); return; }
    if (this.stamina < STAMINA_ATTACK_COST) { showToast("Too winded to swing — catch your breath."); return; }
    this.attackReadyAt = performance.now() + ATTACK_COOLDOWN_MS;
    this.consumeStamina(STAMINA_ATTACK_COST);
    this.room?.send(MessageType.Attack, { targetId });
    setSkillCooldown("attack", ATTACK_COOLDOWN_MS);
    flashCooldown("attack", ATTACK_COOLDOWN_MS);
  }

  /** Raise the shield block (combat-only — you must have your shield out). Locks the player for the
   *  block's duration (committed stance). Stamina is spent ONLY when the block actually fires. */
  private block(): void {
    if (this.me?.dead || this.me?.stunned) return;
    if (!this.combat) { showToast("Draw your weapon to raise your shield."); return; } // shield must be out
    if (this.isBusyActing()) return;                 // mid-swing / already blocking
    const hasShield = isShield(weaponStore.get().hand1) || isShield(weaponStore.get().hand2);
    if (!hasShield) return;
    if (performance.now() < this.blockReadyAt) return; // on cooldown — no block, no stamina wasted
    if (this.stamina < STAMINA_BLOCK_COST) { showToast("Too winded to block."); return; }
    this.blockReadyAt = performance.now() + BLOCK_COOLDOWN_MS;
    // Lock instantly to bridge the cast→server-sync latency; after that the synced `blocking` flag
    // governs the lock for its real duration (and ends it the moment a hit consumes the block).
    this.localBlockUntil = performance.now() + 400;
    this.consumeStamina(STAMINA_BLOCK_COST);
    this.room?.send(MessageType.Block);
    setSkillCooldown("block", BLOCK_COOLDOWN_MS);
    flashCooldown("block", BLOCK_COOLDOWN_MS);
  }

  /** Drive the held sword/shield: rendered as layers composited into the billboard atlas (idle/walk/
   *  run) and baked into the swing sheet (attack), shown only while the weapon is DRAWN (combat) and
   *  alive. Replaces the old 2D placeholder sprites. */
  private updateWeaponSprite(): void {
    const dead = !!this.me?.dead;
    const hasW = isWeapon(weaponStore.get().hand1) || isWeapon(weaponStore.get().hand2);
    const hasS = isShield(weaponStore.get().hand1) || isShield(weaponStore.get().hand2);
    // Pre-build the combat (sword/shield) atlases in the background BEFORE the player draws their
    // weapon, so the combat toggle is an instant cache swap (no lag spike). No-op once cached.
    if (hasW || hasS) this.player.prewarmEquip(hasW && !dead, hasS && !dead);
    // Held layer + swing bake are gated on combat (drawn) + alive; setEquippedHands no-ops when unchanged.
    this.player.setEquippedHands(this.combat && hasW && !dead, this.combat && hasS && !dead);
    // When `attacking` flips on (SERVER-confirmed swing), play the swing sheet + the sound — so the
    // attack SFX only fires on a successful action, not a rejected keypress.
    const attacking = !!this.me?.attacking;
    if (attacking && !this.wasAttacking && this.combat && !dead) {
      this.player.playAttack();
      audio.playAt("attack", this.px.x * U, this.px.y * U);
    }
    this.wasAttacking = attacking;
    // Held block stance (raised shield) while the block flag is up; sound on the confirmed rising edge.
    const blocking = !!this.me?.blocking && !dead;
    if (blocking && !this.wasBlockingLocal) { audio.playAt("block", this.px.x * U, this.px.y * U); this.blockStartMs = performance.now(); }
    else if (!blocking && this.wasBlockingLocal) this.localBlockUntil = 0; // block ended (consumed by a hit, or expired) → release the lock NOW
    this.wasBlockingLocal = blocking;
    this.player.setBlocking(blocking);
    // Block countdown bar (above stamina): drains over the stance; hidden the instant the block ends.
    setBlockProgress(blocking ? 1 - (performance.now() - this.blockStartMs) / BLOCK_DURATION_MS : 0);
  }

  /** Is the player at the casino blackjack table (close enough for the "Press E to sit" prompt)? */
  private nearTable(): boolean {
    if (this.zone !== "casino") return false;
    const dx = BLACKJACK_TABLE_ANCHOR.x - this.px.x, dy = BLACKJACK_TABLE_ANCHOR.y - this.px.y;
    return dx * dx + dy * dy <= TABLE_SIT_RANGE * TABLE_SIT_RANGE;
  }

  /** Is the player at the casino poker table (close enough for the "Press E to sit" prompt)? */
  private nearPokerTable(): boolean {
    if (this.zone !== "casino") return false;
    const dx = POKER_TABLE_ANCHOR.x - this.px.x, dy = POKER_TABLE_ANCHOR.y - this.px.y;
    return dx * dx + dy * dy <= POKER_SIT_RANGE * POKER_SIT_RANGE;
  }

  /** Whether the player is carrying a pickaxe (an inventory item now, not a stat flag). */
  private hasPickaxe(): boolean {
    return inventoryStore.get().some((s) => s.itemId === PICKAXE_ID);
  }

  /** Whether the player is carrying a grass-cutter. */
  private hasGrassCutter(): boolean {
    return inventoryStore.get().some((s) => s.itemId === GRASS_CUTTER_ID);
  }

  private hasSkinningKnife(): boolean {
    return inventoryStore.get().some((s) => s.itemId === SKINNING_KNIFE_ID);
  }

  /** Start skinning the nearest dead hog corpse (needs a knife + high enough level). Returns true if E consumed. */
  private trySkin(): boolean {
    if (this.skin.isSkinning()) return true;
    const near = this.hogs?.nearestCorpse(this.px.x, this.px.y, SKIN_RANGE);
    if (!near) return false; // no corpse in range — let other E actions run
    if (!this.hasSkinningKnife()) { showToast("Skinning requires a skinning knife."); return true; }
    const level = statsStore.get().skinning.level;
    if (!canSkinTier(HOG_SKIN_TIER, level)) { showToast("Your skinning level is too low for this beast."); return true; }
    this.skin.start(near.key, skinDurationMs(HOG_SKIN_TIER, level), performance.now());
    return true;
  }

  /** Open the nearest loot chest's panel. Returns true if E was consumed. */
  private tryOpenChest(): boolean {
    const near = this.chests?.nearest(this.px.x, this.px.y, CHEST_RANGE);
    if (!near || !this.room) return false;
    const chest = this.room.state.chests.get(near.key);
    if (!chest) return false;
    openChest(near.key, [...chest.items], (index) =>
      this.room?.send(MessageType.TakeChestItem, { chestId: near.key, index }));
    return true;
  }

  /** The synced WeedPlant → the status panel's display data. */
  private plantPanelData(plant: WeedPlant): PlantPanelData {
    return { stage: plant.stage, status: plant.status, growth: plant.growth, water: plant.water, dryLeftMs: plant.dryLeftMs };
  }

  /** The status panel's buttons → server plant actions (+ the pot gauge it shows on Water). */
  private plantPanelActions(): PlantPanelActions {
    return {
      water: (plantId) => this.room?.send(MessageType.WaterPlant, { plantId }),
      harvest: (plantId) => this.room?.send(MessageType.HarvestPlant, { plantId }),
      clear: (plantId) => this.room?.send(MessageType.ClearPlant, { plantId }),
      potMl: () => inventoryStore.get().reduce((m, s) => (s.itemId === WATERING_POT_ID ? Math.max(m, s.water ?? 0) : m), 0),
    };
  }

  /** Open the status panel for the nearest weed plant. Returns true if E was consumed. */
  private tryOpenPlantPanel(): boolean {
    if (this.zone !== "kapon") return false;
    const near = this.plants?.nearest(this.px.x, this.px.y, WEED_INTERACT_RANGE);
    if (!near || !this.room) return false;
    const plant = this.room.state.plants.get(near.key);
    if (!plant) return false;
    openPlantPanel(near.key, this.plantPanelData(plant), this.plantPanelActions());
    return true;
  }

  /** Cut the nearest grass tuft (instant). Returns true if E was consumed. */
  private tryCutGrass(): boolean {
    const key = this.grass.nearest(this.px.x, this.px.y);
    if (!key) return false; // no tuft in range — let other E actions run
    if (!this.hasGrassCutter()) { showToast("You need a grass-cutter — talk to the gardener."); return true; }
    this.room?.send(MessageType.CutGrass, { nodeId: key });
    return true;
  }

  /** Start mining the nearest node (or prompt for a pickaxe / level). Returns true if E was consumed. */
  private tryStartMining(): boolean {
    if (this.mine.isMining()) return true;
    const key = this.mine.nearest(this.px.x, this.px.y);
    if (!key) return false; // no node in range — let other E actions run
    if (!this.hasPickaxe()) { showToast("You need a pickaxe — talk to the miner."); return true; }
    const kind = this.mine.kindOf(key) as Parameters<typeof canMineTier>[0] | null;
    const level = statsStore.get().mining.level;
    if (kind && !canMineTier(kind, level)) { showToast("Your mining level is too low for this vein."); return true; }
    this.mine.start(key, level, performance.now());
    return true;
  }

  /** Riding the BMX right now (synced Player.bmxState 2). */
  private ridingBmx(): boolean {
    return this.me?.bmxState === 2;
  }

  /** Standing close enough to our own PARKED bike to hop on. */
  private nearOwnBmx(): boolean {
    if (this.me?.bmxState !== 1) return false;
    return Math.hypot(this.px.x - this.me.bmxX, this.px.y - this.me.bmxY) <= BMX_MOUNT_RANGE;
  }

  /** Create/position/remove one player's PARKED bike prop (own + every remote, keyed by sid). */
  private syncParkedBmx(key: string, player: { bmxState: number; bmxX: number; bmxY: number } | null | undefined): void {
    const parked = player?.bmxState === 1;
    let prop = this.bmxParked.get(key);
    if (parked && player) {
      if (!prop) {
        prop = buildBmx();
        prop.group.rotation.z = 0.12; // resting lean, like it's on a kickstand
        prop.group.rotation.y = 0.9;
        this.bmxParked.set(key, prop);
        this.scene.add(prop.group);
      }
      const wx = player.bmxX * U, wz = player.bmxY * U;
      prop.group.position.set(wx, kaponHeightAt(wx, wz), wz);
      prop.group.visible = this.build.zoneOf(player.bmxX, player.bmxY) === this.renderZone();
    } else if (prop) {
      this.scene.remove(prop.group);
      prop.dispose();
      this.bmxParked.delete(key);
    }
  }

  /** Whether the player has the fishing rod EQUIPPED (primary hand) — bagged rods don't count. */
  private hasFishingRod(): boolean {
    return this.me?.hand1 === FISHING_ROD_ID;
  }

  /** Near the RECORDS SCROLL on the tackle-shop wall (the fishing leaderboard). */
  private nearFishBoard(): boolean {
    if (this.zone !== "tackle") return false;
    const b = this.build.tackleBucket?.board;
    if (!b) return false;
    // px-space distance against a WORLD-UNIT reach (convert px → units with U)
    return Math.hypot((this.px.x - b.x) * U, (this.px.y - b.y) * U) < 1.6;
  }

  /** The fishable water body in the CURRENT zone: the Kapon lake or the city pond (else null). */
  private waterBody(): { x: number; z: number; r: number } | null {
    if (this.zone === "kapon") return KAPON_TERRAIN.lake;
    if (this.zone === "village") return CITY_POND;
    return null;
  }

  /** Whether the player stands at a fishable water's edge (within casting reach). */
  private nearWater(): boolean {
    const w = this.waterBody();
    if (!w) return false;
    const wx = this.px.x * U, wz = this.px.y * U;
    return Math.hypot(wx - w.x, wz - w.z) <= w.r + FISH_RANGE * U;
  }

  /** Begin CHARGING a cast (E held at the water with a rod). Returns true if E was consumed —
   *  WITHOUT a rod the water is ignored entirely (no toast, no prompt). */
  private tryBeginCast(): boolean {
    if (this.ridingBmx()) return false; // can't fish from the saddle
    if (this.fishingCtl.isFishing() || this.castCharge) return true;
    if (!this.nearWater() || !this.hasFishingRod()) return false; // let other E actions run
    if ((this.me?.rodBait ?? 0) <= 0) { showToast("Your rod is out of bait — load Worm Bait into it."); return true; }
    this.castCharge = { since: performance.now() };
    this.fishingCtl.chargeShow();
    this.setOwnFishState(2); // rod swings back + strains while the meter runs
    return true;
  }

  /** The charge meter: 0 → 1 → 0 (triangle wave, 1.6s round trip) — release at the top to throw far. */
  private castPower(): number {
    if (!this.castCharge) return 0;
    const t = (performance.now() - this.castCharge.since) / 800;
    return 1 - Math.abs(1 - (t % 2));
  }

  /** E released: throw. The line lands along the player's facing at a distance set by the meter —
   *  in the water it starts the first-person minigame; on dry land the whole cast just cancels. */
  private releaseCast(): void {
    if (!this.castCharge) return;
    const power = this.castPower();
    this.castCharge = null;
    this.fishingCtl.chargeHide();
    const w = this.waterBody();
    const MIN_D = 1.2, MAX_D = 7.5; // world units of throw, scaled by the meter
    const dist = MIN_D + power * (MAX_D - MIN_D);
    const lx = this.px.x * U + Math.sin(this.faceAngle) * dist;
    const lz = this.px.y * U + Math.cos(this.faceAngle) * dist;
    if (!w || Math.hypot(lx - w.x, lz - w.z) > w.r * 0.95) {
      showToast(power < 0.3 ? "The cast fell short of the water." : "The cast missed the water.");
      this.setOwnFishState(0); // rod away — it only shows while casting/fishing
      return;
    }
    this.castPoint = { x: lx, z: lz };
    // the city pond is near-still + holds only the two basic fish; the Kapon lake drifts + holds all
    const pond = this.zone === "village";
    // the worn Fishing Hat (cosmetic hat slot) widens the green zone + slows the fish
    const wearsFishHat = this.me?.appearance.hat === FISHING_HAT_STYLE;
    const hatBonus = wearsFishHat ? FISH_HAT_ZONE_BONUS : 0;
    const speedMult = wearsFishHat ? FISH_HAT_SPEED_MULT : 1;
    this.fishingCtl.start(statsStore.get().fishing.level, performance.now(), power,
      pond ? WATER_PROFILES.pond : WATER_PROFILES.lake, pond ? WATER_FISH.pond : WATER_FISH.lake, hatBonus, speedMult);
    this.room?.send(MessageType.FishStart);
    this.setOwnFishState(3);
  }

  /** Set + sync the local player's rod stance (deduped — remotes render it via Player.fishState). */
  private setOwnFishState(state: number): void {
    this.player.setFishState(state);
    if (state === this.lastSentFishState) return;
    this.lastSentFishState = state;
    this.room?.send(MessageType.FishState, { state });
  }

  /** End the current fishing session (caught / escaped / cancelled) and clear the synced flag.
   *  Quitting while a fish is HOOKED counts as it slipping away — the bait is still lost. */
  private stopFishing(): void {
    const hooked = this.fishingCtl.isHooked();
    if (hooked) showToast("It got away… and took the bait.");
    this.fishingCtl.cancel();
    this.castPoint = null;
    this.lastSentFishState = 0; // the server clears fishState on FishStop
    this.room?.send(MessageType.FishStop, { escaped: hooked } satisfies FishStopMessage);
  }

  /** Use a portal: change zone, teleport to its spawn, swap fog/bounds/visibility. */
  private travel(portal: Portal): void {
    audio.play("door"); // building enter/exit
    // A portal that crosses a REGION boundary (e.g. the Kapon south gate → city) must take the async
    // path: the destination region was unloaded on the way out, so it has to rebuild behind the veil.
    if (regionOfZone(this.zone) !== regionOfZone(portal.to)) { void this.goToZone(portal.to, portal.spawn); return; }
    const leavingCasino = this.zone === "casino" && portal.to === "village";
    this.teleportTo(portal.to, portal.spawn, portal.floor ?? 0);
    if (leavingCasino) {
      // Step out of the casino facing the STREET (south), not back at the building.
      this.faceAngle = Math.PI;
      this.lastSent.dir = headingToDir(this.faceAngle);
      this.maybeSendMovement(SEND_INTERVAL_MS);
    }
  }

  /** Warp to a zone at a given spawn (server px): swap fog/bounds/visibility + push position.
   *  Used by portals, the Ghost Land scroll (→ city), and Winston (→ spawn island). */
  private teleportTo(zone: ZoneId, spawn: { x: number; y: number }, floor = 0): void {
    if (this.redConfinedFromZone(zone)) return; // airtight backstop: static door / return-gate / scroll warps too
    this.deselectNpc();
    // Stay clocked in while roaming the CITY; clock out only when leaving it (e.g. into the Kapon wild).
    if (this.isCityZone(this.zone) && !this.isCityZone(zone) && this.me?.working) {
      this.room?.send(MessageType.ClockToggle, { buildingId: this.zone });
      closeFridge(); closeCook(); closeDispenser(); closeSew();
    }
    this.cancelPlacement(); // leaving mid-placement abandons the ghost
    if (isSafeboxOpen()) closeSafebox();
    closeFurnitureMenu();
    this.zone = zone;
    this.floor = floor; // storey elevation (apartment homes) — synced with the next movement push
    this.refreshAppearances(); // cosmetics force off entering Kapon, restore on the way back to the city
    this.px.x = spawn.x; this.px.y = spawn.y;
    // Face INTO the room on arrival instead of keeping the heading you walked in with. Every interior
    // puts its exit doorway in the SOUTH wall with the room extending north, so face NORTH
    // (faceAngle Math.PI) toward the shop/table/dealer — not back out the door. The visible facing is
    // CAMERA-RELATIVE (the billboard picks its sprite row from faceAngle − camYaw), so also snap the
    // orbit camera behind the player at the door looking north — otherwise a camera the player left
    // angled elsewhere makes them read as facing the door (the casino/Ghost-Lab symptom).
    const INTERIOR_ZONES: ZoneId[] = [
      "tavern", "threads", "casino", "bank", "citytavern", "food", "ghosts", "furni",
      "aptlobby", "apthall2", "apthall3", "anxlobby", "anxhall2", "anxhall3",
    ];
    if (INTERIOR_ZONES.includes(zone)) {
      this.faceAngle = Math.PI;
      this.lastSent.dir = headingToDir(this.faceAngle);
      this.cam.yaw = 0; // camera at the door (south) looking into the room (north) — see the player's back walk in
    } else if (isAptUnitZone(zone)) {
      // The apartment homes are INVERTED interiors: the entry door is on the NORTH wall and the room
      // (and its street window) extends SOUTH — so face + point the camera south instead.
      this.faceAngle = 0;
      this.lastSent.dir = headingToDir(this.faceAngle);
      this.cam.yaw = Math.PI; // camera at the door (north) looking south into the room
    }
    if (!this.isCombatZone()) this.setCombat(false); // sheathe when entering a safe zone
    this.build.applyZone(this.scene, this.zone);
    dayNight.collectLights(this.scene); // (re)capture this zone's lamps/signs so they light sprites at night
    this.refreshFurniture(); // the destination's zone group may have just built — mirror furniture into it
    // Push the new position immediately so others see us cross the threshold.
    this.lastSent = { x: -1, y: -1, dir: this.lastSent.dir, moving: false, running: false, combat: false };
    this.maybeSendMovement(SEND_INTERVAL_MS);
  }

  /** On entering the world, resume at the player's PERSISTED position + zone (the server saves
   *  x/y; the zone is derived from them). Builds the city first if the saved spot is in it. */
  private spawnAtSavedLocation(): void {
    const sx = session.character?.x, sy = session.character?.y;
    if (typeof sx === "number" && typeof sy === "number" && Number.isFinite(sx) && Number.isFinite(sy)) {
      this.px.x = sx; this.px.y = sy;
    }
    const zone = this.build.zoneOf(this.px.x, this.px.y);
    this.build.ensure(zone); this.initCityNpcs(); // build only the spawned-into region (no-op for spawn island)
    // Logged out INSIDE a tenement (at home)? The storey isn't persisted, and at ground level the
    // footprint is solid — resume at that block's street door instead.
    for (const fp of this.build.aptFootprints ?? []) {
      if (zone === "village" && this.px.x >= fp.x0 && this.px.x <= fp.x1 && this.px.y >= fp.y0 && this.px.y <= fp.y1) {
        this.px.x = (fp.x0 + fp.x1) / 2;
        this.px.y = fp.y1 + 1.2 / U; // a step south of the street door (1/U = server px per world unit)
        break;
      }
    }
    this.zone = zone;
    this.build.applyZone(this.scene, this.zone);
    dayNight.collectLights(this.scene); // capture lamps for the spawned-into zone (e.g. relogin in the city)
    this.refreshFurniture(); // mirror placed furniture into the freshly-built spawn region
    this.player.setPosition(this.px.x * U, 0, this.px.y * U);
    this.lastSent = { x: Math.round(this.px.x), y: Math.round(this.px.y), dir: Direction.Down, moving: false, running: false, combat: false };
  }

  private blocked(x: number, y: number): boolean {
    const m = COLLISION_PAD; // inflate every blocker so the billboard can't clip into walls (see const)
    for (const b of this.build.zones[this.zone]!.blockers) {
      if (x >= b.x0 - m && x <= b.x1 + m && y >= b.y0 - m && y <= b.y1 + m) return true;
    }
    // Placed FURNITURE is solid too — the same circle footprints the placement overlap check
    // uses (furnitureRadius), for the building the player is inside (zone id == building id for
    // stores + apartment homes, so stacked apartment storeys never cross-block). The `stuck`
    // escape in updateLocal lets a player already overlapping a piece (woke up on a bed, or the
    // owner dropped one underfoot) walk back out, exactly like walls.
    const bld = this.room?.state.buildings.get(this.zone);
    if (bld) {
      for (const f of bld.furniture) {
        const r = furnitureRadius(f.itemId) / U + m * 0.5;
        const dx = f.x - x, dy = f.y - y;
        if (dx * dx + dy * dy < r * r) return true;
      }
    }
    return false;
  }

  /** The NPC manager for the zone the player is in (none on the open plaza). */
  private activeNpcs(): NpcManager | undefined { return this.npcByZone[this.zone]; }

  private maybeSendMovement(dtMs: number): void {
    if (!this.room) return;
    this.sendTimer += dtMs;
    if (this.sendTimer < SEND_INTERVAL_MS) return;
    this.sendTimer = 0;
    const x = Math.round(this.px.x);
    const y = Math.round(this.px.y);
    const moving = this.keys.size > 0 && !isTableOpen() && this.myPokerSeat < 0 && !this.me?.resting &&
      ["w", "a", "s", "d", "arrowup", "arrowdown", "arrowleft", "arrowright"].some((k) => this.keys.has(k));
    const running = moving && this.running; // the stamina-gated sprint flag (set in updateLocal)
    const dir = moving ? headingToDir(this.faceAngle) : this.lastSent.dir;
    const prev = this.lastSent;
    if (x === prev.x && y === prev.y && dir === prev.dir && moving === prev.moving &&
        running === prev.running && this.combat === prev.combat && this.floor === this.lastSentFloor) return;
    const msg: MoveMessage = { x, y, dir, moving, running, combat: this.combat, floor: this.floor };
    this.room.send(MessageType.Move, msg);
    this.lastSent = { x, y, dir, moving, running, combat: this.combat };
    this.lastSentFloor = this.floor;
  }

  /** The yaw a billboard at (x,z) should use so its sprite + plane face the viewer. In first-person
   *  poker the camera sits IN the scene, so each billboard must face the actual camera POSITION (a
   *  per-object yaw); everywhere else the shared 3rd-person orbit yaw is correct. */
  private viewYaw(x: number, z: number): number {
    if (this.myPokerSeat < 0) return this.cam.yaw;
    const c = this.camera.position;
    return Math.atan2(c.x - x, c.z - z);
  }

  private updateRemotes(dt: number): void {
    const seats = this.build.casinoSeats;
    const pseats = this.build.pokerSeats;
    const now = performance.now();
    for (const e of this.remotes.values()) {
      const seat = e.pokerSeatIndex >= 0 ? pseats?.[e.pokerSeatIndex] : (e.seatIndex >= 0 ? seats?.[e.seatIndex] : undefined);
      const p = e.bb.group.position;
      if (seat) {
        // Seated: snap onto the stool (poker sits lower than blackjack), facing the dealer. No lerp.
        p.set(seat.x, e.pokerSeatIndex >= 0 ? POKER_SEAT_SIT_Y : SEAT_SIT_Y, seat.z);
        if (e.pokerSeatIndex >= 0 && this.build.pokerNpc) {
          e.bb.setHeading(Math.atan2(this.build.pokerNpc.x - seat.x, this.build.pokerNpc.z - seat.z), false, false);
        } else {
          e.bb.setMotion(Direction.Up, false, false);
        }
        e.bb.setSitting(true);
      } else if (e.player.resting) {
        // Resting in a bed: snap onto the bed (the server pinned their position to its centre) at
        // mattress height — storey elevation + the bed's own lift, matching the local sleeper.
        const tx = e.serverX * U, tz = e.serverY * U;
        p.set(tx, aptFloorY(e.player.floor | 0) + this.bedZAt(e.serverX, e.serverY) + BED_SIT_Y, tz);
        e.bb.setMotion(e.dir, false, false);
        e.bb.setSitting(true);
      } else {
        const ridingR = e.player.bmxState === 2;
        e.bb.setBmx(ridingR);
        e.bb.setSitting(ridingR);
        const tx = e.serverX * U, tz = e.serverY * U;
        const dx = tx - p.x, dz = tz - p.z;
        const fy = aptFloorY(e.player.floor | 0); // tenement storey elevation (0 outside the homes)
        if (dx * dx + dz * dz > REMOTE_TELEPORT2) {
          // Big jump (teleport / left a building / changed zones) — SNAP to the destination and fade
          // in, instead of lerping (which streaks them across the map from a far-away spot).
          p.set(tx, kaponHeightAt(tx, tz) + fy, tz);
          e.bb.fadeIn();
        } else {
          p.x += dx * LERP;
          p.z += dz * LERP;
          p.y = kaponHeightAt(p.x, p.z) + fy; // Kapon terrain (0 elsewhere) + apartment storey
        }
        e.bb.setMotion(e.dir, e.moving, e.running);
      }
      // Tick with the REAL frame time (matches the local player) — a hardcoded 16ms made remote
      // animations run fast on >60Hz displays.
      // Fire the sword-swing sheet when this remote's attacking flag rises (everyone sees the swing).
      const atk = !!e.player.attacking;
      if (atk && !e.wasAttacking && !e.player.dead) { e.bb.playAttack(); audio.playAt("attack", p.x, p.z); }
      e.wasAttacking = atk;
      const blk = !!e.player.blocking;
      if (blk && !e.wasBlocking && !e.player.dead) audio.playAt("block", p.x, p.z);
      e.wasBlocking = blk;
      const stn = !!e.player.stunned;
      if (stn && !e.wasStunned && !e.player.dead) e.bb.stagger(); // got their attack blocked → blue stagger
      e.wasStunned = stn;
      e.bb.setBlocking(blk && !e.player.dead); // raised-shield block stance for this remote
      const smk = !!e.player.smoking && !seat; // remote's Blunt buff → stand-and-smoke idle
      if (smk && !e.wasSmoking && !e.player.dead) audio.playAt("smoke", p.x, p.z); // others hear them spark up
      e.wasSmoking = smk;
      e.bb.setSmoking(smk);
      // Jump hop: a bumped jumpSeq starts the airborne pose + a parabola added to the billboard Y.
      const saddle = !seat && e.player.bmxState === 2 ? BMX_SIT_Y : 0; // riders perch on the bike
      if (saddle > 0) {
        const prev = this.bmxRemotePrev.get(e.sessionId);
        if (prev && dt > 0) e.bb.setBmxSpeed(Math.hypot(p.x - prev.x, p.z - prev.z) / dt);
        this.bmxRemotePrev.set(e.sessionId, { x: p.x, z: p.z });
      } else {
        this.bmxRemotePrev.delete(e.sessionId);
      }
      this.syncParkedBmx(e.sessionId, e.player);
      if (!seat) {
        const jseq = e.player.jumpSeq | 0;
        if (jseq !== e.jumpSeq) { e.jumpSeq = jseq; e.jumpT = 0; e.jumpActive = true; e.bb.setJumping(true); }
        if (e.jumpActive) {
          e.jumpT += dt * 1000;
          const jp = Math.min(1, e.jumpT / JUMP_DURATION_MS);
          e.bb.setHop(saddle + 4 * JUMP_HEIGHT * jp * (1 - jp)); // saddle lift + hop — shadow stays planted
          if (jp >= 1) { e.jumpActive = false; e.bb.setHop(saddle); e.bb.setJumping(false); }
        } else {
          e.bb.setHop(saddle);
        }
      } else if (e.jumpActive) { e.jumpActive = false; e.bb.setHop(0); e.bb.setJumping(false); }
      e.bb.tick(dt * 1000, this.viewYaw(p.x, p.z));
      e.bb.setHealth(e.player.health, e.player.maxHealth);
      e.bb.setDead(e.player.dead);
      e.bb.setFishState(e.player.fishState); // rod stance (held/charging/cast/hooked)
      const red = e.player.affinity < 0; // red-named (negative affinity)
      e.bb.setSkeleton(red); // → skeleton marker for everyone
      if (red !== e.labelRed) { // affinity crossed 0 → redraw the name plate in the new colour
        this.scene.remove(e.label);
        (e.label.material as THREE.SpriteMaterial).map?.dispose();
        (e.label.material as THREE.SpriteMaterial).dispose();
        e.label = makeSelectLabel(e.player.name, "P", red ? RED_NAME_COLOR : undefined);
        this.scene.add(e.label);
        e.labelRed = red;
      }
      e.label.position.set(p.x, p.y + 1.5, p.z); // right above the head, matching NPC labels
      // Only show remotes in the SAME render world as us (this is what makes the interior shared:
      // players in the tavern see each other, players outside don't see those inside). The apartment
      // homes RENDER as part of the village — someone at their window is visible from the street,
      // and they see the street below (renderZone maps both sides to "village").
      const sameZone = (seat ? "casino" : this.build.zoneOf(e.serverX, e.serverY)) === this.renderZone();
      e.bb.group.visible = sameZone;
      // A player's NAME (+ [T] tag) only shows while they're SELECTED; drop the selection if they
      // walk into another zone.
      if (e === this.selectedRemote && !sameZone) this.deselectRemote();
      e.label.visible = sameZone && e === this.selectedRemote;
      // Mining: show a swinging pickaxe over a remote who's mid-channel (synced `mining` flag), so
      // everyone sees them chop — not just the miner. One MinerPickaxe per mining remote.
      this.updateRemoteMiningFx(e.sessionId, !!e.player.mining && sameZone && !seat, p, now);
      // Crafting: show a spinning gear over a remote who's mid-channel (synced `crafting` flag).
      this.updateRemoteCraftingFx(e.sessionId, !!e.player.crafting && sameZone && !seat, p, now);
    }
  }

  /** Create/position/remove a remote miner's swinging-pickaxe visual. */
  private updateRemoteMiningFx(sessionId: string, mining: boolean, pos: THREE.Vector3, now: number): void {
    let fx = this.miningFx.get(sessionId);
    if (mining) {
      if (!fx) { fx = new MinerPickaxe(); this.scene.add(fx.group); this.miningFx.set(sessionId, fx); }
      fx.group.position.set(pos.x, 0, pos.z);
      fx.swing(now);
    } else if (fx) {
      this.scene.remove(fx.group);
      fx.dispose();
      this.miningFx.delete(sessionId);
    }
  }

  /** Create/position/remove a remote crafter's spinning-gear visual. */
  private updateRemoteCraftingFx(sessionId: string, crafting: boolean, pos: THREE.Vector3, now: number): void {
    let fx = this.craftingFx.get(sessionId);
    if (crafting) {
      if (!fx) { fx = new CraftGear(); this.scene.add(fx.group); this.craftingFx.set(sessionId, fx); }
      fx.group.position.set(pos.x, 0, pos.z);
      fx.spin(now);
    } else if (fx) {
      this.scene.remove(fx.group);
      fx.dispose();
      this.craftingFx.delete(sessionId);
    }
  }

  private updateGroundItems(): void {
    // Clear the NPC target if it drifts out of range (or we left its zone).
    if (this.selectedNpc) {
      const dx = this.selectedNpc.x - this.px.x * U, dz = this.selectedNpc.z - this.px.y * U;
      if (!this.activeNpcs() || dx * dx + dz * dz > TARGET_CLEAR_RANGE * TARGET_CLEAR_RANGE) this.deselectNpc();
    }
    // Drop a selected player once they wander out of range.
    if (this.selectedRemote) {
      const e = this.selectedRemote, dx = e.bb.group.position.x - this.px.x * U, dz = e.bb.group.position.z - this.px.y * U;
      if (dx * dx + dz * dz > TARGET_CLEAR_RANGE * TARGET_CLEAR_RANGE) this.deselectRemote();
    }
    // Drop a selected city NPC once it (or we) wanders out of range, or we leave its zone.
    if (this.selectedCityNpc) {
      const p = this.cityNpcs?.posOf(this.selectedCityNpc);
      const dx = (p?.x ?? 1e9) - this.px.x * U, dz = (p?.z ?? 1e9) - this.px.y * U;
      if (!p || dx * dx + dz * dz > TARGET_CLEAR_RANGE * TARGET_CLEAR_RANGE) this.deselectCityNpc();
    }
    // Drop a selected cat once it (or we) wanders out of range.
    if (this.selectedCat) {
      const p = this.cats?.posOf(this.selectedCat);
      const dx = (p?.x ?? 1e9) - this.px.x * U, dz = (p?.z ?? 1e9) - this.px.y * U;
      if (!p || dx * dx + dz * dz > TARGET_CLEAR_RANGE * TARGET_CLEAR_RANGE) this.deselectCat();
    }

    // Active portal: a portal in this zone whose trigger is within range — wins over pickup.
    this.activePortal = null;
    for (const p of this.build.portals) {
      if (p.zone !== this.zone) continue;
      const dx = p.trigger.x - this.px.x, dy = p.trigger.y - this.px.y;
      if (dx * dx + dy * dy <= DOOR_RANGE * DOOR_RANGE) { this.activePortal = p; break; }
    }

    // Ground items live in the zone they were dropped in — show + allow pickup there (not just
    // the plaza), so a dropped item is visible and reclaimable wherever it landed.
    const max2 = (PICKUP_RANGE * PICKUP_RANGE);
    let nearest: string | null = null, best = Infinity;
    for (const [key, e] of this.groundItems) {
      if (this.build.zoneOf(e.x, e.y) !== this.renderZone()) continue;
      const dx = e.x - this.px.x, dy = e.y - this.px.y;
      const d2 = dx * dx + dy * dy;
      if (d2 <= max2 && d2 < best) { nearest = key; best = d2; }
    }
    this.highlightKey = nearest;
    const tSec = this.clock.elapsedTime;
    for (const [key, e] of this.groundItems) {
      e.sprite.visible = this.build.zoneOf(e.x, e.y) === this.renderZone();
      const hi = key === nearest;
      e.sprite.position.y = kaponHeightAt(e.x * U, e.y * U) + 0.5 + (hi ? 0.12 + Math.sin(tSec * 6) * 0.06 : 0);
      const s = hi ? 0.7 : 0.55;
      e.sprite.scale.set(s, s, 1);
    }

    if (this.fishingCtl.isFishing()) {
      this.prompt.style.display = "none"; // the fishing overlay shows its own hint
    } else if (isNpcDialogOpen()) {
      this.prompt.style.display = "none"; // the dialogue panel is showing instead
    } else if (this.selectedPortal && this.selectedPortal.zone === this.zone) {
      this.prompt.textContent = "Press T to enter the portal";
      this.prompt.style.display = "block";
    } else if (this.activePortal) {
      // Portal prompt shows even with an NPC selected (the NPC cue is the floating [T] label).
      this.prompt.textContent = this.portalPromptText(this.activePortal);
      this.prompt.style.display = "block";
    } else if (this.nearAptElevator()) {
      this.prompt.textContent = "Press E to call the elevator";
      this.prompt.style.display = "block";
    } else if (this.aptWindowHere()) {
      this.prompt.textContent = this.buildings.get(this.zone)?.curtains ? "Press E to open the curtains" : "Press E to draw the curtains";
      this.prompt.style.display = "block";
    } else if (nearest) {
      this.prompt.textContent = "Press E to pick up";
      this.prompt.style.display = "block";
    } else if (this.chests?.nearest(this.px.x, this.px.y, CHEST_RANGE)) {
      this.prompt.textContent = "Press E to open the chest";
      this.prompt.style.display = "block";
    } else if (this.zone === "kapon" && !isPlantPanelOpen() && this.plants?.nearest(this.px.x, this.px.y, WEED_INTERACT_RANGE)) {
      this.prompt.textContent = "Press E to inspect the weed plant";
      this.prompt.style.display = "block";
    } else if (this.zone === "kapon" && !this.skin.isSkinning() && this.hogs?.nearestCorpse(this.px.x, this.px.y, SKIN_RANGE)) {
      this.prompt.textContent = "Press E to skin";
      this.prompt.style.display = "block";
    } else if (this.nearTable() && this.mySeat === -1) {
      this.prompt.textContent = "Press E to sit at the blackjack table";
      this.prompt.style.display = "block";
    } else if (this.nearPokerTable() && this.myPokerSeat === -1) {
      this.prompt.textContent = "Press E to sit at the poker table";
      this.prompt.style.display = "block";
    } else if (this.nearAtm()) {
      this.prompt.textContent = "Press E for ATM";
      this.prompt.style.display = "block";
    } else if (this.nearRankboard()) {
      this.prompt.textContent = "Press E for the wealth rankings";
      this.prompt.style.display = "block";
    } else if (this.nearStorage()) {
      this.prompt.textContent = "Press E for bank storage";
      this.prompt.style.display = "block";
    } else if (this.nearLeatherMachine() && !this.craft.isBusy()) {
      this.prompt.textContent = "Press E to use the Leather Machine";
      this.prompt.style.display = "block";
    } else if (this.nearCraftingTable() && !this.craft.isBusy()) {
      this.prompt.textContent = "Press E to use the Crafting Table";
      this.prompt.style.display = "block";
    } else if (this.nearCookingPot() && !this.craft.isBusy()) {
      this.prompt.textContent = this.me?.working ? "Press E to cook at the pot" : "Clock in to use the cooking pot";
      this.prompt.style.display = "block";
    } else if (this.nearFoodFridge()) {
      this.prompt.textContent = this.me?.working ? "Press E to open the fridge" : "Clock in to open the fridge";
      this.prompt.style.display = "block";
    } else if (this.nearDrinkDispenser()) {
      this.prompt.textContent = this.me?.working ? "Press E for the drink dispenser" : "Clock in to use the dispenser";
      this.prompt.style.display = "block";
    } else if (this.nearStoreChest()) {
      this.prompt.textContent = this.me?.working ? "Press E to open the store chest" : "Clock in to open the store chest";
      this.prompt.style.display = "block";
    } else if (this.nearSewingMachine() && !this.craft.isBusy()) {
      this.prompt.textContent = this.me?.working ? "Press E to use the sewing machine" : "Clock in to use the sewing machine";
      this.prompt.style.display = "block";
    } else if ((this.zone === "village" || this.zone === "kapon") && !this.mine.isMining() && this.mine.nearest(this.px.x, this.px.y)) {
      this.prompt.textContent = this.hasPickaxe() ? "Press E to mine" : "Talk to the miner for a pickaxe";
      this.prompt.style.display = "block";
    } else if (this.zone === "village" && this.grass.nearest(this.px.x, this.px.y)) {
      this.prompt.textContent = this.hasGrassCutter() ? "Press E to cut grass" : "Talk to the gardener for a grass-cutter";
      this.prompt.style.display = "block";
    } else if (this.nearOwnBmx() && !this.ridingBmx()) {
      this.prompt.textContent = "Press E to ride your BMX";
      this.prompt.style.display = "block";
    } else if (this.nearFishBoard()) {
      this.prompt.textContent = "Press E to read the fishing records";
      this.prompt.style.display = "block";
    } else if (this.nearWater() && !this.fishingCtl.isFishing() && !this.castCharge && this.hasFishingRod()) {
      // Only players with the rod EQUIPPED get the fishing prompt (and it needs bait loaded).
      this.prompt.textContent = (this.me?.rodBait ?? 0) > 0
        ? "Hold E to cast — release to throw"
        : "Your rod needs Worm Bait";
      this.prompt.style.display = "block";
    } else {
      this.prompt.style.display = "none";
    }
    setBottomSlot("prompt", this.prompt.style.display !== "none"); // reflow the bottom stack
  }

  /** The prompt for a portal — building entrances reflect for-sale / lock / ownership state. */
  private portalPromptText(p: Portal): string {
    const bid = isBuildingId(p.to) ? p.to : null;
    if (bid) {
      const b = this.buildings.get(bid);
      const owner = this.iOwnBuilding(bid);
      // On auction → anyone but the owner can bid at the door.
      if (b && b.auctionEndsAt > 0) {
        const status = b.auctionBid > 0 ? `top bid $${b.auctionBid.toLocaleString()}` : `opens at $${b.auctionStartPrice.toLocaleString()}`;
        const left = Math.max(0, b.auctionEndsAt - Date.now());
        const h = Math.floor(left / 3600_000), m = Math.floor((left % 3600_000) / 60_000);
        return `${buildingName(bid)} — ON AUCTION ${status} · ${h}h ${m}m left${owner ? "" : " · Press B to bid"}`;
      }
      // Listed for sale by another owner → a buyer can take it on the spot.
      if (b && b.forSale && !owner && b.ownerId !== "") {
        return `${buildingName(bid)} — FOR SALE $${b.salePrice.toLocaleString()} · Press B to buy`;
      }
      const listed = b?.forSale ? ` · LISTED $${b.salePrice.toLocaleString()}` : "";
      if (b && b.locked) {
        if (owner) return `Press L to unlock · M manage${listed}`;
        if (this.iMayLock(bid)) return `Press E to ${p.label} · L to unlock`; // house keyholder
        return this.iMayEnterLocked(bid) ? `Press E to ${p.label}` : "Locked"; // employees get in
      }
      if (owner) return `Press E to ${p.label} · L lock · M manage${listed}`;
      if (this.iMayLock(bid)) return `Press E to ${p.label} · L lock`; // house keyholder
    }
    return `Press E to ${p.label}`;
  }

  /** A building's locked state (false if unknown). */
  private buildingLocked(id: string): boolean { return this.buildings.get(id)?.locked ?? false; }
  /** Do I own this building? */
  private iOwnBuilding(id: string): boolean {
    const b = this.buildings.get(id);
    return !!b && this.myCharacterId !== "" && b.ownerId === this.myCharacterId;
  }
  /** May I pass a locked door here? (owner, employee, or a house keyholder; unknown building ⇒ yes) */
  private iMayEnterLocked(id: string): boolean {
    const b = this.buildings.get(id);
    if (!b || this.myCharacterId === "") return !b; // unknown building ⇒ allow
    return b.ownerId === this.myCharacterId
      || b.employees.some((e) => e.id === this.myCharacterId)
      || b.keyholders.some((k) => k.id === this.myCharacterId);
  }
  /** May I lock/unlock this door? (owner anywhere; on a HOUSE, anyone holding a door key too) */
  private iMayLock(id: string): boolean {
    if (this.iOwnBuilding(id)) return true;
    const b = this.buildings.get(id);
    return !!b && isHouseBuilding(id) && this.myCharacterId !== "" && b.keyholders.some((k) => k.id === this.myCharacterId);
  }
  /** The building the L/M keys act on: the entrance you're standing at, or — inside a building whose
   *  zone IS its building id (stores, the apartment homes) — the building you're inside. */
  private buildingHere(): string | null {
    const ap = this.activePortal;
    if (ap && isBuildingId(ap.to)) return ap.to;
    return isBuildingId(this.zone) ? this.zone : null;
  }

  /** The zone whose WORLD this player currently renders in. The apartment homes are physically part
   *  of the village (open windows — you see the street, the street sees you), so while `this.zone`
   *  is apt2a… (for bounds/locks/keys), everything visibility-related compares against "village". */
  private renderZone(): ZoneId {
    return isAptUnitZone(this.zone) ? "village" : this.zone;
  }

  /** The local player's render elevation (world units) — the slab top of their tenement storey. */
  private floorY(): number { return aptFloorY(this.floor); }

  // ── Placed furniture (bed / safebox) ────────────────────────────────────────

  /** A building's floor elevation for furniture (apartment homes sit at their storey's slab). */
  private static furnitureElev(bid: string): number {
    return isHouseBuilding(bid) ? aptFloorY(Number(bid.charAt(3)) || 0) : 0;
  }

  /** May I place/move/pick-up furniture here? Same rule as the door lock: the owner, or a house
   *  keyholder in the home they hold a key to. */
  private iMayFurnish(id: string): boolean { return this.iMayLock(id); }

  /** May I open a safebox here? Owner + employees + house keyholders. */
  private iMayAccessSafebox(id: string): boolean {
    const b = this.buildings.get(id);
    if (!b || this.myCharacterId === "") return false;
    return b.ownerId === this.myCharacterId
      || b.employees.some((e) => e.id === this.myCharacterId)
      || b.keyholders.some((k) => k.id === this.myCharacterId);
  }

  /** Re-mirror every building's synced furniture into the scene. Buildings whose zone group isn't
   *  built yet (region unloaded) are skipped and picked up by the next refresh after travel. */
  private refreshFurniture(): void {
    const st = this.room?.state;
    if (!st) return;
    const placements: { bid: string; fid: string; itemId: string; x: number; y: number; z: number; rot: number }[] = [];
    st.buildings.forEach((b, bid) => {
      for (const f of b.furniture) placements.push({ bid, fid: f.fid, itemId: f.itemId, x: f.x, y: f.y, z: f.z, rot: f.rot });
    });
    this.furnitureLayer.sync(
      placements,
      (bid) => this.build.zones[bid as ZoneId]?.group ?? null,
      (bid) => World3D.furnitureElev(bid),
    );
  }

  /** The LIFT (world units) of the sleep-furniture piece under a resting player at (x, y) server px —
   *  a raised bed carries its sleeper with it. 0 when no bed is found (e.g. it was just picked up). */
  private bedZAt(x: number, y: number): number {
    const st = this.room?.state;
    if (!st) return 0;
    const R = 64; // server px — the player is snapped to the bed's centre, so a small radius suffices
    let z = 0, bestD = R * R;
    st.buildings.forEach((b) => {
      for (const f of b.furniture) {
        if (FURNITURE_FUNCTIONS[f.itemId]?.action !== "sleep") continue;
        const dx = f.x - x, dy = f.y - y;
        const d = dx * dx + dy * dy;
        if (d <= bestD) { bestD = d; z = f.z; }
      }
    });
    return z;
  }

  /** The closest placed furniture within reach in the building I'm standing in, or null. */
  private nearbyFurniture(): { bid: string; fid: string; itemId: string } | null {
    const bid = isBuildingId(this.zone) ? this.zone : null;
    if (!bid) return null;
    const b = this.room?.state.buildings.get(bid);
    if (!b) return null;
    const R = 2.4; // world units
    let best: { bid: string; fid: string; itemId: string } | null = null;
    let bestD = R * R;
    for (const f of b.furniture) {
      const dx = (f.x - this.px.x) * U, dy = (f.y - this.px.y) * U;
      const d = dx * dx + dy * dy;
      if (d <= bestD) { bestD = d; best = { bid, fid: f.fid, itemId: f.itemId }; }
    }
    return best;
  }

  /** E near furniture / the OKKA register. Safebox → open its storage; my furniture → the
   *  Move / Pick up menu; the store register (owner) → collect the takings. */
  private tryFurnitureInteract(): boolean {
    const reg = this.build.furniRegister;
    if (reg && this.zone === "furni" && this.iOwnBuilding(FURNI_BUILDING_ID)) {
      const dx = (reg.x - this.px.x) * U, dy = (reg.y - this.px.y) * U;
      if (dx * dx + dy * dy <= 2.0 * 2.0) {
        this.room?.send(MessageType.CollectProfit, { buildingId: FURNI_BUILDING_ID, amount: 0 }); // 0 ⇒ take all
        return true;
      }
    }
    const near = this.nearbyFurniture();
    if (!near) return false;
    if (near.itemId === SAFEBOX_ID && this.iMayAccessSafebox(near.bid)) { openSafebox(near.bid, near.fid); return true; }
    return this.openFurnitureMenu(near);
  }

  /** The options menu on a placed piece — a floating panel at the piece's screen centre: its
   *  FUNCTIONAL option first (Sleep for a bed; Open for a safebox you may access), then Move /
   *  Pick up for players entitled to edit it. No options ⇒ silently nothing (someone else's
   *  decor). Clicking anywhere outside the panel closes it. */
  private openFurnitureMenu(hit: { bid: string; fid: string; itemId: string }): boolean {
    const name = ITEM_NAMES[hit.itemId as keyof typeof ITEM_NAMES] ?? hit.itemId;
    const options: FurnitureMenuOption[] = [];
    const fn = FURNITURE_FUNCTIONS[hit.itemId];
    if (fn?.action === "sleep") {
      options.push({ label: fn.label, onPick: () => this.room?.send(MessageType.UseFurniture, { buildingId: hit.bid, fid: hit.fid }) });
    } else if (fn?.action === "storage" && this.iMayAccessSafebox(hit.bid)) {
      options.push({ label: fn.label, onPick: () => openSafebox(hit.bid, hit.fid) });
    } else if (fn?.action === "smelt" && this.iMayAccessSafebox(hit.bid)) {
      // The furnace IS the forge station, and it only serves the building's own people (owner /
      // staff / keyholders — the safebox rule). The server re-checks both on every Craft.
      options.push({ label: fn.label, onPick: () => openCraftStation("forge") });
    }
    if (this.iMayFurnish(hit.bid)) {
      options.push({ label: "Move", onPick: () => this.startMovingFurniture(hit) });
      options.push({ label: "Pick up", onPick: () => this.room?.send(MessageType.PickupFurniture, { buildingId: hit.bid, fid: hit.fid }) });
    }
    if (!options.length) return false;
    // Anchor the panel on the piece's mid-height centre, projected to screen space.
    const obj = this.furnitureLayer.objectOf(hit.bid, hit.fid);
    let sx = window.innerWidth / 2, sy = window.innerHeight / 2;
    if (obj) {
      const v = obj.position.clone();
      v.y += 0.5; // the prop's mid-height, not its floor point
      v.project(this.camera);
      sx = (v.x * 0.5 + 0.5) * window.innerWidth;
      sy = (-v.y * 0.5 + 0.5) * window.innerHeight;
    }
    showFurniMenu(sx, sy, name, options);
    return true;
  }

  /** Inventory "Place" on a furniture item — enter placement mode if I'm inside a property I may furnish. */
  private tryStartPlacing(slot: { itemId: string; slotIndex: number }): void {
    const bid = isBuildingId(this.zone) ? this.zone : null;
    if (!bid || !this.iMayFurnish(bid)) { showToast("Stand inside a property you own (or hold a key to) to place furniture."); return; }
    this.beginPlacement({ bid, itemId: slot.itemId, slotIndex: slot.slotIndex, x: Math.round(this.px.x), y: Math.round(this.px.y), z: 0, rot: 0 });
  }

  /** "Move" on a placed piece — re-enter placement mode from its current synced spot. */
  private startMovingFurniture(hit: { bid: string; fid: string; itemId: string }): void {
    const f = this.room?.state.buildings.get(hit.bid)?.furniture.find((p) => p.fid === hit.fid);
    if (!f) return;
    this.beginPlacement({ bid: hit.bid, itemId: hit.itemId, fid: hit.fid, x: f.x, y: f.y, z: f.z, rot: f.rot });
  }

  private beginPlacement(p: { bid: string; itemId: string; slotIndex?: number; fid?: string; x: number; y: number; z: number; rot: number }): void {
    this.cancelPlacement();
    const group = this.build.zones[p.bid as ZoneId]?.group;
    if (!group) return;
    const ghost = furnitureMesh(p.itemId);
    ghostify(ghost);
    group.add(ghost);
    this.placing = { ...p, mode: "move", ghost };
    this.updatePlacementGhost();
    showPlacementBar({
      setMode: (mode) => {
        const cur = this.placing;
        if (!cur) return;
        cur.mode = mode;
        setPlacementBarMode(mode);
      },
      confirm: () => this.confirmPlacement(),
      cancel: () => this.cancelPlacement(),
    });
  }

  /** Send the placement to the server (place from the bag, or move an existing piece) + exit the mode. */
  private confirmPlacement(): void {
    const p = this.placing;
    if (!p) return;
    if (this.placementOverlaps(p)) { showToast("It won't fit there — too close to another piece."); return; }
    const at = { buildingId: p.bid, x: p.x, y: p.y, z: p.z, rot: p.rot };
    if (p.fid) this.room?.send(MessageType.MoveFurniture, { ...at, fid: p.fid });
    else if (p.slotIndex !== undefined) this.room?.send(MessageType.PlaceFurniture, { ...at, slotIndex: p.slotIndex });
    this.cancelPlacement();
  }

  /** Would the ghost overlap another placed piece? Mirrors the server's 2D circle bound (per-item
   *  footprint radii, height-blind — furniture can't overlap OR stack). */
  private placementOverlaps(p: { bid: string; itemId: string; fid?: string; x: number; y: number }): boolean {
    const b = this.room?.state.buildings.get(p.bid);
    if (!b) return false;
    const r1 = furnitureRadius(p.itemId) / U; // world units → server px
    for (const f of b.furniture) {
      if (p.fid && f.fid === p.fid) continue; // moving a piece ignores its own old spot
      const r = r1 + furnitureRadius(f.itemId) / U;
      const dx = f.x - p.x, dy = f.y - p.y;
      if (dx * dx + dy * dy < r * r) return true;
    }
    return false;
  }

  private updatePlacementGhost(): void {
    const p = this.placing;
    if (!p) return;
    p.ghost.position.set(p.x * U, World3D.furnitureElev(p.bid) + p.z, p.y * U);
    p.ghost.rotation.y = p.rot;
    setGhostBlocked(p.ghost, this.placementOverlaps(p)); // red glow while it can't be placed
  }

  private cancelPlacement(): void {
    if (!this.placing) return;
    this.placing.ghost.removeFromParent();
    this.placing = null;
    hidePlacementBar();
  }

  /** Pointer drag while placing: MOVE mode slides the ghost along the room floor (the ray through
   *  the cursor is intersected with the horizontal plane at the ghost's current height, so it lands
   *  exactly under the cursor); ROTATE mode spins it with horizontal drag. */
  private dragPlacement(e: PointerEvent, dx: number): void {
    const p = this.placing;
    if (!p) return;
    if (p.mode === "rotate") {
      p.rot = (p.rot + dx * 0.012) % (Math.PI * 2);
      this.updatePlacementGhost();
      return;
    }
    const ndc = new THREE.Vector2((e.clientX / window.innerWidth) * 2 - 1, -(e.clientY / window.innerHeight) * 2 + 1);
    this.pickRay.setFromCamera(ndc, this.camera);
    const plane = new THREE.Plane(new THREE.Vector3(0, 1, 0), -(World3D.furnitureElev(p.bid) + p.z));
    const hit = new THREE.Vector3();
    if (!this.pickRay.ray.intersectPlane(plane, hit)) return; // looking above the horizon — no floor point
    const b = this.build.zones[p.bid as ZoneId]?.bounds;
    p.x = Math.round(hit.x / U); p.y = Math.round(hit.z / U);
    if (b) { p.x = Math.max(b.x0, Math.min(b.x1, p.x)); p.y = Math.max(b.y0, Math.min(b.y1, p.y)); }
    this.updatePlacementGhost();
  }

  /** Placement-mode keys: Esc cancels, E/Enter confirms (the toolbar buttons do the same). */
  private handlePlacementKey(k: string, _e: KeyboardEvent): boolean {
    if (!this.placing) return false;
    if (k === "e" || k === "enter") { this.confirmPlacement(); return true; }
    if (k === "escape" || k === "backspace") { this.cancelPlacement(); return true; }
    return false;
  }

  /** The apartment ceiling light's brightness while someone is home (subtle warm glow). */
  private static readonly APT_LIGHT_INTENSITY = 3.5;

  /** Keep each apartment home's ceiling light + window curtain in sync. Light: warm cone ON while
   *  anyone is home (me, or any remote on that storey inside the unit's bounds), dark when empty —
   *  it spills out of the open window, so the street reads who's home at night. Curtain: mirrors the
   *  synced Building.curtains (drawn closed = visible). */
  private updateAptLights(): void {
    const units = this.build.aptUnits;
    if (!units) return;
    for (const u of units) {
      let on = this.zone === u.zone;
      if (!on) {
        for (const e of this.remotes.values()) {
          if ((e.player.floor | 0) !== u.floor) continue;
          if (e.serverX < u.bounds.x0 || e.serverX > u.bounds.x1 || e.serverY < u.bounds.y0 || e.serverY > u.bounds.y1) continue;
          on = true; break;
        }
      }
      if (on !== u.light.visible) {
        u.light.visible = on;
        u.light.intensity = on ? World3D.APT_LIGHT_INTENSITY : 0;
      }
      u.curtain.visible = this.buildings.get(u.zone)?.curtains ?? false;
    }
  }

  /** The home whose window the player is standing at (for the E curtain toggle), or null. */
  private aptWindowHere(): { zone: ZoneId } | null {
    if (!isAptUnitZone(this.zone)) return null;
    const u = this.build.aptUnits?.find((x) => x.zone === this.zone);
    if (!u) return null;
    const dx = u.windowAnchor.x - this.px.x, dy = u.windowAnchor.y - this.px.y;
    return dx * dx + dy * dy <= 60 * 60 ? { zone: u.zone } : null;
  }

  /** The elevator whose lobby I'm standing in (each tenement block has its own), or null. */
  private aptElevatorHere(): { x: number; y: number; floors: { label: string; zone: ZoneId; spawn: { x: number; y: number } }[] } | null {
    return this.build.aptElevators?.find((ev) => ev.zone === this.zone) ?? null;
  }
  /** Standing at this lobby's elevator doors? (E → the floor picker) */
  private nearAptElevator(): boolean {
    const ev = this.aptElevatorHere();
    if (!ev) return false;
    const dx = ev.x - this.px.x, dy = ev.y - this.px.y;
    return dx * dx + dy * dy <= 55 * 55;
  }
  /** Open the elevator floor picker; picking a floor rides up (a zone teleport, like a door). */
  private openAptElevator(): void {
    const ev = this.aptElevatorHere();
    if (!ev) return;
    openElevator(ev.floors, (i) => {
      const f = ev.floors[i];
      if (!f) return;
      audio.play("door");
      this.teleportTo(f.zone, f.spawn);
    });
  }
  /** Can I buy this building? (someone else's, listed for sale, not on auction) */
  private iCanBuy(id: string): boolean {
    const b = this.buildings.get(id);
    return !!b && b.forSale && b.auctionEndsAt <= 0 && b.ownerId !== "" && b.ownerId !== this.myCharacterId;
  }

  /** Confirm before buying a listed building — shows the price (paid from carried cash) + the seller. */
  private confirmBuyBuilding(bid: string): void {
    const b = this.buildings.get(bid);
    if (!b || !this.iCanBuy(bid)) return;
    const price = b.salePrice;
    showConfirm({
      title: `Buy ${buildingName(bid)}?`,
      body: `${b.ownerName || "The owner"} is asking <b>$${price.toLocaleString()}</b>, paid from your carried cash. The property is yours the moment you confirm.`,
      confirmLabel: `Buy — $${price.toLocaleString()}`,
      cancelLabel: "Not now",
      danger: false,
      onConfirm: () => {
        // Re-check against the LIVE listing — the price may have changed / been unlisted while the
        // dialog sat open; never send a purchase the player didn't see.
        const cur = this.buildings.get(bid);
        if (!cur || !this.iCanBuy(bid) || cur.salePrice !== price) {
          showToast("The listing changed — check the price and try again.");
          return;
        }
        this.room?.send(MessageType.BuyBuilding, { buildingId: bid });
      },
    });
  }

  /** Can I bid on this building? (live auction on someone else's building) */
  private iCanBid(id: string): boolean {
    const b = this.buildings.get(id);
    return !!b && b.auctionEndsAt > Date.now() && b.ownerId !== this.myCharacterId;
  }

  /** Build the bid-panel view for an on-auction building. */
  private bidView(bid: string): BidView {
    const b = this.buildings.get(bid);
    return {
      buildingId: bid, buildingName: buildingName(bid),
      endsAt: b?.auctionEndsAt ?? 0, startPrice: b?.auctionStartPrice ?? 0,
      minIncrement: b?.auctionMinIncrement ?? 0, bid: b?.auctionBid ?? 0,
      bidderName: b?.auctionBidderName ?? "",
      iAmTopBidder: !!b && this.myCharacterId !== "" && b.auctionBidderId === this.myCharacterId,
    };
  }

  /** Open the bid panel at the door — the amount is held from the BANK until outbid. Mirrors the
   *  server gates up front (one active bid at a time; house limit) so the player sees WHY instead
   *  of a panel that only rejects. */
  private promptBid(bid: string): void {
    const b = this.buildings.get(bid);
    if (!b) return;
    // Already the standing top bidder somewhere else → that money is committed.
    const other = [...this.buildings.entries()]
      .find(([oid, ob]) => oid !== bid && ob.auctionEndsAt > Date.now() && ob.auctionBidderId === this.myCharacterId);
    if (other) { showToast(`You already have an active bid on ${buildingName(other[0])} — one auction at a time.`); return; }
    // At the house limit → a won house could never be handed over (admins use their raised limit).
    if (isHouseBuilding(bid) && !this.me?.admin) {
      const homes = [...this.buildings.entries()].filter(([oid, ob]) => ob.ownerId === this.myCharacterId && isHouseBuilding(oid)).length;
      if (homes >= DEFAULT_HOUSE_LIMIT) { showToast(`You already own a home (limit ${DEFAULT_HOUSE_LIMIT}) — you can't bid on another.`); return; }
    }
    openAuctionBid(this.bidView(bid), {
      bid: (amount) => this.room?.send(MessageType.BidAuction, { buildingId: bid, amount }),
      close: () => { /* nothing extra to tear down */ },
    });
  }

  /** Build the manage-panel view for a building: staff + applicants (businesses) or door keyholders
   *  (houses), plus its for-sale listing. */
  private manageView(bid: string): ManageView {
    const b = this.buildings.get(bid);
    const onAuction = (b?.auctionEndsAt ?? 0) > 0;
    return {
      buildingId: bid, buildingName: buildingName(bid), cap: MAX_EMPLOYEES_PER_BUILDING,
      isHouse: isHouseBuilding(bid),
      employees: b ? b.employees : [], applicants: b ? b.applicants : [],
      keyholders: b ? b.keyholders : [],
      forSale: b?.forSale ?? false, salePrice: b?.salePrice ?? 0,
      auction: b && onAuction
        ? { endsAt: b.auctionEndsAt, startPrice: b.auctionStartPrice, minIncrement: b.auctionMinIncrement, bid: b.auctionBid, bidderName: b.auctionBidderName }
        : null,
      canAuction: !!this.me?.admin && !onAuction && (b?.ownerId ?? "") !== "",
      canCancelAuction: !!this.me?.admin && onAuction,
    };
  }

  /** Open the manage panel for a building the local player owns (review applications, fire staff,
   *  hand out / take back house keys, list for sale). */
  private openManageFor(bid: string): void {
    openBuildingManage(this.manageView(bid), {
      approve: (cid) => this.room?.send(MessageType.ReviewApplication, { buildingId: bid, characterId: cid, accept: true }),
      reject: (cid) => this.room?.send(MessageType.ReviewApplication, { buildingId: bid, characterId: cid, accept: false }),
      fire: (cid) => this.room?.send(MessageType.Fire, { buildingId: bid, characterId: cid }),
      giveKey: (name) => this.room?.send(MessageType.GiveKey, { buildingId: bid, characterName: name }),
      revokeKey: (cid) => this.room?.send(MessageType.RevokeKey, { buildingId: bid, characterId: cid }),
      list: (price) => this.room?.send(MessageType.ListBuilding, { buildingId: bid, price }),
      unlist: () => this.room?.send(MessageType.ListBuilding, { buildingId: bid, price: 0 }),
      startAuction: (durationHours, startPrice, minIncrement) =>
        this.room?.send(MessageType.StartAuction, { buildingId: bid, durationHours, startPrice, minIncrement }),
      cancelAuction: () => this.room?.send(MessageType.CancelAuction, { buildingId: bid }),
      close: () => { /* nothing extra to tear down */ },
    });
  }

  /** Keep the open staff panel current when that building's synced state changes. */
  private refreshManageIfOpen(id: string): void {
    if (isBuildingManageOpen() && buildingManageId() === id) refreshBuildingManage(this.manageView(id));
  }

  private updateCamera(): void {
    // Cinematic shot (Sal's city tour): the camera holds the shot while the caption panel is up.
    // If the dialog was force-closed some other way (T etc.), the tour dissolves back to gameplay.
    if (this.cinema) {
      if (!isNpcDialogOpen()) { this.cinema = null; setNpcDialogCinematic(false); }
      else {
        this.camera.position.copy(this.cinema.pos);
        this.camera.lookAt(this.cinema.look);
        return;
      }
    }
    // First-person while fishing: stand at the shore, eyes looking out over the lake (slightly down at
    // the bobber). The 2D pole overlay is drawn on top by FishingController.
    if (this.fishingCtl.isFishing()) {
      const px = this.px.x * U, pz = this.px.y * U;
      const gy = kaponHeightAt(px, pz); // 0 on the flat village ground
      const w = this.waterBody() ?? KAPON_TERRAIN.lake; // Kapon lake or the city pond
      const look = this.castPoint ?? { x: w.x, z: w.z }; // eyes on where the LINE actually landed
      const eye = new THREE.Vector3(px, gy + 1.55, pz);
      this.camera.position.copy(eye);
      this.camera.lookAt(new THREE.Vector3(look.x, gy + 0.2, look.z)); // out + down toward the water
      return;
    }
    // First-person while seated at the poker table: eyes at the stool, looking across the felt
    // at the dealer. The own billboard is hidden (updateLocal) so it isn't in shot; the own
    // hole cards fly to just in front, in frame.
    if (this.myPokerSeat >= 0 && this.build.pokerSeats?.[this.myPokerSeat]) {
      const seat = this.build.pokerSeats[this.myPokerSeat];
      const tableC = new THREE.Vector3(POKER_TABLE_ANCHOR.x / 32, 0.66, POKER_TABLE_ANCHOR.y / 32);
      // Pull the eye BACK behind the seat (away from the felt) so the cards dealt in front are in frame.
      const back = new THREE.Vector3(seat.x - tableC.x, 0, seat.z - tableC.z).normalize();
      const eye = new THREE.Vector3(seat.x, POKER_SEAT_SIT_Y + 1.12, seat.z).addScaledVector(back, 0.5);
      this.camera.position.copy(eye);
      if (this.camShake > 0.002) {
        this.camera.position.x += (Math.random() - 0.5) * this.camShake;
        this.camera.position.y += (Math.random() - 0.5) * this.camShake;
        this.camShake *= 0.82;
      }
      // Base look points across the felt; left-click drag adds a yaw/pitch offset (turn the head).
      const fwd = tableC.clone().sub(eye);
      const baseYaw = Math.atan2(fwd.x, fwd.z);
      const basePitch = Math.atan2(fwd.y, Math.hypot(fwd.x, fwd.z));
      const yaw = baseYaw + this.fpYaw;
      const pitch = Math.max(-1.2, Math.min(0.6, basePitch + this.fpPitch));
      const cp = Math.cos(pitch);
      const target = eye.clone().add(new THREE.Vector3(Math.sin(yaw) * cp, Math.sin(pitch), Math.cos(yaw) * cp));
      this.camera.lookAt(target);
      return;
    }
    const px = this.px.x * U, pz = this.px.y * U;
    // Terrain lift (0 outside Kapon) + the tenement storey the player stands on — the camera must
    // ride at the player's REAL elevation or an upper-floor home shows the building's innards.
    const gy = kaponHeightAt(px, pz) + this.floorY();
    const target = new THREE.Vector3(px, gy + 1.0, pz);
    const desired = new THREE.Vector3(
      px + Math.sin(this.cam.yaw) * this.cam.dist * Math.cos(this.cam.pitch),
      gy + this.cam.dist * Math.sin(this.cam.pitch) + 0.9,
      pz + Math.cos(this.cam.yaw) * this.cam.dist * Math.cos(this.cam.pitch),
    );
    // WoW-style camera collision: if a building wall sits between the player and the
    // desired camera spot, pull the camera in along the ray so the view isn't blocked.
    const toCam = desired.clone().sub(target);
    const dist = toCam.length();
    const dir = toCam.multiplyScalar(1 / dist);
    this.camRay.set(target, dir);
    this.camRay.far = dist;
    const colliders = this.build.zones[this.zone]!.colliders;
    const hit = this.camRay.intersectObjects(colliders, false)[0];
    const place = hit ? Math.max(1.4, hit.distance - 0.35) : dist;
    this.camera.position.copy(target).addScaledVector(dir, place);
    // Inside an apartment home the camera must STAY in the room — the collision ray can slip out
    // through the open window (a hole has nothing to hit), so hard-clamp to the interior box.
    if (isAptUnitZone(this.zone)) {
      const b = this.build.zones[this.zone]!.bounds;
      const m = 0.15; // small margin off the walls
      this.camera.position.x = Math.max(b.x0 * U + m, Math.min(b.x1 * U - m, this.camera.position.x));
      this.camera.position.z = Math.max(b.y0 * U + m, Math.min(b.y1 * U - m, this.camera.position.z));
      this.camera.position.y = Math.max(this.floorY() + 0.35, Math.min(this.floorY() + 2.45, this.camera.position.y));
    }
    // Got-hit camera shake: a quick decaying jitter of the eye (still aimed at the player).
    if (this.camShake > 0.002) {
      this.camera.position.x += (Math.random() - 0.5) * this.camShake;
      this.camera.position.y += (Math.random() - 0.5) * this.camShake;
      this.camera.position.z += (Math.random() - 0.5) * this.camShake;
      this.camShake *= 0.82;
    }
    this.camera.lookAt(target);
  }

  // --- networking (mirrors WorldScene.connectMultiplayer) ---------------------

  private async connectMultiplayer(): Promise<void> {
    // Join with retries: a cold/just-woken server (or a momentary "seat reservation expired") makes the
    // first joinOrCreate fail. Without a retry the client silently dropped into a dead, empty world.
    // Each attempt re-runs matchmaking (fresh reservation), with a short backoff between tries.
    const MAX_TRIES = 4;
    let room: GameRoom | undefined;
    for (let attempt = 1; attempt <= MAX_TRIES && !this.disposed; attempt++) {
      try {
        // The in-memory access token is short-lived (15m) and may have expired during a long session;
        // re-mint it from the httpOnly refresh cookie before each join so a stale "jwt expired" token
        // doesn't strand us on the loading screen.
        await session.bootstrap().catch(() => {});
        room = await joinGameRoom(session.character?.characterId);
        break;
      } catch (err) {
        console.error(`[net] join attempt ${attempt}/${MAX_TRIES} failed`, err);
        if (this.disposed || attempt === MAX_TRIES) break;
        showStatus(`Connecting to the server… (retry ${attempt}/${MAX_TRIES - 1})`, { spinner: true });
        await new Promise((r) => setTimeout(r, 800 * attempt)); // linear backoff: 0.8s, 1.6s, 2.4s
      }
    }
    if (this.disposed) { room?.leave(); return; }
    if (!room) {
      // Every attempt failed — surface an actionable error instead of a silent empty world. The server
      // may be waking up (free-tier spin-down), so a reload usually succeeds.
      showStatus("Couldn't reach the server — it may be waking up.", { actionLabel: "Retry", onAction: () => location.reload() });
      return;
    }
    hideStatus(); // connected — clear any retry status
    this.room = room;
    const $ = getStateCallbacks(room);
    this.callbacks = $;

    // Shared building ownership/lock/staff — mirror it so doors/prompts/entry + the manage panel react.
    const syncBuilding = (b: Building, id: string): void => {
      this.buildings.set(id, {
        ownerId: b.ownerId, ownerName: b.ownerName, locked: b.locked,
        employees: [...b.employees].map((e) => ({ id: e.id, name: e.name })),
        applicants: [...b.applicants].map((a) => ({ id: a.id, name: a.name })),
        keyholders: [...b.keyholders].map((k) => ({ id: k.id, name: k.name })),
        forSale: b.forSale, salePrice: b.salePrice, curtains: b.curtains,
        auctionEndsAt: b.auctionEndsAt, auctionStartPrice: b.auctionStartPrice, auctionMinIncrement: b.auctionMinIncrement,
        auctionBid: b.auctionBid, auctionBidderId: b.auctionBidderId, auctionBidderName: b.auctionBidderName,
      });
      this.refreshManageIfOpen(id); // keep the owner's manage panel live as staff/applications change
      // If this building changed hands and it's no longer mine, close the manage panel I had open for it
      // (admins keep it open — they may manage/auction buildings they don't own).
      if (isBuildingManageOpen() && buildingManageId() === id && !this.iOwnBuilding(id) && !this.me?.admin) closeBuildingManage();
      // Keep an open bid panel live; close it when the auction ends (settled/cancelled) so no one bids into the void.
      if (isAuctionBidOpen() && auctionBidId() === id) {
        if ((this.buildings.get(id)?.auctionEndsAt ?? 0) > 0) refreshAuctionBid(this.bidView(id));
        else closeAuctionBid();
      }
    };
    $(room.state).buildings.onAdd((b: Building, id: string) => {
      syncBuilding(b, id);
      $(b).onChange(() => syncBuilding(b, id));
      $(b).employees.onAdd(() => syncBuilding(b, id));     // hiring pushes to this nested list
      $(b).employees.onRemove(() => syncBuilding(b, id));  // firing splices it
      $(b).applicants.onAdd(() => syncBuilding(b, id));    // a new application
      $(b).applicants.onRemove(() => syncBuilding(b, id)); // approved/rejected
      $(b).keyholders.onAdd(() => syncBuilding(b, id));    // the owner handed out a door key
      $(b).keyholders.onRemove(() => syncBuilding(b, id)); // a key was taken back
      // Placed furniture: the server rebuilds the list on every change, so add/remove covers moves too.
      $(b).furniture.onAdd(() => this.refreshFurniture());
      $(b).furniture.onRemove(() => this.refreshFurniture());
    });
    $(room.state).buildings.onRemove((_b: Building, id: string) => { this.buildings.delete(id); });

    $(room.state).players.onAdd((player, sessionId) => {
      if (sessionId === room.sessionId) {
        this.me = player; // our live combat state (health/blocking/attacking/dead)
        // Keep the local avatar + inventory preview in sync when the server changes our
        // appearance (equip/unequip). The local billboard is otherwise set once at construct.
        $(player.appearance).onChange(() => {
          appearanceStore.set(appearanceFromSchema(player.appearance));
          this.applyLocalAppearance();
        });
        // Mirror our cosmetic layer + show-cosmetics flag, and re-compose the avatar on any change.
        const syncCos = (): void => {
          cosmeticStore.set({ hat: player.cosHat, top: player.cosTop, bottom: player.cosBottom, shoes: player.cosShoes, showCosmetics: player.showCosmetics });
          this.applyLocalAppearance();
        };
        $(player).listen("cosHat", syncCos);
        $(player).listen("cosTop", syncCos);
        $(player).listen("cosBottom", syncCos);
        $(player).listen("cosShoes", syncCos);
        $(player).listen("showCosmetics", syncCos);
        $(player).listen("working", () => this.applyLocalAppearance()); // uniform on/off hides/shows cosmetics
        // Bed rest: the server snapped our synced position onto the bed — mirror it into the local
        // px so the sit pose sits exactly where everyone else sees it (movement is locked meanwhile).
        $(player).listen("resting", (resting: boolean) => {
          if (resting) { this.px.x = player.x; this.px.y = player.y; }
        });
        syncCos();
        // Mirror our equipped weapons; combat mode auto-disengages if both hands are emptied.
        const syncHands = (): void => {
          weaponStore.set({ hand1: player.hand1, hand2: player.hand2, rodBait: player.rodBait });
          if (this.combat && !weaponStore.hasWeapon()) this.setCombat(false);
          this.updateWeaponSprite();
          // bake only the equipped sword/shield into the attack swing (not both)
          this.player.setEquippedHands(
            isWeapon(player.hand1) || isWeapon(player.hand2),
            isShield(player.hand1) || isShield(player.hand2),
          );
        };
        $(player).listen("hand1", syncHands);
        $(player).listen("hand2", syncHands);
        $(player).listen("rodBait", syncHands);
        syncHands();
        // Mirror the worn neck accessory (charm) into its store so the paperdoll shows it.
        $(player).listen("neck", () => neckStore.set(player.neck));
        neckStore.set(player.neck);
        // Mirror live health into the stats store so the HUD health bar reflects combat damage.
        const syncHealth = (): void => {
          const s = statsStore.get();
          statsStore.set({ ...s, stats: { ...s.stats, health: player.health, maxHealth: player.maxHealth } });
        };
        $(player).listen("health", syncHealth);
        $(player).listen("maxHealth", syncHealth);
        return;
      }
      this.addRemote(sessionId, player);
    });
    $(room.state).players.onRemove((_player, sessionId) => this.removeRemote(sessionId));
    $(room.state).groundItems.onAdd((item, key) => this.addGroundItem(key, item));
    $(room.state).groundItems.onRemove((_item, key) => this.removeGroundItem(key));
    $(room.state).mineNodes.onAdd((node, key) => {
      this.mine.addNode(key, node);
      $(node).onChange(() => this.mine.updateNode(key, node.hitsLeft));
    });
    $(room.state).mineNodes.onRemove((_node, key) => this.mine.removeNode(key));
    $(room.state).grassNodes.onAdd((node, key) => this.grass.addNode(key, node));
    $(room.state).grassNodes.onRemove((_node, key) => this.grass.removeNode(key));
    // Wild hogs (server-driven attack NPCs in Kapon): mirror their synced state into the renderer.
    this.hogs ??= new HogController(this.scene);
    $(room.state).hogs.onAdd((hog: Hog, key: string) => {
      this.hogs!.add(key, hog);
      $(hog).onChange(() => {
        this.hogs!.sync(key, hog);
        if (this.selectedHog === key) {
          if (hog.state === "death") this.deselectHog();           // can't keep targeting a corpse
          else showNpcTarget(null, "Wild Hog", hog.health, hog.maxHealth); // live health bar
        }
      });
    });
    $(room.state).hogs.onRemove((_hog: Hog, key: string) => {
      this.hogs!.remove(key);
      if (this.selectedHog === key) this.deselectHog();
    });

    // Wild goblins (lvl 10).
    this.goblins ??= new GoblinController(this.scene);
    $(room.state).goblins.onAdd((goblin: Goblin, key: string) => {
      this.goblins!.add(key, goblin);
      $(goblin).onChange(() => {
        this.goblins!.sync(key, goblin);
        if (this.selectedGoblin === key) {
          if (goblin.state === "death") this.deselectGoblin();
          else showNpcTarget(null, "Goblin", goblin.health, goblin.maxHealth);
        }
      });
    });
    $(room.state).goblins.onRemove((_g: Goblin, key: string) => {
      this.goblins!.remove(key);
      if (this.selectedGoblin === key) this.deselectGoblin();
    });

    // Wild slimes (lvl 15).
    this.slimes ??= new SlimeController(this.scene);
    $(room.state).slimes.onAdd((slime: Slime, key: string) => {
      this.slimes!.add(key, slime);
      $(slime).onChange(() => {
        this.slimes!.sync(key, slime);
        if (this.selectedSlime === key) {
          if (slime.state === "death") this.deselectSlime();
          else showNpcTarget(null, "Slime", slime.health, slime.maxHealth);
        }
      });
    });
    $(room.state).slimes.onRemove((_s: Slime, key: string) => {
      this.slimes!.remove(key);
      if (this.selectedSlime === key) this.deselectSlime();
    });

    // Pet hogs (summoned companions): mirror their synced follow state; they render across all scenes.
    this.pets ??= new PetHogController(this.scene);
    $(room.state).petHogs.onAdd((pet: PetHog, key: string) => {
      this.pets!.add(key, pet);
      $(pet).onChange(() => {
        this.pets!.sync(key, pet);
        if (this.selectedPet === key) showNpcTarget(null, "Hoglet", pet.health, pet.maxHealth);
      });
    });
    $(room.state).petHogs.onRemove((_pet: PetHog, key: string) => {
      this.pets!.remove(key);
      if (this.selectedPet === key) this.deselectPet();
    });
    // Friendly gate guards (server-driven combat NPCs in Kapon): mirror their synced state.
    this.guards ??= new GuardController(this.scene);
    $(room.state).guards.onAdd((guard: Guard, key: string) => {
      this.guards!.add(key, guard);
      $(guard).onChange(() => {
        this.guards!.sync(key, guard);
        if (this.selectedGuard === key) {
          if (guard.state === "dead") this.deselectGuard();           // can't keep targeting a downed guard
          else showNpcTarget(null, guard.name || "Gate Guard", guard.health, guard.maxHealth);
        }
      });
    });
    $(room.state).guards.onRemove((_guard: Guard, key: string) => {
      this.guards!.remove(key);
      if (this.selectedGuard === key) this.deselectGuard();
    });
    // Synced city NPCs (walk-in restaurant customers): mirror their server-driven position.
    this.cityNpcs ??= new CityNpcController(this.scene);
    $(room.state).cityNpcs.onAdd((npc: CityNpc, key: string) => {
      this.cityNpcs!.add(key, npc);
      $(npc).onChange(() => this.cityNpcs!.sync(key, npc));
    });
    $(room.state).cityNpcs.onRemove((_npc: CityNpc, key: string) => {
      this.cityNpcs!.remove(key);
      if (this.selectedCityNpc === key) this.deselectCityNpc();
    });
    // Loot chests (dropped by slain hogs): render the glowing chest + keep the open loot panel synced.
    this.chests ??= new ChestController(this.scene);
    $(room.state).chests.onAdd((chest: Chest, key: string) => {
      this.chests!.add(key, chest);
      const refresh = (): void => updateChest(key, [...chest.items]);
      $(chest.items).onAdd(refresh);
      $(chest.items).onRemove(refresh);
    });
    // Weed plants growing in the Kapon wilds: billboard + keep the open status panel live.
    this.plants ??= new WeedPlantController(this.scene);
    $(room.state).plants.onAdd((plant: WeedPlant, key: string) => {
      this.plants!.add(key, plant);
      $(plant).onChange(() => {
        this.plants!.update(key, plant);
        updatePlantPanel(key, this.plantPanelData(plant));
      });
    });
    $(room.state).plants.onRemove((_plant: WeedPlant, key: string) => {
      this.plants!.remove(key);
      if (currentPlantId() === key) closePlantPanel();
    });
    $(room.state).chests.onRemove((_chest: Chest, key: string) => {
      this.chests!.remove(key);
      if (currentChestId() === key) closeChest();
    });
    $(room.state).portals.onAdd((node, key) => {
      const e = new PortalEntity(node.name, node.zone, node.destZone, { x: node.destX, y: node.destY }, node.x * U, node.y * U, node.secondsLeft);
      this.portals.set(key, e);
      this.scene.add(e.group);
      if (node.zone === this.zone) audio.playAt("portal_open", node.x * U, node.y * U); // it just swirled open near us
      $(node).onChange(() => e.setRemaining(node.secondsLeft));
    });
    $(room.state).portals.onRemove((_node, key) => {
      const e = this.portals.get(key);
      if (!e) return;
      if (this.selectedPortal === e) this.deselectPortal();
      audio.stopLoop(`portal:${key}`);
      this.scene.remove(e.group);
      e.dispose();
      this.portals.delete(key);
    });

    room.onMessage(MessageType.Inventory, (msg: InventoryMessage) => {
      inventoryStore.set(msg.slots ?? []);
      this.resolveFirstInventory?.(); this.resolveFirstInventory = null; // unblock the boot veil (once)
    });
    room.onMessage(MessageType.PickupRejected, (msg: PickupRejectedMessage) =>
      showToast(PICKUP_REJECT_TEXT[msg.reason] ?? "Can't pick that up"));
    room.onMessage(MessageType.EquipRejected, (msg: EquipRejectedMessage) =>
      showToast(EQUIP_REJECT_TEXT[msg.reason] ?? "Can't do that"));
    room.onMessage(MessageType.Balance, (msg: BalanceMessage) => balanceStore.set(msg.balance ?? 0));
    room.onMessage(MessageType.DayTime, (msg: DayTimeMessage) => dayNight.syncClock(msg.serverEpochMs)); // anchor day/night to server
    room.onMessage(MessageType.PlayerStats, (msg: PlayerStatsMessage) =>
      statsStore.set({ stats: msg.stats, baseStats: msg.baseStats, bankMoney: msg.bankMoney, mining: msg.mining, skinning: msg.skinning, cooking: msg.cooking ?? { ...DEFAULT_COOKING }, fishing: msg.fishing ?? { ...DEFAULT_FISHING }, professions: msg.professions ?? [], gearArmor: msg.gearArmor ?? 0, statBonus: msg.statBonus ?? { strength: 0, dex: 0, agility: 0 }, wornMods: msg.wornMods }));
    room.onMessage(MessageType.CraftResult, (msg: CraftResultMessage) => {
      // The SUCCESS/FAIL pixel pop over the player is the feedback; `stop` ⇒ the batch couldn't run
      // (server already sent a Notice), so cancel quietly.
      if (msg.stop) { this.craft.cancel(); return; }
      this.craft.showResult(msg.succeeded ?? 0, msg.attempted ?? 0, performance.now());
    });
    room.onMessage(MessageType.Notice, (msg: NoticeMessage) => showToast(msg.text));
    room.onMessage(MessageType.QuestLog, (msg: QuestLogMessage) => { setQuestLog(msg.quests ?? []); this.refreshQuestMarkers(); });
    room.onMessage(MessageType.FishBoard, (msg: FishBoardMessage) => setFishBoard(msg));
    room.onMessage(MessageType.FishCaught, (msg: FishCaughtMessage) => showFishCatch(msg.kind, msg.size));
    room.onMessage(MessageType.Leaderboard, (msg: LeaderboardMessage) => renderLeaderboard(msg.entries ?? [], !!msg.youAnonymous));
    room.onMessage(MessageType.Storage, (msg: StorageMessage) => storageStore.set({ slots: msg.slots ?? [], capacity: msg.capacity ?? 0, tier: msg.tier ?? 0, upgradeCost: msg.upgradeCost ?? null }));
    room.onMessage(MessageType.Market, (msg: MarketMessage) => marketStore.set({ listings: msg.listings ?? [], myCount: msg.myCount ?? 0, maxListings: msg.maxListings ?? 10 }));
    room.onMessage(MessageType.Fridge, (msg: FridgeMessage) => fridgeStore.set({ buildingId: msg.buildingId, slots: msg.slots ?? [], capacity: msg.capacity ?? 0 }));
    room.onMessage(MessageType.Safebox, (msg: SafeboxMessage) => setSafeboxSnapshot(msg.buildingId, msg.fid, msg.slots ?? []));
    room.onMessage(MessageType.FoodMenu, (msg: FoodMenuMessage) => foodMenuStore.set({ buildingId: msg.buildingId, items: msg.items ?? [], owner: !!msg.owner, funds: msg.funds ?? 0 }));
    room.onMessage(MessageType.ChatSay, (msg: ChatSayMessage) => {
      this.chat.say(msg.sessionId, msg.text, performance.now());
      // Proximity chat blip — from the speaker's position (own pos if it's us).
      const sp = msg.sessionId === room.sessionId ? this.player.group.position : this.remotes.get(msg.sessionId)?.bb.group.position;
      if (sp) audio.playAt("chat", sp.x, sp.z);
    });
    room.onMessage(MessageType.ServerNotice, (msg: ServerNoticeMessage) => showServerNotice(msg.text, msg.tag));
    // "Got hit" feedback: flash + shake whoever took the hit (player / remote / hog / guard), plus a
    // red screen pulse + camera shake when it's the local player. Fully-blocked hits do nothing.
    room.onMessage(MessageType.CombatHit, (msg: CombatHitMessage) => {
      const id = msg.target;
      // Got-hit feedback (real damage only): flash/shake the struck entity.
      if (!msg.blocked && !msg.miss && msg.damage > 0) {
        if (id === room.sessionId) {
          this.player.hit();
          this.camShake = Math.max(this.camShake, 0.3);
          flashHurt(Math.min(1, msg.damage / 40));
        } else if (this.remotes.has(id)) {
          this.remotes.get(id)!.bb.hit();
        } else if (id.startsWith("goblin")) {
          this.goblins?.hit(id);
        } else if (id.startsWith("slime")) {
          this.slimes?.hit(id);
        } else if (id.startsWith("hog")) {
          this.hogs?.hit(id);
        } else if (id.startsWith("pet_")) {
          this.pets?.hit(id);
        } else if (id.startsWith("guard")) {
          this.guards?.hit(id);
        }
      }
      // Floating combat number — LOCAL ONLY: shown when I dealt or took this hit (private to me).
      if (msg.attacker === room.sessionId || id === room.sessionId) {
        const wp = this.targetWorldPos(id);
        const sp = wp ? this.projectToScreen(wp) : null;
        if (sp) {
          const kind = msg.miss ? "miss" : msg.blocked ? "block" : id === room.sessionId ? "take" : "deal";
          popCombatNumber(sp.x, sp.y, { kind, amount: msg.damage, crit: msg.crit });
        }
      }
    });
    room.onMessage(MessageType.CombatDied, (msg: CombatDiedMessage) => { this.keys.clear(); if (this.combat) this.setCombat(false); showDeath(msg.respawnInMs); });
    room.onMessage(MessageType.CombatRespawn, (msg: CombatRespawnMessage) => {
      hideDeath();
      this.keys.clear();
      // Relocate the client to the respawn spot (the city middle) so it doesn't resume at the death site.
      void this.goToZone("village", { x: msg.x, y: msg.y });
    });
    room.onMessage(MessageType.TradeInvite, (msg: TradeInviteMessage) => showTradeInvite(msg.fromName));
    room.onMessage(MessageType.TradeUpdate, (msg: TradeUpdateMessage) => renderTrade(msg));
    room.onMessage(MessageType.TradeClosed, (msg: TradeClosedMessage) => {
      closeTradeWindow();
      const txt = msg.reason === "completed" ? null
        : msg.reason === "declined" ? "Trade declined."
        : msg.reason === "left" ? "The other player left — trade cancelled."
        : msg.reason === "failed" ? "Trade failed." : "Trade cancelled.";
      if (txt) showToast(txt);
    });
    room.onMessage(MessageType.SkillLevelUp, (msg: SkillLevelUpMessage) => {
      showSkillLevelUp(msg.skill, msg.level); audio.play("levelup");
    });
    room.onMessage(MessageType.TableSeat, (msg: TableSeatMessage) => {
      const prev = this.mySeat;
      this.mySeat = msg.seatIndex ?? -1;
      this.rebuildTableView();
      if (this.mySeat !== -1 && prev === -1) openTable();
      if (this.mySeat === -1) closeTable();
    });
    room.onMessage(MessageType.TableRejected, (msg: TableRejectedMessage) =>
      showToast(TABLE_REJECT_TEXT[msg.reason] ?? "Can't do that"));
    room.onMessage(MessageType.ActionRejected, (msg: ActionRejectedMessage) =>
      showToast(ACTION_REJECT_TEXT[msg.reason] ?? "Can't do that"));

    // ── Poker ───────────────────────────────────────────────────────────────
    room.onMessage(MessageType.PokerSeated, (msg: PokerSeatedMessage) => {
      const prev = this.myPokerSeat;
      this.myPokerSeat = msg.seatIndex ?? -1;
      if (this.myPokerSeat === -1) { this.myHole = ""; this.fpYaw = 0; this.fpPitch = 0; } // back to 3rd-person
      this.setPokerFpHud(this.myPokerSeat >= 0); // hide/restore the game HUD for the clean FP poker view
      this.keys.clear(); // drop any held movement keys on sit-down / stand-up
      // Sync our facing toward the dealer so EVERYONE renders us looking at the table (the remote-render
      // path snaps us toward the dealer too, but this also covers any fallback that uses the synced dir).
      const sp = this.myPokerSeat >= 0 ? this.build.pokerSeats?.[this.myPokerSeat] : undefined;
      if (sp && this.build.pokerNpc && this.room) {
        this.faceAngle = Math.atan2(this.build.pokerNpc.x - sp.x, this.build.pokerNpc.z - sp.z);
        const x = Math.round(this.px.x), y = Math.round(this.px.y), dir = headingToDir(this.faceAngle);
        this.room.send(MessageType.Move, { x, y, dir, moving: false, running: false, combat: this.combat });
        this.lastSent = { x, y, dir, moving: false, running: false, combat: this.combat };
      }
      this.rebuildPokerView();
      if (this.myPokerSeat !== -1 && prev === -1) openPokerTable();
      if (this.myPokerSeat === -1) {
        closePokerTable();
        this.cards3d?.clear(); // left the table → wipe any 3D cards so they don't linger / stack next time
        this.lastBoardLen = 0; this.lastCardCounts = []; this.lastPokerPhase = "";
      }
    });
    room.onMessage(MessageType.PokerHole, (msg: PokerHoleMessage) => {
      this.myHole = msg.cards ?? "";
      this.rebuildPokerView();
    });
    room.onMessage(MessageType.PokerRejected, (msg: PokerRejectedMessage) =>
      showToast(POKER_REJECT_TEXT[msg.reason] ?? "Can't do that"));
    room.onMessage(MessageType.PokerActionRejected, (msg: PokerActionRejectedMessage) =>
      showToast(POKER_ACTION_REJECT_TEXT[msg.reason] ?? "Can't do that"));
    room.send(MessageType.RequestInventory);

    room.onStateChange(() => { this.rebuildTableView(); this.rebuildPokerView(); });
    this.rebuildTableView();
    this.rebuildPokerView();

    room.onDrop(() => {
      showStatus("Reconnecting…", { spinner: true });
      this.clearReconnectFallback();
      this.reconnectFallback = window.setTimeout(() => {
        showStatus("Connection lost.", { actionLabel: "Reconnect", onAction: () => location.reload() });
      }, 10000);
    });
    room.onReconnect(() => { this.clearReconnectFallback(); hideStatus(); });
    room.onLeave((code) => {
      if (code === KICK_DUPLICATE_SESSION) {
        this.clearReconnectFallback();
        try { room.reconnection.enabled = false; } catch { /* ignore */ }
        showStatus("You signed in from another window.", {
          actionLabel: "Return here",
          onAction: () => location.reload(),
        });
      }
    });
  }

  /** The DISPLAYED appearance = gear (base) with the cosmetic layer overlaid — UNLESS hidden: the viewer
   *  is in Kapon (the fighting zone), the subject is working (their uniform must show), or that player has
   *  cosmetics toggled off. Because a client only renders same-zone players, the viewer's `this.zone`
   *  check is consistent for everyone. */
  private displayAppearance(base: CharacterAppearance, cos: { cosHat: string; cosTop: string; cosBottom: string; cosShoes: string }, showCos: boolean, working: boolean): CharacterAppearance {
    if (!showCos || working || this.zone === "kapon") return base;
    return applyCosmetics(base, cos);
  }

  /** Recompose the LOCAL avatar from the gear appearance + cosmetic layer (respects the toggle + zone). */
  private applyLocalAppearance(): void {
    const c = cosmeticStore.get();
    const display = this.displayAppearance(appearanceStore.get(), { cosHat: c.hat, cosTop: c.top, cosBottom: c.bottom, cosShoes: c.shoes }, c.showCosmetics, this.me?.working ?? false);
    void this.player.setAppearance(display);
  }

  /** Recompose a REMOTE avatar from its synced gear + cosmetic layer (respects our zone + their flags). */
  private applyRemoteAppearance(e: RemotePlayer): Promise<void> {
    const display = this.displayAppearance(appearanceFromSchema(e.player.appearance), e.player, e.player.showCosmetics, e.player.working);
    return e.bb.setAppearance(display);
  }

  /** Re-apply the display appearance to every visible avatar — call on zone change so cosmetics force off
   *  on entering Kapon and return on the way back to the city. */
  private refreshAppearances(): void {
    this.applyLocalAppearance();
    for (const e of this.remotes.values()) void this.applyRemoteAppearance(e);
  }

  private addRemote(sessionId: string, player: Player): void {
    const bb = new CharBillboard();
    const syncRemoteHands = (): void => {
      const w = isWeapon(player.hand1) || isWeapon(player.hand2);
      const s = isShield(player.hand1) || isShield(player.hand2);
      // Pre-warm their combat atlases ahead of time so a remote drawing their weapon doesn't hitch.
      if (w || s) bb.prewarmEquip(w && !player.dead, s && !player.dead);
      bb.setEquippedHands(player.combat && !player.dead && w, player.combat && !player.dead && s);
    };
    syncRemoteHands();
    bb.group.position.set(player.x * U, 0, player.y * U);
    this.scene.add(bb.group);
    const labelRed = player.affinity < 0;
    const label = makeSelectLabel(player.name, "P", labelRed ? RED_NAME_COLOR : undefined); // name + [P] tag, shown when selected
    label.visible = false;
    this.scene.add(label);

    const entity: RemotePlayer = {
      bb, label, sessionId, player, labelRed,
      serverX: player.x, serverY: player.y,
      dir: (player.dir as Direction) ?? Direction.Down,
      moving: player.moving,
      running: player.running,
      wasAttacking: false,
      wasBlocking: false,
      wasStunned: false,
      wasSmoking: false,
      jumpSeq: player.jumpSeq | 0, // baseline so we don't hop the instant we first see them
      jumpActive: false,
      jumpT: 0,
      seatIndex: player.seatIndex ?? -1,
      pokerSeatIndex: player.pokerSeatIndex ?? -1,
    };
    this.remotes.set(sessionId, entity);
    void this.applyRemoteAppearance(entity).then(syncRemoteHands); // gear + cosmetic overlay

    this.callbacks?.(player).onChange(() => {
      entity.serverX = player.x;
      entity.serverY = player.y;
      entity.dir = (player.dir as Direction) ?? Direction.Down;
      entity.moving = player.moving;
      entity.running = player.running;
      entity.seatIndex = player.seatIndex ?? -1;
      entity.pokerSeatIndex = player.pokerSeatIndex ?? -1;
      syncRemoteHands(); // re-bake the attack swing's sword/shield when this remote (un)equips
    });
    this.callbacks?.(player.appearance).onChange(() => void this.applyRemoteAppearance(entity));
    // Re-compose when this remote's cosmetic layer / show flag / work state changes (NOT on every move).
    const applyCos = (): void => void this.applyRemoteAppearance(entity);
    this.callbacks?.(player).listen("cosHat", applyCos, false);
    this.callbacks?.(player).listen("cosTop", applyCos, false);
    this.callbacks?.(player).listen("cosBottom", applyCos, false);
    this.callbacks?.(player).listen("cosShoes", applyCos, false);
    this.callbacks?.(player).listen("showCosmetics", applyCos, false);
    this.callbacks?.(player).listen("working", applyCos, false);
  }

  private removeRemote(sessionId: string): void {
    const e = this.remotes.get(sessionId);
    if (!e) return;
    this.chat.remove(sessionId);
    if (this.selectedRemote === e) this.deselectRemote();
    this.scene.remove(e.bb.group);
    this.scene.remove(e.label);
    e.bb.dispose();
    (e.label.material as THREE.SpriteMaterial).map?.dispose();
    (e.label.material as THREE.SpriteMaterial).dispose();
    this.remotes.delete(sessionId);
    const fx = this.miningFx.get(sessionId);
    if (fx) { this.scene.remove(fx.group); fx.dispose(); this.miningFx.delete(sessionId); }
    const gfx = this.craftingFx.get(sessionId);
    if (gfx) { this.scene.remove(gfx.group); gfx.dispose(); this.craftingFx.delete(sessionId); }
    this.syncParkedBmx(sessionId, null); // their parked bike leaves with them
    this.bmxRemotePrev.delete(sessionId);
  }

  private addGroundItem(key: string, item: GroundItem): void {
    const tex = new THREE.CanvasTexture(drawItemIcon(item.itemId));
    tex.magFilter = THREE.NearestFilter;
    tex.minFilter = THREE.NearestFilter;
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true }));
    sprite.scale.set(0.55, 0.55, 1);
    sprite.position.set(item.x * U, 0.5, item.y * U);
    dayNight.registerMat(sprite.material, sprite.position); // darken/cool with the cycle (+ nearby lamps)
    this.scene.add(sprite);
    this.groundItems.set(key, { sprite, x: item.x, y: item.y });
  }

  private removeGroundItem(key: string): void {
    const e = this.groundItems.get(key);
    if (!e) return;
    this.scene.remove(e.sprite);
    dayNight.unregisterMat(e.sprite.material);
    (e.sprite.material as THREE.SpriteMaterial).map?.dispose();
    (e.sprite.material as THREE.SpriteMaterial).dispose();
    this.groundItems.delete(key);
    if (this.highlightKey === key) this.highlightKey = null;
  }

  private rebuildTableView(): void {
    if (!this.room) return;
    const bj = this.room.state.blackjack;
    const seats: SeatView[] = [];
    bj.seats.forEach((seat, key) => {
      seats.push({
        index: Number(key),
        occupied: seat.occupied,
        name: seat.name,
        appearance: appearanceFromSchema(seat.appearance),
        connected: seat.connected,
        spectating: seat.spectating,
        pendingBet: seat.pendingBet,
        hand: handView(seat.hand),
        splitHand: handView(seat.splitHand),
        hasSplit: seat.hasSplit,
      });
    });
    seats.sort((a, b) => a.index - b.index);
    blackjackStore.set({
      phase: bj.phase,
      activeSeat: bj.activeSeat,
      activeSplitHand: bj.activeSplitHand,
      timerEndsAt: bj.timerEndsAt,
      dealer: { cards: bj.dealer.cards, value: bj.dealer.value, soft: bj.dealer.soft },
      seats,
      mySeat: this.mySeat,
    });
  }

  private rebuildPokerView(): void {
    if (!this.room) return;
    const pk = this.room.state.poker;
    const seats: PokerSeatView[] = [];
    pk.seats.forEach((seat, key) => {
      seats.push({
        index: Number(key),
        occupied: seat.occupied,
        name: seat.name,
        appearance: appearanceFromSchema(seat.appearance),
        connected: seat.connected,
        chips: seat.chips,
        bet: seat.bet,
        cardCount: seat.cardCount,
        revealed: seat.revealed,
        status: seat.status,
        lastAction: seat.lastAction,
      });
    });
    seats.sort((a, b) => a.index - b.index);
    this.animatePokerDeal(pk.phase, pk.community, seats);
    const meSeat = seats.find((s) => s.index === this.myPokerSeat);
    // End-of-hand result (winner + WINNING HAND). The poker table is GLOBAL state, so every client sees
    // pk.result — only show the banner to players actually SEATED at the poker table.
    if (pk.result && pk.result !== this.lastPokerResult) {
      this.lastPokerResult = pk.result;
      if (this.myPokerSeat >= 0) {
        const iWon = meSeat?.lastAction === "won";
        // Showdown → render the winner's two hole cards INSIDE the result panel (empty for uncontested wins).
        const cards = (pk.resultCards ? pk.resultCards.split(" ").filter(Boolean) : []).map((code) => {
          const c = drawCard(code);
          c.style.cssText = "width:60px;height:84px;image-rendering:pixelated;border-radius:4px;box-shadow:0 3px 8px rgba(0,0,0,.6);";
          return c;
        });
        showBanner(iWon ? `🏆 ${pk.result}` : pk.result, 7500, cards);
      }
    } else if (!pk.result) {
      this.lastPokerResult = "";
    }
    // "Your turn" cue — a soft chime the first frame the action lands on us.
    const myTurn = this.myPokerSeat >= 0 && pk.activeSeat === this.myPokerSeat && meSeat?.status === "active";
    if (myTurn && !this.pokerWasMyTurn) audio.play("your_turn");
    this.pokerWasMyTurn = myTurn;
    pokerStore.set({
      phase: pk.phase,
      community: pk.community,
      pot: pk.pot,
      currentBet: pk.currentBet,
      minRaise: pk.minRaise,
      button: pk.button,
      activeSeat: pk.activeSeat,
      timerEndsAt: pk.timerEndsAt,
      seats,
      mySeat: this.myPokerSeat,
      myHole: this.myHole,
    });
  }

  /** Fly community + hole cards in 3D from the dealer as the synced state changes (delta-driven). */
  private animatePokerDeal(phase: string, community: string, seats: PokerSeatView[]): void {
    const group = this.build.zones.casino?.group;
    const dealer = this.build.pokerNpc;
    const seatPos = this.build.pokerSeats;
    if (!group || !dealer || !seatPos) return;
    if (!this.cards3d) this.cards3d = new Cards3D(group);
    const board = community ? community.split(" ").filter(Boolean) : [];
    const from = new THREE.Vector3(dealer.x, 1.1, dealer.z);
    const bx = POKER_TABLE_ANCHOR.x / 32, bz = POKER_TABLE_ANCHOR.y / 32;

    // New hand — wipe the felt + cancel any pending deals + reset trackers, so last round's cards don't
    // stack with this one. Triggered by: the board shrinking, the table going idle, or (the bulletproof
    // one) the phase freshly entering "preflop" (a new hand always starts there, regardless of board timing).
    const newHand = phase === "preflop" && this.lastPokerPhase !== "preflop";
    this.lastPokerPhase = phase;
    if (board.length < this.lastBoardLen || phase === "idle" || newHand) {
      this.cards3d.clear();
      this.pokerDealQueue.length = 0;
      if (this.pokerDealTimer !== undefined) { window.clearTimeout(this.pokerDealTimer); this.pokerDealTimer = undefined; }
      this.lastBoardLen = 0;
      this.lastCardCounts = [];
    }
    const FELT_Y = 0.66; // just above the felt surface
    // Community (public) cards lie flat FACE-UP in the centre of the felt, dealt out one by one.
    for (let i = this.lastBoardLen; i < board.length; i++) {
      const to = new THREE.Vector3(bx + (i - 2) * 0.22, FELT_Y, bz);
      const code = board[i];
      this.enqueuePokerDeal(() => this.cards3d?.deal(from, to, { dur: 620, code }));
    }
    this.lastBoardLen = board.length;
    // Hole cards: two cards that hover in the AIR in front of each player with a flowing bob, facing
    // that player. THIS client renders only ITS OWN cards face-up (the private hole from PokerHole);
    // every other seat's cards stay face-down. Dealt one at a time.
    for (const s of seats) {
      const isMe = s.index === this.myPokerSeat;
      const prev = this.lastCardCounts[s.index] ?? 0;
      if (s.cardCount === 2 && prev !== 2) {
        // Our own faces come from the private PokerHole message — defer the deal (without marking it
        // dealt) until it arrives, so we never flash our cards face-down.
        if (isMe && !this.myHole) continue;
        const pos = seatPos[s.index];
        if (pos) {
          const dx = pos.x - bx, dz = pos.z - bz, len = Math.hypot(dx, dz) || 1;
          const ux = dx / len, uz = dz / len;            // unit direction centre → seat (toward the player)
          const cardY = POKER_SEAT_SIT_Y + 0.95;          // chest/eye height — in front of the FP camera
          const hx = pos.x - ux * 0.32, hz = pos.z - uz * 0.32; // a touch IN FRONT of the seat (toward the table)
          const face = new THREE.Vector3(pos.x + ux * 0.55, cardY, pos.z + uz * 0.55); // toward the player's camera
          const ox = -uz * 0.1, oz = ux * 0.1;            // perpendicular offset for the two cards
          const myCards = isMe ? this.myHole.split(" ").filter(Boolean) : [];
          for (let c = 0; c < 2; c++) {
            const sgn = c === 0 ? -1 : 1;
            const to = new THREE.Vector3(hx + ox * sgn, cardY, hz + oz * sgn);
            const code = isMe ? myCards[c] : undefined; // face-up only for our own seat
            this.enqueuePokerDeal(() => this.cards3d?.deal(from, to, { dur: 620, faceToward: face, code }));
          }
          this.lastCardCounts[s.index] = 2; // dealt
        }
      } else if (s.cardCount !== 2) {
        this.lastCardCounts[s.index] = s.cardCount;
      }
    }
  }

  /** Hide the game HUD (food/water, ability slots, J-skill notice, control prompts + button bar) while
   *  seated at the poker table, so the first-person view is clean. Toggled by a body class so it also
   *  overrides the prompt's inline display. */
  private setPokerFpHud(on: boolean): void {
    const id = "gl-poker-fp-style";
    if (!document.getElementById(id)) {
      const s = document.createElement("style");
      s.id = id;
      s.textContent = "body.gl-poker-fp #gl-vitals, body.gl-poker-fp #gl-stamina, body.gl-poker-fp #gl-skillbar, "
        + "body.gl-poker-fp #gl-skillnotice, body.gl-poker-fp #gl-prompt, body.gl-poker-fp #gl-hud-bar, "
        + "body.gl-poker-fp #gl-hud-combatbar "
        + "{ display: none !important; }";
      document.head.appendChild(s);
    }
    document.body.classList.toggle("gl-poker-fp", on);
  }

  /** Queue a card-fly so the dealer sends them out one at a time (a slow, sequential deal). */
  private enqueuePokerDeal(fn: () => void): void {
    this.pokerDealQueue.push(fn);
    if (this.pokerDealTimer === undefined) this.pumpPokerDeal();
  }
  private pumpPokerDeal(): void {
    const fn = this.pokerDealQueue.shift();
    if (!fn) { this.pokerDealTimer = undefined; return; }
    fn();
    this.pokerDealTimer = window.setTimeout(() => this.pumpPokerDeal(), POKER_DEAL_GAP_MS);
  }

  private clearReconnectFallback(): void {
    if (this.reconnectFallback !== undefined) {
      window.clearTimeout(this.reconnectFallback);
      this.reconnectFallback = undefined;
    }
  }

  // --- teardown ---------------------------------------------------------------

  destroy(): void {
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.clearReconnectFallback();
    this.room?.leave();
    this.room = undefined;
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("resize", this.onResize);
    for (const e of this.remotes.values()) { this.scene.remove(e.bb.group); e.bb.dispose(); this.scene.remove(e.label); }
    this.remotes.clear();
    for (const fx of this.miningFx.values()) { this.scene.remove(fx.group); fx.dispose(); }
    this.miningFx.clear();
    for (const fx of this.craftingFx.values()) { this.scene.remove(fx.group); fx.dispose(); }
    this.craftingFx.clear();
    for (const key of [...this.groundItems.keys()]) this.removeGroundItem(key);
    for (const e of this.portals.values()) { this.scene.remove(e.group); e.dispose(); }
    this.portals.clear();
    for (const m of Object.values(this.npcByZone)) m?.dispose();
    this.chat.dispose();
    destroyServerNotice();
    this.cats?.dispose();
    this.hogs?.dispose();
    this.goblins?.dispose();
    this.slimes?.dispose();
    this.pets?.dispose();
    this.cityNpcs?.dispose();
    this.guards?.dispose();
    this.chests?.dispose();
    this.plants?.dispose();
    this.skin?.dispose();
    closeChest();
    destroyNpcDialog();
    destroyNpcTarget();
    destroyGameMenu();
    this.player.dispose();
    this.prompt.remove();
    this.composer?.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }
}
